import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { createPosSession, getPosResource, mutatePosResource } from './lib/pos-api';
import {
  STATUS_STYLES, EXPENSE_STATUS_STYLES, TAB_ACTIVE_CLASSES,
  methodLabels, getOrderSourceLabel,
  getDiscountReasonLabel,
  MAX_DISCOUNT_PERCENT, STAFF_MAX_DISCOUNT_PERCENT,
  DEFAULT_INVENTORY, baseIngredients, menuRecipes
} from './utils/constants';
import {
  classNames, formatCedi
} from './utils/formatters';
import { cloneBundlesForAmend } from './utils/orderDrafts';
import {
  ANALYTICS_REVENUE_STATUSES,
  buildAnalyticsSnapshot,
  buildEodReport,
  calculateAmendedOrderTotal,
  createDateRangeMatcher,
  dedupeExpenses,
  getAnalyticsTimestamp,
  getExpenseIdentityKeys,
  getOrderDiscountAmount,
  getOrderItemRevenue,
  getOrderRevenueAmount,
  getReconstructedOrderTotal,
  isAnalyticsRevenueOrder,
} from './utils/analytics';
import {
  resolveCloudOrderTotal,
  findStrandedOrders,
  nextSyncDelay,
  nextRetryDelay,
  resolveSyncWatermark,
  countBundleChanges,
  SYNC_BASE_MS,
  isRefusedForThisLogin,
  loginOrderWindowStart,
  planOrderRangeFetch,
  nextOrderCoverage,
} from './utils/sync';
import { safeErrorMessage } from './utils/errorMessages';
import {
  userCanAny,
  canAmendOrders,
  canViewEodSales,
  canUseMarketing,
  canViewFinancialReports,
  canReadExpenses,
  canUploadQueued,
  RIDER_READ_PERMISSIONS,
  INVENTORY_PERMISSIONS,
} from './utils/permissions';
import { subscribeToOrderChanges } from './lib/realtime';
import useOrderAlarm from './hooks/useOrderAlarm';
import NewOrderAlert from './components/NewOrderAlert';
import { alertSound } from './utils/alertSound';
import AppModals from './components/AppModals';
import BottomNav from './components/BottomNav';
import { AppContext, ClockProvider } from './context/AppContext';
import POSTab from './pages/POSTab';
import InstallPrompt from './components/InstallPrompt';
import { isPushSupported, requestNotificationPermission, subscribeToPush, saveSubscription, unsubscribeFromPush } from './utils/pushNotifications';
import { canPromptInstall, promptInstall, isRunningInstalled, installHelpText } from './utils/installApp';

// Non-default tabs are code-split so they leave the initial bundle and load on
// first navigation. POSTab stays eager since it's the default landing screen.
const ApprovalsTab = lazy(() => import('./pages/ApprovalsTab'));
const QueueTab = lazy(() => import('./pages/QueueTab'));
const KitchenTab = lazy(() => import('./pages/KitchenTab'));
const HistoryTab = lazy(() => import('./pages/HistoryTab'));
const RidersTab = lazy(() => import('./pages/RidersTab'));
const AnalyticsTab = lazy(() => import('./pages/AnalyticsTab'));
const ExpensesTab = lazy(() => import('./pages/ExpensesTab'));
const MyExpensesTab = lazy(() => import('./pages/MyExpensesTab'));
const InventoryTab = lazy(() => import('./pages/InventoryTab'));
const ComplaintsTab = lazy(() => import('./pages/ComplaintsTab'));
const MarketingTab = lazy(() => import('./pages/MarketingTab'));
const EodSalesTab = lazy(() => import('./pages/EodSalesTab'));
const FinanceTab = lazy(() => import('./pages/finance/FinanceTab'));
// Admin-only AI assistant — code-split so it never weighs on the initial bundle.
const AiAssistant = lazy(() => import('./components/ai/AiAssistant'));

const defaultBundlePackaging = 'rubber';
const packagingPricing = { rubber: 0, paper: 5 };
const DAY_IN_MS = 24 * 60 * 60 * 1000;
// Backend (src/app/api/pos/route.js) clamps limit to 1000. Use the full ceiling
// so a full 4-5k-order archive paginates in ~5 requests instead of 10.
const CLOUD_FETCH_PAGE_SIZE = 1000;
const CLOUD_FETCH_MAX_PAGES = 200;
const ANALYTICS_REFRESH_DEDUPE_MS = 30000;
const ORDERS_STORAGE_KEY = 'restaurant_orders';
const ORDERS_STORAGE_MIN_RETAIN = 100;

const isQuotaExceededError = (err) => Boolean(
  err && (
    err.name === 'QuotaExceededError'
    || err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || err.code === 22
    || err.code === 1014
  ),
);

const sortOrdersForStorage = (orders) => [...orders].sort((a, b) => {
  const aT = new Date(a?.lastModified || a?.paidAt || a?.timestamp || 0).getTime();
  const bT = new Date(b?.lastModified || b?.paidAt || b?.timestamp || 0).getTime();
  return bT - aT;
});

// Persist orders to localStorage; on QuotaExceededError, trim oldest first and retry.
// The in-memory state still holds the full set — this only caches a recent slice for reloads.
const persistOrdersToStorage = (orders) => {
  if (!Array.isArray(orders)) return;
  if (orders.length === 0) {
    try { localStorage.setItem(ORDERS_STORAGE_KEY, '[]'); } catch { /* ignore */ }
    return;
  }
  let candidate = sortOrdersForStorage(orders);
  for (let attempts = 0; attempts < 12; attempts += 1) {
    try {
      localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(candidate));
      if (candidate.length < orders.length) {
        console.warn(`[Storage] localStorage quota exceeded — cached ${candidate.length}/${orders.length} most recent orders. Full set stays in memory.`);
      }
      return;
    } catch (err) {
      if (!isQuotaExceededError(err)) {
        console.warn('[Storage] orders persist failed (non-quota):', err);
        return;
      }
      if (candidate.length <= ORDERS_STORAGE_MIN_RETAIN) {
        try { localStorage.removeItem(ORDERS_STORAGE_KEY); } catch { /* ignore */ }
        console.warn('[Storage] localStorage too small even for the latest 100 orders — dropping orders cache.');
        return;
      }
      candidate = candidate.slice(0, Math.max(ORDERS_STORAGE_MIN_RETAIN, Math.floor(candidate.length / 2)));
    }
  }
};

const safeSetLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.warn(`[Storage] failed to write ${key}:`, err?.name || err);
  }
};

const formatBackendDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ShellIcon = ({ name }) => {
  const sharedProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  switch (name) {
    case 'pos':
      return <svg {...sharedProps}><path d="M4 7h16" /><path d="M6 7l1 11a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-11" /><path d="M9 10h6" /><path d="M10 14h4" /></svg>;
    case 'queue':
      return <svg {...sharedProps}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /><path d="M7 15h2" /><path d="M12 15h5" /></svg>;
    case 'kitchen':
      return <svg {...sharedProps}><path d="M4 4v8" /><path d="M7 4v8" /><path d="M10 4v8" /><path d="M7 12v8" /><path d="M15 4c1.657 0 3 1.567 3 3.5V20" /><path d="M18 4v16" /></svg>;
    case 'approvals':
      return <svg {...sharedProps}><path d="M20 7 9 18l-5-5" /></svg>;
    case 'history':
      return <svg {...sharedProps}><path d="M12 8v5l3 2" /><path d="M21 12a9 9 0 1 1-3.17-6.86" /></svg>;
    case 'riders':
      return <svg {...sharedProps}><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="10" cy="7" r="4" /><path d="M19 8v6" /><path d="M16 11h6" /></svg>;
    case 'analytics':
      return <svg {...sharedProps}><path d="M4 19h16" /><path d="M7 16V9" /><path d="M12 16V5" /><path d="M17 16v-3" /></svg>;
    case 'expenses':
      return <svg {...sharedProps}><path d="M12 3v18" /><path d="M16 7a4 4 0 0 0-4-2 3.5 3.5 0 0 0 0 7 3.5 3.5 0 0 1 0 7 4 4 0 0 1-4-2" /></svg>;
    case 'myexpenses':
      return <svg {...sharedProps}><path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M19 8v6" /><path d="M16 11h6" /></svg>;
    case 'inventory':
      return <svg {...sharedProps}><path d="M21 8v8a2 2 0 0 1-1 1.73l-7 4a2 2 0 0 1-2 0l-7-4A2 2 0 0 1 3 16V8" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /><path d="m7 4 5-3 5 3" /></svg>;
    case 'eod':
      return <svg {...sharedProps}><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 7h6" /><path d="M9 11h.01" /><path d="M12 11h.01" /><path d="M15 11h.01" /><path d="M9 15h.01" /><path d="M12 15h.01" /><path d="M15 15h.01" /></svg>;
    case 'marketing':
      return <svg {...sharedProps}><path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" /></svg>;
    case 'finance':
      return <svg {...sharedProps}><path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6" /><path d="M9 12h6" /><path d="M9 16h3" /></svg>;
    default:
      return <svg {...sharedProps}><circle cx="12" cy="12" r="8" /></svg>;
  }
};

// Matches the .pos-toast exit transition in index.css.
const TOAST_EXIT_MS = 200;

const getLatestOrderAdjustment = (order, type) => {
  if (!order || !Array.isArray(order.adjustments)) return null;

  for (let index = order.adjustments.length - 1; index >= 0; index -= 1) {
    const adjustment = order.adjustments[index];
    if (adjustment?.type === type) return adjustment;
  }

  return null;
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

// Cash value of a percentage discount against an items subtotal. Mirrors the
// clamping in analytics.getOrderDiscountAmount so the cart preview and the
// persisted order can never disagree.
const calcDiscountAmount = (itemsSubtotal, percent) => {
  const subtotal = Math.max(Number(itemsSubtotal) || 0, 0);
  const pct = Math.min(Math.max(Number(percent) || 0, 0), MAX_DISCOUNT_PERCENT);
  return Math.min(round2((subtotal * pct) / 100), subtotal);
};

// A staff-requested discount awaiting an admin decision. While one is pending
// the order sits in the queue at FULL price and payment is locked, so the till
// can never collect less than what has actually been authorised.
const getPendingDiscountRequest = (order) => {
  const approval = order?.approval;
  if (!approval || approval.status !== 'pending' || approval.type !== 'discount') return null;
  return approval;
};

const hasPendingDiscountRequest = (order) => Boolean(getPendingDiscountRequest(order));

// The applied discount record (percent, reason, who approved it), as opposed to
// its cash value which comes from analytics.getOrderDiscountAmount.
const getOrderDiscountAdjustment = (order) => getLatestOrderAdjustment(order, 'discount');

const parseDeliveryFeeFromNotes = (notes) => {
  if (!notes) return null;
  const match = String(notes).match(/(?:^|\|\s*)Delivery Fee:\s*(?:GHS|GH₵|₵)?\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;

  return {
    amount: Number(match[1] || 0),
    source: 'notes',
  };
};

const DELIVERY_CODE_NOTE_LABEL = 'Delivery Code';

const normalizeDeliveryVerificationCode = (value) => {
  if (value === undefined || value === null) return '';

  const digitsOnly = String(value).replace(/\D/g, '');
  if (!digitsOnly) return '';

  return digitsOnly.slice(0, 8);
};

const parseDeliveryVerificationCodeFromNotes = (notes) => {
  if (!notes) return '';

  const match = String(notes).match(/(?:delivery|verification|confirmation)\s*code\s*[:#-]?\s*(\d{4,8})/i);
  return normalizeDeliveryVerificationCode(match?.[1] || '');
};

const stripDeliveryVerificationCodeFromNotes = (notes) => {
  if (!notes) return null;

  const cleaned = String(notes)
    .replace(/(?:\r?\n|\s*\|\s*)?Delivery Code:\s*\d{4,8}/ig, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+\|\s+/g, ' | ')
    .trim()
    .replace(/^\|\s*/, '')
    .replace(/\s*\|$/, '')
    .trim();

  return cleaned || null;
};

const appendDeliveryVerificationCodeToNotes = (notes, deliveryCode) => {
  const normalizedCode = normalizeDeliveryVerificationCode(deliveryCode);
  const baseNotes = stripDeliveryVerificationCodeFromNotes(notes);

  if (!normalizedCode) return baseNotes;

  return baseNotes
    ? `${baseNotes}\n${DELIVERY_CODE_NOTE_LABEL}: ${normalizedCode}`
    : `${DELIVERY_CODE_NOTE_LABEL}: ${normalizedCode}`;
};

const createDeliveryVerificationCode = () => String(Math.floor(1000 + Math.random() * 9000));

const getCloudOrderDeliveryVerificationCode = (record) => {
  const candidates = [
    record?.delivery_verification_code,
    record?.deliveryVerificationCode,
    record?.delivery_code,
    record?.deliveryCode,
    record?.verification_code,
    record?.verificationCode,
    record?.confirmation_code,
    record?.confirmationCode,
    record?.customer_code,
    record?.customerCode,
    record?.otp,
    record?.otp_code,
    record?.otpCode,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeDeliveryVerificationCode(candidate);
    if (normalized) return normalized;
  }

  return parseDeliveryVerificationCodeFromNotes(record?.notes);
};

const getOrderDeliveryFeeInfo = (order) => {
  const adjustment = getLatestOrderAdjustment(order, 'delivery_fee');
  if (adjustment) {
    return {
      amount: Number(adjustment.amount || adjustment.fee || 0),
      source: adjustment.source || 'adjustment',
      label: adjustment.label || '',
      timestamp: adjustment.timestamp || null,
    };
  }

  return parseDeliveryFeeFromNotes(order?.notes);
};

const getOrderRiderAssignmentAdjustment = (order) => getLatestOrderAdjustment(order, 'rider_assignment');

const normalizeOrderType = (orderType) => {
  const normalized = String(orderType || '').trim().toLowerCase();

  if (normalized === 'delivery' || normalized === 'online') {
    return 'online';
  }

  if (normalized === 'pickup' || normalized === 'in-person' || normalized === 'in_person') {
    return 'in-person';
  }

  return normalized || 'online';
};

const getCollectionRecords = (value, nestedKeys = []) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  for (const key of nestedKeys) {
    if (Array.isArray(value[key])) return value[key];
  }

  return [];
};

const getRecordIdentity = (record) => normalizeCloudIdentifier(
  record?.id
  || record?.order_id
  || record?.orderId
  || record?.client_order_id
  || record?.clientOrderId
  || record?.supabase_id
  || record?.supabaseId
);

const mergeFetchedRecords = (existingRecords, incomingRecords) => {
  const merged = [...existingRecords];
  const seen = new Set(existingRecords.map(getRecordIdentity).filter(Boolean));
  let added = 0;

  incomingRecords.forEach((record) => {
    const identity = getRecordIdentity(record);
    if (identity && seen.has(identity)) return;
    if (identity) seen.add(identity);
    merged.push(record);
    added += 1;
  });

  return { records: merged, added };
};

const normalizeCloudIdentifier = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const getCloudOrderId = (record) => {
  const candidates = [
    record?.order_id,
    record?.orderId,
    record?.client_order_id,
    record?.clientOrderId,
    record?.local_order_id,
    record?.localOrderId,
    record?.pos_order_id,
    record?.posOrderId,
    record?.external_id,
    record?.externalId,
    record?.id,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCloudIdentifier(candidate);
    if (normalized) return normalized;
  }

  return '';
};

const getCloudOrderRowId = (record) => {
  const candidates = [
    record?.supabase_id,
    record?.supabaseId,
    record?.row_id,
    record?.rowId,
    record?.id,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCloudIdentifier(candidate);
    if (normalized) return normalized;
  }

  return '';
};

const getOrderIdentityKeys = (order) => Array.from(new Set(
  [normalizeCloudIdentifier(order?.id), normalizeCloudIdentifier(order?.supabase_id)].filter(Boolean)
));

const getOrderDeliveryVerificationCode = (order) => {
  if (!order || typeof order !== 'object') return '';

  const candidates = [
    order.deliveryVerificationCode,
    order.delivery_verification_code,
    order.deliveryCode,
    order.delivery_code,
    order.verificationCode,
    order.verification_code,
    order.confirmationCode,
    order.confirmation_code,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeDeliveryVerificationCode(candidate);
    if (normalized) return normalized;
  }

  return parseDeliveryVerificationCodeFromNotes(order.notes);
};

const getOrderItemsForSync = (orderData) => {
  if (Array.isArray(orderData?.items)) return orderData.items;
  if (!Array.isArray(orderData?.bundles)) return [];

  return orderData.bundles.flatMap(bundle => (
    Array.isArray(bundle?.items)
      ? bundle.items
      : Object.values(bundle?.items || {})
  )).filter(item => Number(item?.quantity || 0) > 0);
};

const buildOrderSyncPayload = (orderData, user, includeExtendedFields = true) => {
  const timestamp = orderData?.timestamp || new Date().toISOString();
  const deliveryVerificationCode = getOrderDeliveryVerificationCode(orderData);
  const payload = {
    id: normalizeCloudIdentifier(orderData?.id) || String(Date.now()),
    timestamp,
    last_modified: orderData?.lastModified || timestamp,
    items: getOrderItemsForSync(orderData),
    total: Number(orderData?.total || 0),
    status: orderData?.status || 'pending_payment',
    processed_by: orderData?.processedBy || user?.fullName || 'Unknown',
    user_role: user?.role || orderData?.userRole || 'unknown',
    order_type: normalizeOrderType(orderData?.orderType),
    order_source: orderData?.orderSource || null,
    order_source_detail: orderData?.orderSourceDetail || null,
    customer_name: orderData?.customerName || null,
    customer_phone: orderData?.customerPhone || null,
    delivery_address: orderData?.deliveryAddress || null,
    notes: appendDeliveryVerificationCodeToNotes(orderData?.notes, deliveryVerificationCode),
    payment_amount: orderData?.paymentAmount != null ? Number(orderData.paymentAmount) : null,
    balance_given: orderData?.balanceGiven != null ? Number(orderData.balanceGiven) : null,
    payment_method: orderData?.paymentMethod || null,
  };

  if (!includeExtendedFields) return payload;

  return {
    ...payload,
    delivery_verification_code: deliveryVerificationCode || null,
    bundles: orderData?.bundles || null,
    split_payments: orderData?.splitPayments || null,
    paid_at: orderData?.paidAt || null,
    ready_at: orderData?.readyAt || null,
    completed_at: orderData?.completedAt || null,
    version: orderData?.version || 1,
    adjustments: orderData?.adjustments || [],
    approval: orderData?.approval || null,
    due_amount: orderData?.dueAmount != null ? Number(orderData.dueAmount) : null,
    refund_due: orderData?.refundDue != null ? Number(orderData.refundDue) : null,
    packaging: orderData?.packaging || 'rubber',
  };
};

const buildMinimalOrderSyncPayload = (orderData, user) => {
  const payload = buildOrderSyncPayload(orderData, user, false);
  return {
    id: payload.id,
    timestamp: payload.timestamp,
    last_modified: payload.last_modified,
    items: payload.items,
    total: payload.total,
    status: payload.status,
    processed_by: payload.processed_by,
    user_role: payload.user_role,
    order_type: payload.order_type,
  };
};

// Permission gates (see utils/permissions) are also used to avoid firing
// requests this login will only be refused — a kitchen display used to open
// with a burst of 403s for riders, inventory and inventory-resets on every load.

// Hour (local, 24h) at which stock automatically resets to the daily defaults.
// 23 = 11pm, set by the owner so the restock lands after the day's trading has
// fully closed rather than partway through evening service.
const INVENTORY_RESET_HOUR = 23;

const isRetryableOrderSyncError = (error) => {
  const status = Number(error?.status || 0);
  if (status === 401 || status === 403) return false;
  if (status >= 500) return true;

  const message = String(error?.message || '').toLowerCase();
  return !status || status >= 400 || message.includes('server error') || message.includes('schema cache');
};

const isRetryableSyncPayloadError = (error) => {
  const status = Number(error?.status || 0);
  if (status === 401 || status === 403) return false;
  return !status || status >= 400;
};

const MenuCategoryIcon = ({ category, className = 'h-4 w-4' }) => {
  const sharedProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
  };

  switch (category) {
    case 'mainPackages':
      return <svg {...sharedProps}><path d="M4 7h16" /><path d="M6 7l1 10a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-10" /><path d="M9 11h6" /><path d="M10 15h4" /></svg>;
    case 'extras':
      return <svg {...sharedProps}><path d="M8 5h8" /><path d="M10 5v5a4 4 0 1 0 4 0V5" /><path d="M6 19h12" /></svg>;
    default:
      return <svg {...sharedProps}><path d="M12 3v18" /><path d="M6 7h12" /><path d="M7 12h10" /><path d="M8 17h8" /></svg>;
  }
};

const RestaurantPOS = () => {
  const POS_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  const menuData = {
    mainPackages: [
      { name: "Regular chicken", price: 39, image: "/Images/menu/regular-chicken.webp" },
      { name: "Regular fish", price: 48, image: "/Images/menu/regular-fish.webp" },
      { name: "Turkey delight", price: 48, image: "/Images/menu/turkey-delight.webp" },
      { name: "Double k sardine", price: 50, image: "/Images/menu/double-k-sardine.webp" },
      { name: "Double k tuna", price: 65, image: "/Images/menu/double-k-tuna.webp" },
      { name: "Pork lovers", price: 60, image: "/Images/menu/pork-lovers.webp" },
      { name: "Bossu", price: 62, image: "/Images/menu/bossu.webp" },
      { name: "YOLO / Twa we num", price: 150, image: "/Images/menu/yolo.webp" },
      { name: "Cedi saver", price: 24, image: "/Images/menu/cedi-saver.jpg" },
      { name: "Beefy up", price: 30, image: "/Images/menu/beefy-up.jpg" },
      { name: "Meatlovers (Pork)", price: 100, image: "/Images/menu/meatlovers-pork.webp" },
      { name: "Meatlovers (Gizzard)", price: 100, image: "/Images/menu/meatlovers-gizzard.webp" },
      { name: "Chili Rice", price: 100, image: "/Images/menu/assorted-rice.jpg" },
      { name: "Gizzard pack with 1 kenkey", price: 45, image: "/Images/menu/gizzard-pack-1-kenkey.webp" },
      { name: "Gizzard pack with 2 kenkey", price: 50, image: "/Images/menu/gizzard-pack-2-kenkey.webp" }
    ],
    extras: [
      { name: "1 plate of chili rice", price: 20, image: "/Images/extras/assorted-rice-plate.webp" },
      { name: "1 kenkey", price: 8, image: "/Images/extras/kenkey.webp" },
      { name: "1 omelette", price: 7, image: "/Images/extras/omelette.webp" },
      { name: "1 piece chicken", price: 13, image: "/Images/extras/chicken-piece.webp" },
      { name: "1 piece fish", price: 17, image: "/Images/extras/fish-piece.webp" },
      { name: "1 beef sausage", price: 10, image: "/Images/extras/beef-sausage.webp" },
      { name: "Extra komis sauce", price: 8, image: "/Images/extras/komis-sauce.webp" },
      { name: "1 kenkey + sauces", price: 10, image: "/Images/extras/kenkey-sauces.webp" },
      { name: "1 chicken sausage", price: 7, image: "/Images/extras/chicken-sausage.webp" },
      { name: "1 tin sardine", price: 17, image: "/Images/extras/tin-sardine.webp" },
      { name: "1 tin tuna", price: 27, image: "/Images/extras/tin-tuna.webp" },
      { name: "1 bottle sobolo (small)", price: 10, image: "/Images/extras/sobolo-small.webp" },
      { name: "1 bottle sobolo (big)", price: 12, image: "/Images/extras/sobolo-big.webp" },
      { name: "1 turkey", price: 17, image: "/Images/extras/turkey.webp" },
      { name: "Pork", price: 30, image: "/Images/extras/pork.webp" },
      { name: "Gizzard", price: 20, image: "/Images/extras/gizzard.webp" },
      { name: "Soft drinks", price: 10, image: "/Images/extras/soft-drinks.webp" },
      { name: "Bottled water", price: 5, image: "/Images/extras/bottled-water.webp" },
      { name: "Ceres juice", price: 26, image: "/Images/extras/ceres-juice.webp" },
      { name: "Tsoofi", price: 35, image: "/Images/extras/tsoofi.webp" },
      { name: "Shrimps", price: 20, image: "/Images/extras/shrimps.webp" }
    ],
    promotions: [
      { name: "Students Promo - Option 1", price: 50, image: "/Images/promos/students-option1.webp", description: "1 kenkey, chicken, egg, gizzard" },
      { name: "Students Promo - Option 2", price: 50, image: "/Images/promos/students-option2.webp", description: "1 kenkey, turkey, fish, egg" },
      { name: "BBQ wings Promo", price: 100, description: "2 kenkey, 1 fish, 4 chicken wings (barbecue, kebab pepper or plain), 2 eggs, 1 beef sausage, 1 sobolo" },
      { name: "Love box package", price: 200, image: "/Images/promos/love-box.jpg", description: "Special love box package" }
    ]
  };
  // Core state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const currentUserRef = useRef(null); // mirror for reading in stale closures (intervals, event handlers)
  const [, setPosAccessToken] = useState('');
  const posAccessTokenRef = useRef('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  // Sign-in can take several seconds on tablet Wi-Fi; without this the button
  // looked dead and staff tapped it again.
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const createEmptyBundle = (id, name) => ({ id, name, items: {}, packaging: defaultBundlePackaging });
  const bundleHasItems = (bundle) => Object.values(bundle?.items || {}).some(item => item.quantity > 0);
  const [currentBundles, setCurrentBundles] = useState([createEmptyBundle(Date.now(), 'Package 1')]);
  const [activeBundleId, setActiveBundleId] = useState(currentBundles[0]?.id);
  const [orders, setOrders] = useState([]);
  const [syncStatus, setSyncStatus] = useState('idle');
  const syncStatusRef = useRef('idle'); // mirror for reading in stale closures (intervals)
  // Queued records the signed-in login was refused (403), keyed `${kind}:${id}`.
  // They stay in the tablet's queue but are not retried under this login, which
  // used to flip the header between Error and Synced on every sync. Cleared at
  // each sign-in so the next person gets a fresh attempt.
  const heldSyncKeysRef = useRef(new Set());
  const heldNoticeShownRef = useRef(false);
  const setSyncStatusSafe = (val) => {
    // Any successful sync proves the pipe works again — reset the failed-write
    // retry backoff so pending records flush promptly.
    if (val === 'success') retryAttemptRef.current = 0;
    // "Synced" would hide records still waiting on this tablet for another login.
    const shown = val === 'success' && heldSyncKeysRef.current.size > 0 ? 'held' : val;
    syncStatusRef.current = shown;
    setSyncStatus(shown);
  };
  // Drives the Analytics KPI skeleton. True while a cloud orders fetch is in
  // flight (and on first mount, so we don't render the localStorage-hydrated
  // partial numbers before the cloud refresh resolves). The analytics-tab
  // effect debounces this 250ms for subsequent fetches so quick incremental
  // round-trips don't flash the skeleton on every filter click.
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const analyticsHasResolvedOnceRef = useRef(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState([]);
  const [currentTab, setCurrentTab] = useState('pos');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('pos_dark_mode') === 'true');
  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => { const next = !prev; localStorage.setItem('pos_dark_mode', String(next)); return next; });
  }, []);
  const [dateFilter, setDateFilter] = useState('today');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const setPosAccessTokenSafe = useCallback((token) => {
    posAccessTokenRef.current = token || '';
    setPosAccessToken(token || '');
  }, []);
  const persistAuthSession = useCallback((user, accessToken) => {
    const payload = { user, accessToken, timestamp: Date.now() };
    localStorage.setItem('restaurant_auth', JSON.stringify(payload));
    currentUserRef.current = user; // sync ref immediately so stale closures see the user
    setCurrentUser(user);
    setIsAuthenticated(true);
    setPosAccessTokenSafe(accessToken);
  }, [setPosAccessTokenSafe]);
  const [customDate, setCustomDate] = useState(''); // single-day picker used by compact tabs
  const [notification, setNotification] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Guards against a double-tap confirming the same order's payment twice.
  const payingOrdersRef = useRef(new Set());
  const [celebrateOrder, setCelebrateOrder] = useState(false);
  // POS inputs
  const [orderType, setOrderType] = useState('online');
  const [orderSource, setOrderSource] = useState('');
  const [orderSourceDetail, setOrderSourceDetail] = useState('');
  // Walk-in only makes sense for in-person orders: preselect it when the order
  // type flips to in-person (still changeable), and clear it when flipping back
  // to online so staff must pick the real channel.
  useEffect(() => {
    if (orderType === 'in-person' && !orderSource) {
      setOrderSource('walk-in');
    } else if (orderType === 'online' && orderSource === 'walk-in') {
      setOrderSource('');
    }
  }, [orderType, orderSource]);
  useEffect(() => { setOrderSourceDetail(''); }, [orderSource]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryFeeInput, setDeliveryFeeInput] = useState('');
  const [notes, setNotes] = useState('');
  // Cart discount: a percentage off the items subtotal only. Admins apply it
  // directly; everyone else can only request it (capped, and the cart total
  // stays at full price until an admin approves).
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountReasonDetail, setDiscountReasonDetail] = useState('');
  useEffect(() => { setDiscountReasonDetail(''); }, [discountReason]);
  const [posPaymentMethod, setPosPaymentMethod] = useState(''); // 'momo' | 'cash' | 'card' | 'split'
  const [splitPayments, setSplitPayments] = useState([]); // [{method: 'momo', amount: 100}, {method: 'cash', amount: 50}]
  const [showSplitPaymentModal, setShowSplitPaymentModal] = useState(false);
  const [showRiderAssignmentModal, setShowRiderAssignmentModal] = useState(false);
  const [riderAssignmentTarget, setRiderAssignmentTarget] = useState(null);
  const [riderAssignmentForm, setRiderAssignmentForm] = useState({ riderId: '', note: '' });
  const [riders, setRiders] = useState([]);
  const [riderPresence, setRiderPresence] = useState([]);
  const [riderAssignments, setRiderAssignments] = useState([]);
  // Queue input for cash
  const [paymentInputs, setPaymentInputs] = useState({}); // { [orderId]: amountString }
  // Responsive + header menu
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef(null);
  // Amend modal state
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [amendTarget, setAmendTarget] = useState(null);
  const [amendDraftBundles, setAmendDraftBundles] = useState([]);
  const [amendReason, setAmendReason] = useState('');
  const [amendPaymentMethod, setAmendPaymentMethod] = useState('');
  const [amendSplitPayments, setAmendSplitPayments] = useState([]); // [{method, amount}]
  const [amendCustomerName, setAmendCustomerName] = useState('');
  const [amendCustomerPhone, setAmendCustomerPhone] = useState('');
  const [amendCustomerAddress, setAmendCustomerAddress] = useState('');
  const [amendModalTab, setAmendModalTab] = useState('items'); // 'items' | 'details'
  const syncIntervalRef = useRef(null);
  // Stock cycle (cycleDateStr) this device has already confirmed a reset for, so
  // the poller only spends a request on inventory-resets when the 23:00 boundary
  // has actually been crossed.
  const resetCycleRef = useRef(null);
  const syncEmptyTicksRef = useRef(0);
  // True only while a periodic tick is actually running. Separate from
  // syncStatus, which is a UI label that failed writes can leave stuck.
  const syncTickInFlightRef = useRef(false);
  // Latest tick function, so wake-ups (screen on, back online, tab switch) can
  // run a sync immediately instead of waiting out the adaptive backoff.
  const runPeriodicTickRef = useRef(null);
  // Bumped on every start/stop so a tick from a previous session cannot
  // resurrect the poller after logout.
  const syncGenerationRef = useRef(0);
  // Last reported state of the realtime channel ('SUBSCRIBED', 'CLOSED', …).
  const realtimeStatusRef = useRef('idle');
  const retryTimeoutRef = useRef(null);
  // Consecutive failed retry rounds — drives the exponential backoff in scheduleRetry.
  const retryAttemptRef = useRef(0);
  // The stretch of order history this session has fully downloaded, and when
  // (see planOrderRangeFetch). Login covers only the last few days, so a range
  // inside that asks only for changed rows while an older one is downloaded whole.
  const orderCoverageRef = useRef(null);
  // Skip re-running the analytics effect for the same range within 30s.
  const lastAnalyticsFetchRef = useRef({ rangeKey: '', when: 0 });
  // Menu search
  const [menuSearch, setMenuSearch] = useState('');
  const SOUND_SRC = '/sounds/order-is-ready.mp3';
  const [soundArmed, setSoundArmed] = useState(false);
  const prevReadyIdsRef = useRef(new Set());
  const readyInitRef = useRef(false);
  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [pendingSyncExpenses, setPendingSyncExpenses] = useState([]);
  // Complaints + marketing activity logs (fetched on demand when their tab opens)
  const [complaints, setComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  // Configured login accounts (names only) from the backend — feeds the
  // complaint "staff responsible" suggestions instead of guessing from old
  // order history.
  const [staffMembers, setStaffMembers] = useState([]);
  const [marketingActivities, setMarketingActivities] = useState([]);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [expenseDraft, setExpenseDraft] = useState({ category: '', amount: '', description: '' });
  const [expenseSearch, setExpenseSearch] = useState('');
  const [showExpenseEditModal, setShowExpenseEditModal] = useState(false);
  const [editExpenseTarget, setEditExpenseTarget] = useState(null);
  const [showExpenseDeleteConfirm, setShowExpenseDeleteConfirm] = useState(false);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  // NEW: Date range filter for expenses
  const [expenseDateRange, setExpenseDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const expenseDateRangeRef = useRef(expenseDateRange);
  useEffect(() => { expenseDateRangeRef.current = expenseDateRange; }, [expenseDateRange]);
  // ISO time of the last successful periodic sync — sent as updatedSince so the
  // backend returns only orders/inventory changed since then. null = full fetch.
  const periodicSyncSinceRef = useRef(null);
  
  // NEW: Employee expense submission state
  const [myExpenses] = useState([]);
  const [showMyExpenseModal, setShowMyExpenseModal] = useState(false);
  const [myExpenseForm, setMyExpenseForm] = useState({ category: '', amount: '', description: '' });
  // Expense bulk-select & confirm-dialog state
  const [selectedExpenseIds, setSelectedExpenseIds] = useState(new Set());
  // null | { type:'approve'|'reject', ids:[…], message:string }
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Confirmation for placing order
  const [showOrderConfirm, setShowOrderConfirm] = useState(false);
  // Inventory Management
  const [inventory, setInventory] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [inventoryForm, setInventoryForm] = useState({ itemName: '', currentStock: '', lowStockThreshold: '', dailyDefault: '', unit: 'piece', costPerUnit: '' });
  const [editInventoryItem, setEditInventoryItem] = useState(null);
  const [inventorySearch, setInventorySearch] = useState('');
  const [showStockAlert, setShowStockAlert] = useState(false);
  const [stockMovements, setStockMovements] = useState([]);
  const [showMovementsModal, setShowMovementsModal] = useState(false);
  const [pendingSyncInventory, setPendingSyncInventory] = useState([]);
  // Ref mirrors of the three offline queues. The flush routines run from timers
  // and event handlers that hold stale closures, and they cannot read state
  // through a no-op setState updater — React 18 gives no guarantee about when
  // that updater runs, and an empty read means queued records never leave the
  // device. Every flush path is timer- or event-driven, so a commit-time mirror
  // is always current by the time it is read.
  // Scratch slot for the order list an updater just produced, so the persist
  // happens once outside the updater instead of on every (possibly repeated)
  // invocation of it.
  const nextOrdersToPersistRef = useRef(null);
  const pendingSyncRef = useRef([]);
  const pendingSyncExpensesRef = useRef([]);
  const pendingSyncInventoryRef = useRef([]);
  useEffect(() => { pendingSyncRef.current = pendingSync; }, [pendingSync]);
  useEffect(() => { pendingSyncExpensesRef.current = pendingSyncExpenses; }, [pendingSyncExpenses]);
  useEffect(() => { pendingSyncInventoryRef.current = pendingSyncInventory; }, [pendingSyncInventory]);
  // For flashing and alerts
  const notifiedPrep = useRef(new Set());
  const notifiedWait = useRef(new Set());
  // now / kitchenNow live in ClockProvider so their 30/60s ticks don't re-render
  // the whole AppContext consumer tree. Consume them via useClock().
  // Feature: End-of-Day Report
  const [showEodReport, setShowEodReport] = useState(false);
  const [eodReportDate, setEodReportDate] = useState(new Date().toISOString().split('T')[0]);
  // Feature: Export Data
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportRangeType, setExportRangeType] = useState('single'); // 'single' | 'range'
  const [exportSingleDate, setExportSingleDate] = useState(new Date().toISOString().split('T')[0]);
  const [exportStartDate, setExportStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [exportEndDate, setExportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [exportTypes, setExportTypes] = useState({ orders: true, expenses: true });
  const [exportOrderStatuses, setExportOrderStatuses] = useState({ all: true, picked_up: false, delivered: false, paid: false, ready: false, pending_payment: false });
  // Feature: Kitchen Display Mode
  const [kitchenDisplayMode, setKitchenDisplayMode] = useState(false);
  // =============================
  // EXPORT / CSV FUNCTIONS
  // =============================
  const getExportDateRange = () => {
    if (exportRangeType === 'single') {
      const start = new Date(exportSingleDate + 'T00:00:00');
      const end = new Date(exportSingleDate + 'T23:59:59.999');
      return { start, end };
    } else {
      const start = new Date(exportStartDate + 'T00:00:00');
      const end = new Date(exportEndDate + 'T23:59:59.999');
      return { start, end };
    }
  };

  const escapeCsvCell = (val) => {
    const s = String(val == null ? '' : val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const arrayToCsv = (headers, rows) => {
    const lines = [headers.map(escapeCsvCell).join(',')];
    rows.forEach(row => lines.push(row.map(escapeCsvCell).join(',')));
    return lines.join('\n');
  };

  const downloadCsv = (csvString, filename) => {
    const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const runExport = () => {
    const { start, end } = getExportDateRange();
    const dateLabel = exportRangeType === 'single'
      ? exportSingleDate
      : `${exportStartDate}_to_${exportEndDate}`;

    let exported = 0;

    if (exportTypes.orders) {
      let filtered = orders.filter(o => {
        const ts = new Date(o.timestamp);
        return ts >= start && ts <= end;
      });

      if (!exportOrderStatuses.all) {
        const activeStatuses = Object.entries(exportOrderStatuses)
          .filter(([k, v]) => k !== 'all' && v)
          .map(([k]) => k);
        if (activeStatuses.length > 0) {
          filtered = filtered.filter(o => activeStatuses.includes(o.status));
        }
      }

      if (filtered.length > 0) {
        const headers = [
          'Order ID', 'Date/Time', 'Order Type', 'Order Source', 'Status',
          'Customer Name', 'Customer Phone', 'Delivery Address',
          'Items Summary', 'Item Count', 'Discount %', 'Discount (GHS)', 'Discount Reason', 'Total (GHS)',
          'Payment Method', 'Processed By', 'Rider Name', 'Rider Payout (GHS)', 'Notes'
        ];
        const rows = filtered
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          .map(o => {
            const riderInfo = getOrderAssignedRider(o);
            const discountAdjustment = getLatestOrderAdjustment(o, 'discount');
            const discountValue = getOrderDiscountAmount(o);

            return [
              '#' + o.id.slice(-6),
              new Date(o.timestamp).toLocaleString(),
              o.orderType === 'in-person' ? 'In-Person' : 'Online',
              getOrderSourceLabel(o),
              STATUS_STYLES[o.status]?.label || o.status || '',
              o.customerName || (o.orderType === 'in-person' ? 'Walk-in' : 'Online Customer'),
              o.customerPhone || '',
              o.deliveryAddress || '',
              (o.items || []).map(i => `${i.name} x${i.quantity}`).join(' | '),
              (o.items || []).reduce((s, i) => s + (Number(i.quantity) || 0), 0),
              discountAdjustment?.percent ? String(discountAdjustment.percent) : '',
              discountValue > 0 ? discountValue.toFixed(2) : '',
              discountValue > 0 ? getDiscountReasonLabel(discountAdjustment?.reason, discountAdjustment?.reasonDetail) : '',
              Number(o.total || 0).toFixed(2),
              methodLabels[o.paymentMethod] || o.paymentMethod || '',
              o.processedBy || '',
              riderInfo?.riderName || '',
              getOrderDeliveryFee(o).toFixed(2),
              o.notes || ''
            ];
          });
        downloadCsv(arrayToCsv(headers, rows), `komis_orders_${dateLabel}.csv`);
        exported++;
      } else {
        showToast('No orders found for the selected period/filters', 'warning');
        return;
      }
    }

    if (exportTypes.expenses) {
      const filtered = expenseLedger.filter(e => {
        const ts = new Date(e.timestamp);
        return ts >= start && ts <= end;
      });

      if (filtered.length > 0) {
        const headers = [
          'Expense ID', 'Date/Time', 'Category', 'Amount (GHS)',
          'Description', 'Submitted By', 'Status'
        ];
        const rows = filtered
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          .map(e => [
            e.id?.slice(-6) || '',
            new Date(e.timestamp).toLocaleString(),
            e.category || '',
            Number(e.amount || 0).toFixed(2),
            e.description || '',
            e.by || '',
            EXPENSE_STATUS_STYLES[e.status]?.label || e.status || ''
          ]);
        downloadCsv(arrayToCsv(headers, rows), `komis_expenses_${dateLabel}.csv`);
        exported++;
      } else if (!exportTypes.orders) {
        showToast('No expenses found for the selected period', 'warning');
        return;
      }
    }

    if (exported > 0) {
      showToast(`Export successful! ${exported} file(s) downloaded.`, 'success');
      setShowExportModal(false);
    }
  };

  // Each toast cancels the previous one's timers — otherwise an earlier toast's
  // timeout would dismiss a newer toast early. Leaving is a separate step so the
  // toast can slide out the way it came before it unmounts.
  const toastTimersRef = useRef({ hide: null, remove: null });
  const showToast = (message, type = 'success', duration = 2500) => {
    clearTimeout(toastTimersRef.current.hide);
    clearTimeout(toastTimersRef.current.remove);
    setNotification({ message, type, id: Date.now(), leaving: false });
    toastTimersRef.current.hide = setTimeout(() => {
      setNotification((current) => (current ? { ...current, leaving: true } : current));
      toastTimersRef.current.remove = setTimeout(() => setNotification(null), TOAST_EXIT_MS);
    }, duration);
  };
  // =============================
// THERMAL RECEIPT PRINT FUNCTION
// =============================
const printReceiptWindow = (order) => {
  const deliveryVerificationCode = getOrderDeliveryVerificationCode(order);
  const discountAdjustment = getLatestOrderAdjustment(order, 'discount');
  const discountValue = getOrderDiscountAmount(order);
  const receiptWindow = window.open('', 'PRINT', 'height=700,width=420,scrollbars=yes');

  const businessName = 'Komis Kenkey';
  const tagline = 'Best Kenkey in East Legon!';
  const address = 'Accra, Ghana';
  const phone = '059 976 7244';
  const logoPath = window.location.origin + '/Images/komis.png';

  const formatItem = (item) => {
    const name = item.name.length > 16 ? item.name.slice(0, 16) : item.name.padEnd(16, ' ');
    const qty = String(item.quantity).padStart(2, ' ');
    const price = Number(item.price).toFixed(2).padStart(6, ' ');
    return `${name}${qty} x ${price}`;
  };

  receiptWindow.document.write(`
    <html>
    <head>
      <title>Receipt - Order #${order.id.slice(-6)}</title>
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
        }
        * { box-sizing: border-box; }
        body {
          font-family: monospace, sans-serif;
          font-size: 12px;
          width: 80mm;
          margin: 0 auto;
          padding: 4mm;
          background: #fff;
        }
        .receipt-container { width: 100%; }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        img.logo {
          width: 40mm;
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0 auto 6px auto;
        }
        hr { border-top: 1px dashed #000; margin: 6px 0; }
        pre { margin: 2px 0; font-size: 11px; }

        /* Action bar — only visible on screen, hidden when printing */
        .action-bar {
          display: flex;
          gap: 8px;
          justify-content: center;
          padding: 12px 0 8px;
          border-top: 1px solid #e5e7eb;
          margin-top: 12px;
        }
        .btn-print {
          background: #2563eb;
          color: #fff;
          border: none;
          padding: 8px 20px;
          border-radius: 6px;
          font-size: 13px;
          cursor: pointer;
          font-family: sans-serif;
        }
        .btn-print:hover { background: #1d4ed8; }
        .btn-close {
          background: #e5e7eb;
          color: #374151;
          border: none;
          padding: 8px 20px;
          border-radius: 6px;
          font-size: 13px;
          cursor: pointer;
          font-family: sans-serif;
        }
        .btn-close:hover { background: #d1d5db; }

        @media print {
          @page { size: 80mm auto; margin: 0; }
          body { margin: 0 !important; padding: 4mm !important; }
          .action-bar { display: none !important; }
        }
      </style>
    </head>
    <body>
      <div class="receipt-container">
        <div class="center"><img src="${logoPath}" class="logo" onerror="this.style.display='none'" /></div>
        <div class="center bold">${businessName}</div>
        <div class="center">${tagline}</div>
        <div class="center">${address}</div>
        <div class="center">${phone}</div>
        <hr />
        <div><span class="bold">Order ID:</span> #${order.id.slice(-6)}</div>
        <div><span class="bold">Date:</span> ${new Date(order.timestamp).toLocaleString()}</div>
        <div><span class="bold">Payment:</span> ${order.paymentMethod}</div>
        ${order.customerName ? `<div><span class="bold">Customer:</span> ${order.customerName}</div>` : ''}
        ${deliveryVerificationCode ? `<div><span class="bold">Delivery Code:</span> ${deliveryVerificationCode}</div>` : ''}
        <hr />
        <div class="bold">ITEMS</div>
        ${order.items.map(i => `<pre>${formatItem(i)}</pre>`).join('')}
        <hr />
        ${discountValue > 0 ? `<div>DISCOUNT${discountAdjustment?.percent ? ` (${discountAdjustment.percent}%)` : ''}: -GHS ${discountValue.toFixed(2)}</div>` : ''}
        <div class="bold">TOTAL: GHS ${Number(order.total).toFixed(2)}</div>
        <hr />
        <div class="center">Thank you for your purchase!</div>
        <div class="center">Powered by Komis POS</div>

        <div class="action-bar">
          <button class="btn-print" onclick="window.print()">🖨️ Print</button>
          <button class="btn-close" onclick="window.close()">✕ Close</button>
        </div>
      </div>
    </body>
    </html>
  `);

  receiptWindow.document.close();
};

// =============================
// END-OF-DAY REPORT COMPUTATION
// =============================
const computeEodReport = (dateStr) => buildEodReport({ dateStr, orders, expenses });
  useEffect(() => {
    const savedAuth = localStorage.getItem('restaurant_auth');
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        if (authData?.user && authData?.accessToken && authData.timestamp > Date.now() - POS_SESSION_MAX_AGE_MS) {
          currentUserRef.current = authData.user; // sync ref immediately before initializeApp
          setCurrentUser(authData.user);
          setIsAuthenticated(true);
          setPosAccessTokenSafe(authData.accessToken);
          // Land on a tab this user actually has, exactly as handleLogin does.
          // Without this a restored session kept the initial 'pos' value.
          setCurrentTab(authData.user.permissions?.[0] || 'pos');
          initializeApp(authData.user, authData.accessToken);
        } else {
          localStorage.removeItem('restaurant_auth');
        }
      } catch {
        localStorage.removeItem('restaurant_auth');
      }
    }
  }, [setPosAccessTokenSafe]);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    if (!isMobile) {
      setShowMobileCart(false);
      setShowMobileMoreMenu(false);
    }
  }, [isMobile]);
  useEffect(() => {
    const onDocClick = (e) => { if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) setShowHeaderMenu(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  // NEW: Reload expenses when date range changes
  useEffect(() => {
    if (isOnline && currentUser?.role === 'admin') {
      fetchExpensesFromCloud().then(cloudExpenses => {
        if (cloudExpenses) {
          setExpenses(prev => {
            const merged = mergeExpenses(prev, cloudExpenses);
            saveToLocal({ expenses: merged });
            return merged;
          });
        }
      });
    }
  }, [expenseDateRange.startDate, expenseDateRange.endDate]);
  // clear expense checkboxes whenever the active tab changes
  useEffect(() => {
    setSelectedExpenseIds(new Set());
  }, [currentTab]);
  // When switching away from 'custom' filter, clear both date pickers so stale values don't linger
  useEffect(() => {
    if (dateFilter !== 'custom') {
      setCustomDate('');
    }
  }, [dateFilter]);
  useEffect(() => {
    const checkAlerts = () => {
      const currentTime = Date.now();
      orders.forEach(o => {
        if (o.status === 'paid' && o.paidAt) {
          const paidTime = new Date(o.paidAt).getTime();
          const prepMin = (currentTime - paidTime) / 60000;
          if (prepMin > 15 && !notifiedPrep.current.has(o.id)) {
            showToast(`Order #${o.id.slice(-6)} is approaching 20 min prep time!`, 'warning', 5000);
            notifiedPrep.current.add(o.id);
          }
        } else if (o.status === 'ready' && o.readyAt) {
          const readyTime = new Date(o.readyAt).getTime();
          const waitMin = (currentTime - readyTime) / 60000;
          if (waitMin > 5 && !notifiedWait.current.has(o.id)) {
            showToast(`Customer for order #${o.id.slice(-6)} has been waiting >5 min in Ready queue!`, 'warning', 5000);
            notifiedWait.current.add(o.id);
          }
        }
      });
    };
    checkAlerts();
    const alertInterval = setInterval(checkAlerts, 60000);
    return () => clearInterval(alertInterval);
  }, [orders]);
  const initializeApp = (user, accessTokenOverride = '') => {
    if (accessTokenOverride) {
      setPosAccessTokenSafe(accessTokenOverride);
    }
    // A new sign-in may be allowed what the last one was refused.
    resetHeldUploads();
    // Orders reload from local storage, which may hold fewer than were in memory.
    orderCoverageRef.current = null;
    loadLocalData();
    setupOnlineStatusListeners();
    startPeriodicSync();
    initialCloudSync();
    initialExpensesSync(user);
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLoggingIn) return;
    setLoginError('');
    setIsLoggingIn(true);
    // Unlock audio while the tap is still the active user gesture. Browsers
    // refuse to start an AudioContext outside one, and doing it after the
    // network round-trip below can fall outside the activation window. Without
    // this the new-order alarm stays silent until somebody happens to press
    // "Enable sound", and nothing would tell them it had.
    alertSound.arm();
    try {
      const data = await createPosSession(loginForm.username.trim(), loginForm.password);
      const user = data.user;
      persistAuthSession(user, data.accessToken);
      setCurrentTab(user.permissions[0] || 'pos');
      initializeApp(user, data.accessToken);
      setLoginForm({ username: '', password: '' });
      showToast(`Welcome back, ${user.fullName}!`);
    } catch (error) {
      // Show "wrong credentials" for auth failures, but a connectivity message
      // when the server is unreachable — never the raw backend text.
      setLoginError(safeErrorMessage(error, 'Invalid username or password', {
        context: 'login',
        statusOverrides: { 401: 'Invalid username or password', 403: 'Invalid username or password' },
      }));
    } finally {
      setIsLoggingIn(false);
    }
  };
  const handleLogout = () => {
    currentUserRef.current = null;
    resetHeldUploads();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setPosAccessTokenSafe('');
    localStorage.removeItem('restaurant_auth');
    stopPeriodicSync();
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    window.removeEventListener('online', handleOnlineStatusChange);
    window.removeEventListener('offline', handleOnlineStatusChange);
    const newId = Date.now();
    setCurrentBundles([createEmptyBundle(newId, 'Package 1')]);
    setActiveBundleId(newId);
    setCurrentTab('pos');
    setLoginForm({ username: '', password: '' });
    setCustomerName(''); setCustomerPhone(''); setDeliveryAddress(''); setDeliveryFeeInput(''); setNotes(''); setPosPaymentMethod('');
  };
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );

  // Every signed-in role subscribes. A new website order has to reach whoever
  // is actually on shift, not just the owner — and push is the only thing that
  // can wake a tablet whose screen has gone off.
  const isPushEligible = Boolean(currentUser) && isPushSupported();

  // Subscribe this device and register the subscription with the backend, which
  // identifies the admin from the POS access token. Used by both the explicit
  // "Enable" action and the silent refresh on each admin login.
  const registerAdminPush = useCallback(async () => {
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      console.warn('Push: REACT_APP_VAPID_PUBLIC_KEY is not set; cannot subscribe.');
      return false;
    }
    const subscription = await subscribeToPush(vapidKey);
    if (!subscription) return false;
    const saved = await saveSubscription(subscription, posAccessTokenRef.current);
    if (saved) setNotificationsEnabled(true);
    return saved;
  }, []);

  // Fire a real on-device notification the moment an admin turns notifications
  // on, so they get immediate proof the alerts will show up here. (Only on the
  // explicit enable action — never on the silent re-register at login.)
  const showEnableConfirmation = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification('Notifications are on ✅', {
        body: "You'll get alerts here for new website orders, kitchen delays, approvals, low stock and large transactions.",
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-96x96.png',
        tag: 'admin-push-enabled',
      });
    } catch {
      // showing the confirmation is best-effort
    }
  }, []);

  // Push notification permission — ask admins once after login.
  useEffect(() => {
    if (!isAuthenticated || !isPushEligible) return;
    if (localStorage.getItem('push_permission_asked')) return;

    // Delay the prompt slightly so login doesn't feel jarring
    const timer = setTimeout(() => {
      if (!localStorage.getItem('push_permission_asked')) {
        setShowPushPrompt(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isAuthenticated, isPushEligible]);

  // If an admin already granted permission (prior session / another device),
  // silently refresh the subscription on login so a rotated endpoint or token
  // stays registered with the backend.
  useEffect(() => {
    if (!isAuthenticated || !isPushEligible) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    registerAdminPush();
  }, [isAuthenticated, isPushEligible, registerAdminPush]);

  const handlePushAccept = useCallback(async () => {
    setShowPushPrompt(false);
    localStorage.setItem('push_permission_asked', 'true');
    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      const ok = await registerAdminPush();
      if (ok) {
        showToast('Notifications enabled', 'success');
        showEnableConfirmation();
      } else {
        showToast('Could not enable notifications. Try again from the menu.', 'warning');
      }
    }
  }, [registerAdminPush, showEnableConfirmation]);

  const handlePushDismiss = useCallback(() => {
    setShowPushPrompt(false);
    localStorage.setItem('push_permission_asked', 'true');
  }, []);

  // Menu toggle: turn admin notifications on (request + subscribe) or off
  // (unsubscribe locally and remove from the backend).
  // Header menu "Install app": the install banner disappears for good once
  // someone taps "Not now", which left that tablet with no way to install.
  // Opens the browser's install dialog when it has offered one, otherwise says
  // how to install on this device.
  const handleInstallApp = async () => {
    if (canPromptInstall() && (await promptInstall()) !== 'unavailable') return;
    showToast(installHelpText(), 'info', 10000);
  };

  const handleToggleNotifications = useCallback(async () => {
    if (notificationsEnabled) {
      const ok = await unsubscribeFromPush(posAccessTokenRef.current);
      if (ok) {
        setNotificationsEnabled(false);
        showToast('Notifications turned off', 'success');
      } else {
        showToast('Could not turn off notifications.', 'warning');
      }
      return;
    }
    localStorage.setItem('push_permission_asked', 'true');
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      showToast('Notifications are blocked in your browser settings.', 'warning');
      return;
    }
    const ok = await registerAdminPush();
    showToast(ok ? 'Notifications enabled' : 'Could not enable notifications.', ok ? 'success' : 'warning');
    if (ok) showEnableConfirmation();
  }, [notificationsEnabled, registerAdminPush, showEnableConfirmation]);

  const hasPermission = (permission) => currentUser && currentUser.permissions.includes(permission);
  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'pos', label: 'POS System', permission: 'pos', icon: '🛒', active: TAB_ACTIVE_CLASSES.pos },
      { id: 'queue', label: 'Payment Queue', permission: 'queue', icon: '💳', active: TAB_ACTIVE_CLASSES.queue },
      { id: 'kitchen', label: 'Kitchen', permission: 'kitchen', icon: '👨‍🍳', active: TAB_ACTIVE_CLASSES.kitchen },
      { id: 'history', label: 'History', permission: 'history', icon: '📋', active: TAB_ACTIVE_CLASSES.history },
      { id: 'riders', label: 'Riders', permission: 'riders', icon: '🛵', active: TAB_ACTIVE_CLASSES.riders },
      { id: 'analytics', label: 'Analytics', permission: 'analytics', icon: '📊', active: TAB_ACTIVE_CLASSES.analytics },
      { id: 'eod', label: 'End-of-Day Sales', permission: 'eod', icon: '🧮', active: TAB_ACTIVE_CLASSES.eod },
      { id: 'expenses', label: 'Expenses', permission: 'expenses', icon: '💸', active: TAB_ACTIVE_CLASSES.expenses },
      { id: 'myexpenses', label: 'My Expenses', permission: 'myexpenses', icon: '💵', active: TAB_ACTIVE_CLASSES.myexpenses },
      { id: 'inventory', label: 'Inventory', permission: 'inventory', icon: '📦', active: TAB_ACTIVE_CLASSES.inventory },
      { id: 'complaints', label: 'Complaints', permission: 'pos', icon: '⚠️', active: TAB_ACTIVE_CLASSES.complaints }
    ];
    const tabs = baseTabs.filter(t => (t.id === 'eod' ? canViewEodSales(currentUser) : hasPermission(t.permission)));
    if (currentUser?.role === 'admin') {
      const kitchenIdx = tabs.findIndex(t => t.id === 'kitchen');
      const insertIdx = kitchenIdx >= 0 ? kitchenIdx + 1 : 1;
      tabs.splice(insertIdx, 0, { id: 'approvals', label: 'Approvals', permission: 'approvals', icon: '✅', active: TAB_ACTIVE_CLASSES.approvals });
      // Admins get the complaints tab even when their permission list predates it.
      if (!tabs.some(existing => existing.id === 'complaints')) {
        tabs.push(baseTabs.find(t => t.id === 'complaints'));
      }
    }
    // Marketing: the owner, and supervisors who log campaigns ('marketing').
    if (canUseMarketing(currentUser)) {
      tabs.push({ id: 'marketing', label: 'Marketing', permission: 'marketing', icon: '📢', active: TAB_ACTIVE_CLASSES.marketing });
    }
    // Finance: the accountant (finance permission) and the owner. Admin
    // permission lists predate this tab, so admins get it by role.
    if (currentUser?.role === 'admin' || hasPermission('finance')) {
      tabs.push({ id: 'finance', label: 'Finance', permission: 'finance', icon: '🧾', active: TAB_ACTIVE_CLASSES.finance });
    }
    return tabs;
  };
  // Deep-link from a push notification: ?tab=<id> opens that tab (if permitted),
  // then the param is stripped so a refresh doesn't keep forcing it.
  useEffect(() => {
    if (!isAuthenticated) return;
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    if (!requestedTab) return;
    if (getAvailableTabs().some(t => t.id === requestedTab)) setCurrentTab(requestedTab);
    window.history.replaceState({}, '', window.location.pathname);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  const sessionExpiredRef = useRef(false);
  const handleSessionExpired = () => {
    if (sessionExpiredRef.current) return; // only show once
    sessionExpiredRef.current = true;
    stopPeriodicSync();
    showToast('Session expired — please log in again.', 'error', 10000);
    handleLogout();
  };
  const isAuthError = (e) => e && e.status === 401;
  const handleOnlineStatusChange = () => {
    setIsOnline(navigator.onLine);
    if (navigator.onLine) { showToast('Back online! Syncing data...', 'success'); syncPendingOrders(); syncPendingExpenses(); syncPendingInventory(); refreshRiderData(); }
    else { setSyncStatusSafe('offline'); showToast('Working offline', 'warning'); }
  };
  const setupOnlineStatusListeners = () => {
    // Remove first to prevent stacking on re-init
    window.removeEventListener('online', handleOnlineStatusChange);
    window.removeEventListener('offline', handleOnlineStatusChange);
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
  };
  const loadLocalData = () => {
    const savedOrders = localStorage.getItem('restaurant_orders');
    const savedPending = localStorage.getItem('pending_sync');
    const savedLastSync = localStorage.getItem('last_sync_time');
    const savedExpenses = localStorage.getItem('expenses');
    const savedPendingExpenses = localStorage.getItem('pending_sync_expenses');
    const savedInventory = localStorage.getItem('inventory');
    if (savedOrders) { try { setOrders(JSON.parse(savedOrders) || []); } catch { setOrders([]); } }
    if (savedPending) { try { setPendingSync(JSON.parse(savedPending) || []); } catch { setPendingSync([]); } }
    if (savedLastSync) setLastSyncTime(new Date(savedLastSync));
    if (savedExpenses) { try { setExpenses(JSON.parse(savedExpenses) || []); } catch { setExpenses([]); } }
    if (savedPendingExpenses) { try { setPendingSyncExpenses(JSON.parse(savedPendingExpenses) || []); } catch { setPendingSyncExpenses([]); } }
    if (savedInventory) {
      try {
        const parsed = JSON.parse(savedInventory) || [];
        // Patch any stale dailyDefault=0 entries immediately using DEFAULT_INVENTORY
        const patched = parsed.map(item => {
          if (item.ingredientId) {
            const def = DEFAULT_INVENTORY.find(d => d.id === item.ingredientId);
            if (def && (!item.dailyDefault || item.dailyDefault === 0)) {
              return { ...item, dailyDefault: def.dailyDefault };
            }
          }
          return item;
        });
        setInventory(patched);
      } catch { setInventory([]); }
    }
    const savedPendingInventory = localStorage.getItem('pending_sync_inventory');
    if (savedPendingInventory) { try { setPendingSyncInventory(JSON.parse(savedPendingInventory) || []); } catch { setPendingSyncInventory([]); } }
    const savedComplaints = localStorage.getItem('complaints');
    if (savedComplaints) { try { setComplaints(JSON.parse(savedComplaints) || []); } catch { setComplaints([]); } }
    const savedMarketing = localStorage.getItem('marketing_activities');
    if (savedMarketing) { try { setMarketingActivities(JSON.parse(savedMarketing) || []); } catch { setMarketingActivities([]); } }
  };
  // saveToLocal: persist one or more slices to localStorage.
  // Pass ONLY the slices you have fresh data for — omitted keys are left untouched
  // in localStorage (no stale-closure overwrites).
  const saveToLocal = (overrides = {}) => {
    if (overrides.orders !== undefined)            persistOrdersToStorage(overrides.orders);
    if (overrides.pending !== undefined)           safeSetLocalStorage('pending_sync',           JSON.stringify(overrides.pending));
    if (overrides.expenses !== undefined)          safeSetLocalStorage('expenses',               JSON.stringify(overrides.expenses));
    if (overrides.pendingExpenses !== undefined)   safeSetLocalStorage('pending_sync_expenses',  JSON.stringify(overrides.pendingExpenses));
    if (overrides.inventory !== undefined)         safeSetLocalStorage('inventory',              JSON.stringify(overrides.inventory));
    if (overrides.complaints !== undefined)        safeSetLocalStorage('complaints',             JSON.stringify(overrides.complaints));
    if (overrides.marketing !== undefined)         safeSetLocalStorage('marketing_activities',   JSON.stringify(overrides.marketing));
    safeSetLocalStorage('last_sync_time', new Date().toISOString());
  };
  const heldSyncKey = (kind, record) => `${kind}:${record?.id}`;
  const holdForAnotherLogin = (kind, record) => {
    heldSyncKeysRef.current.add(heldSyncKey(kind, record));
    if (heldNoticeShownRef.current) return;
    heldNoticeShownRef.current = true;
    showToast('Some changes saved on this tablet can’t be uploaded under this login. They stay saved and upload when someone with access signs in here.', 'warning', 10000);
  };
  const releaseHeld = (kind, record) => { heldSyncKeysRef.current.delete(heldSyncKey(kind, record)); };
  const resetHeldUploads = () => {
    heldSyncKeysRef.current = new Set();
    heldNoticeShownRef.current = false;
  };
  // Splits a tablet queue into what this login may upload now and what it must
  // leave for another login: either the backend's gate for that kind refuses
  // this login outright, or it already refused that record this session.
  const splitHeldUploads = (kind, records) => {
    const mayUpload = canUploadQueued(currentUserRef.current, kind);
    const held = [];
    const toSend = [];
    records.forEach((record) => {
      if (!mayUpload || heldSyncKeysRef.current.has(heldSyncKey(kind, record))) held.push(record);
      else toSend.push(record);
    });
    held.forEach((record) => holdForAnotherLogin(kind, record));
    return { held, toSend };
  };
  // Cloud sync for orders
  const addToPendingSync = (orderData) => {
    setPendingSync(prev => {
      const newPending = [
        ...prev.filter(p => p.id !== orderData.id),
        { ...orderData, syncTimestamp: Date.now() }
      ];
      localStorage.setItem('pending_sync', JSON.stringify(newPending));
      return newPending;
    });
  };
  const syncOrderToCloud = async (orderData) => {
    if (!navigator.onLine) {
      addToPendingSync(orderData);
      return false;
    }

    try {
      setSyncStatusSafe('syncing');

      const payload = buildOrderSyncPayload(orderData, currentUserRef.current, true);

      // Upsert: insert if new, update if exists — avoids .single() errors
      // when a row was already partially inserted on a previous attempt.
      let result;
      try {
        result = await mutatePosResource('orders', 'upsert', payload, posAccessTokenRef.current);
      } catch (fullPayloadError) {
        if (!isRetryableOrderSyncError(fullPayloadError)) throw fullPayloadError;

        console.warn('Order sync full payload failed; retrying with compatibility payload:', fullPayloadError);
        try {
          result = await mutatePosResource(
            'orders',
            'upsert',
            buildOrderSyncPayload(orderData, currentUserRef.current, false),
            posAccessTokenRef.current,
          );
        } catch (compatiblePayloadError) {
          if (!isRetryableOrderSyncError(compatiblePayloadError)) throw compatiblePayloadError;

          console.warn('Order sync compatibility payload failed; retrying with minimal payload:', compatiblePayloadError);
          result = await mutatePosResource(
            'orders',
            'upsert',
            buildMinimalOrderSyncPayload(orderData, currentUserRef.current),
            posAccessTokenRef.current,
          );
        }
      }

      // After a successful upsert, store the Supabase row id locally so future
      // calls take the update path (avoids duplicate key conflicts), and adopt
      // the server's last_modified in place of the optimistic one we guessed
      // from this device's clock. Without that, a till running a few minutes
      // fast keeps a local timestamp in the future, and mergeOrders — which
      // resolves conflicts by "newest lastModified wins" — would reject a
      // genuinely newer change made on another device.
      const returnedId = result?.id;
      const serverLastModified = result?.last_modified || null;
      if ((returnedId && !orderData.supabase_id) || serverLastModified) {
        setOrders(prev => {
          const updated = prev.map((o) => {
            if (o.id !== orderData.id) return o;
            // Only adopt the server stamp if nothing edited this order again
            // while the request was in flight; that later edit carries its own
            // timestamp and will stamp itself when it syncs.
            const unchangedSinceRequest = o.lastModified === orderData.lastModified;
            return {
              ...o,
              supabase_id: o.supabase_id || returnedId || null,
              lastModified: (serverLastModified && unchangedSinceRequest)
                ? serverLastModified
                : o.lastModified,
            };
          });
          saveToLocal({ orders: updated });
          return updated;
        });
      }

      releaseHeld('orders', orderData);
      setSyncStatusSafe('success');
      setLastSyncTime(new Date());
      return true;
    } catch (e) {
      if (isAuthError(e)) {
        // Queue it before logging out. Returning without queueing left the
        // order on this device only, and nothing ever retried it.
        addToPendingSync(orderData);
        handleSessionExpired();
        return false;
      }
      if (isRefusedForThisLogin(e)) {
        // E.g. a supervisor's amendment still waiting to upload when a front-desk
        // login is on the tablet ("Only a supervisor can amend orders").
        console.warn('Order upload refused for this login; holding it on this tablet:', e);
        addToPendingSync(orderData);
        holdForAnotherLogin('orders', orderData);
        setSyncStatusSafe('held');
        return false;
      }
      console.error('Supabase sync error:', e);
      setSyncStatusSafe('error');
      addToPendingSync(orderData);
      scheduleRetry();
      showToast(safeErrorMessage(e, 'Couldn’t sync the order. It’s saved and will retry.', { context: 'order-sync' }), 'error');
      return false;
    }
  };
  // Re-upload orders this device holds that never reached the cloud (see
  // findStrandedOrders). Reads storage rather than state: a restored session
  // syncs on mount, before loadLocalData's setOrders has rendered.
  const uploadStrandedOrders = async (cloudOrders) => {
    if (!userCanAny(currentUserRef.current, ['pos', 'queue', 'kitchen', 'riders'])) return;
    const readStored = (key) => {
      try { return JSON.parse(localStorage.getItem(key) || '[]') || []; } catch { return []; }
    };
    const stranded = findStrandedOrders({
      localOrders: readStored(ORDERS_STORAGE_KEY),
      cloudOrders,
      pendingOrders: [...readStored('pending_sync'), ...pendingSyncRef.current],
    });
    if (!stranded.length) return;
    console.warn(`[CloudSync] Re-uploading ${stranded.length} order(s) that never reached the cloud`);
    let uploaded = 0;
    for (const order of stranded) {
      if (await syncOrderToCloud(order)) uploaded += 1;
    }
    if (uploaded) showToast(`${uploaded} unsynced order${uploaded === 1 ? '' : 's'} uploaded`, 'success');
  };
  const fetchResourceRecordsFromCloud = async (resource, nestedKeys, params = {}) => {
    const pageSize = CLOUD_FETCH_PAGE_SIZE;
    const maxPages = CLOUD_FETCH_MAX_PAGES;
    let offset = 0;
    let page = 1;
    let allRecords = [];

    for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
      const data = await getPosResource(resource, posAccessTokenRef.current, {
        ...params,
        limit: pageSize,
        pageSize,
        offset,
        page,
      });
      const pageRecords = getCollectionRecords(data, nestedKeys);

      if (!pageRecords.length) break;

      const merged = mergeFetchedRecords(allRecords, pageRecords);
      allRecords = merged.records;

      if (pageRecords.length < pageSize || merged.added === 0) break;

      offset += pageRecords.length;
      page += 1;
    }

    return allRecords;
  };

  // `prefetched` carries order rows the periodic bootstrap bundle already
  // delivered, so the tick maps them instead of making a second full-table round
  // trip. It MUST stay the second parameter: passing rows positionally as
  // `dateRangeOverride` spreads them into the query string as
  // `&0=[object Object]&1=…`, which is what broke sync between 2026-05-26 (the
  // revert that dropped this parameter) and now.
  const fetchFromCloud = async (dateRangeOverride = {}, prefetched) => {
    if (!navigator.onLine) return null;

    try {
      const records = prefetched !== undefined
        ? getCollectionRecords(prefetched, ['orders', 'rows', 'records', 'items'])
        : await fetchResourceRecordsFromCloud('orders', ['orders', 'rows', 'records', 'items'], dateRangeOverride);

      const mapped = records.map(record => {
        const rawBundles = record.bundles;
        const bundleList = Array.isArray(rawBundles) ? rawBundles : (rawBundles ? Object.values(rawBundles) : []);
        const backfilledItems = record.items ?? bundleList.flatMap((b) => {
          const bi = b?.items;
          if (!bi) return [];
          return Array.isArray(bi) ? bi : Object.values(bi);
        });
        const backfilledTotal = resolveCloudOrderTotal(record);
        return {
          id: getCloudOrderId(record) || getCloudOrderRowId(record),
          timestamp: record.timestamp,
          lastModified: record.last_modified || record.timestamp,
          items: backfilledItems,
          bundles: record.bundles || null,
          total: backfilledTotal,
        status: record.status || 'pending_payment',
        processedBy: record.processed_by || 'Unknown',
        userRole: record.user_role || 'unknown',
        orderType: normalizeOrderType(record.order_type),
        orderSource: record.order_source || null,
        orderSourceDetail: record.order_source_detail || null,
        customerName: record.customer_name || null,
        customerPhone: record.customer_phone || null,
        deliveryAddress: record.delivery_address || null,
        notes: stripDeliveryVerificationCodeFromNotes(record.notes || null),
        deliveryVerificationCode: getCloudOrderDeliveryVerificationCode(record) || null,
        paymentAmount: record.payment_amount != null ? Number(record.payment_amount) : null,
        balanceGiven: record.balance_given != null ? Number(record.balance_given) : null,
        paymentMethod: record.payment_method || null,
        splitPayments: record.split_payments || null,
        paidAt: record.paid_at || (ANALYTICS_REVENUE_STATUSES.includes(record.status) ? record.timestamp : null),
        readyAt: record.ready_at || null,
        completedAt: record.completed_at || null,
        version: record.version || 1,
        adjustments: record.adjustments || [],
        approval: record.approval || null,
        dueAmount: record.due_amount != null ? Number(record.due_amount) : null,
        refundDue: record.refund_due != null ? Number(record.refund_due) : null,
        packaging: record.packaging || 'rubber',
        supabase_id: record.id   // ← important: store Supabase row id
      };
    });

    const needsItemsBackfill = mapped.filter(o => o.items.length === 0 && o.bundles).length;
    const needsTotalBackfill = mapped.filter(o => o.total === 0 && o.paymentAmount && o.paymentAmount > 0).length;
    console.log(`[CloudSync] Fetched ${mapped.length} orders, ${needsItemsBackfill} need items backfill, ${needsTotalBackfill} need total backfill`);

    return mapped;
    } catch (e) {
      if (isAuthError(e)) { handleSessionExpired(); return null; }
      console.error('Supabase fetch error:', e);
      showToast('Failed to load orders from cloud', 'error');
      // null, not []: an empty list read as "the cloud has no orders" — login
      // then reported Synced and every unacknowledged local order looked stranded.
      return null;
    }
  };
  // Orders for the range Analytics or History is showing, as one paginated
  // stream (page size 1000).
  //   - A range inside what this session already downloaded sends `updatedSince`,
  //     so the backend returns only rows modified since then — typically 0-10
  //     rows on a quiet minute. The merge into local state stitches them in.
  //   - A range reaching back further is downloaded whole, and the covered
  //     history grows to include it.
  const fetchAnalyticsFromCloud = async (dateRangeOverride = {}) => {
    const range = { ...(dateRangeOverride || {}) };
    const updatedSince = planOrderRangeFetch({ range, coverage: orderCoverageRef.current });
    const fetchStartedAt = new Date().toISOString();
    const cloud = await fetchFromCloud(updatedSince ? { ...range, updatedSince } : range);
    if (Array.isArray(cloud)) {
      orderCoverageRef.current = nextOrderCoverage({
        range,
        coverage: orderCoverageRef.current,
        fetchStartedAt,
        incremental: Boolean(updatedSince),
      });
    }
    return cloud;
  };
  // The orders login and "Sync now" need: the last few days, in full. Used to be
  // the whole history, which on tablet Wi-Fi often timed out part-way and
  // showed "Failed to load orders from cloud" at sign-in.
  const fetchRecentOrdersFromCloud = async () => {
    const range = { startDate: loginOrderWindowStart() };
    const fetchStartedAt = new Date().toISOString();
    const cloud = await fetchFromCloud(range);
    if (Array.isArray(cloud)) {
      orderCoverageRef.current = nextOrderCoverage({ range, coverage: orderCoverageRef.current, fetchStartedAt });
    }
    return cloud;
  };
  const fetchRidersFromCloud = async (prefetched) => {
    if (!navigator.onLine) return [];

    try {
      if (prefetched !== undefined) return prefetched || [];
      return await getPosResource('riders', posAccessTokenRef.current) || [];
    } catch (e) {
      showToast(safeErrorMessage(e, 'Couldn’t load riders. Please try again.', { context: 'riders-load' }), 'error');
      return null;
    }
  };
  const fetchRiderPresenceFromCloud = async (prefetched) => {
    if (!navigator.onLine) return [];

    try {
      if (prefetched !== undefined) return prefetched || [];
      return await getPosResource('rider-presence', posAccessTokenRef.current) || [];
    } catch (e) {
      console.error('Rider presence fetch error:', e);
      return null;
    }
  };
  const fetchRiderAssignmentsFromCloud = async (prefetched) => {
    if (!navigator.onLine) return [];

    try {
      if (prefetched !== undefined) return prefetched || [];
      return await getPosResource('rider-assignments', posAccessTokenRef.current) || [];
    } catch (e) {
      console.error('Rider assignment fetch error:', e);
      return null;
    }
  };
  const refreshRiderData = async (bundle) => {
    // "Sync now" and coming back online call this without a bundle; for a login
    // without rider access that meant three 403s and a "permission" error toast.
    if (!userCanAny(currentUserRef.current, RIDER_READ_PERMISSIONS)) return;
    const [cloudRiders, cloudPresence, cloudAssignments] = await Promise.all([
      fetchRidersFromCloud(bundle?.riders),
      fetchRiderPresenceFromCloud(bundle?.riderPresence),
      fetchRiderAssignmentsFromCloud(bundle?.riderAssignments),
    ]);

    if (Array.isArray(cloudRiders)) setRiders(cloudRiders);
    if (Array.isArray(cloudPresence)) setRiderPresence(cloudPresence);
    if (Array.isArray(cloudAssignments)) setRiderAssignments(cloudAssignments);
  };
  const mergeOrders = (local, cloud) => {
    const merged = [];
    const indexByKey = new Map();

    const upsertOrder = (incoming) => {
      const keys = getOrderIdentityKeys(incoming);
      const existingIndex = keys.map(key => indexByKey.get(key)).find(index => index !== undefined);

      if (existingIndex === undefined) {
        const nextIndex = merged.push(incoming) - 1;
        keys.forEach(key => indexByKey.set(key, nextIndex));
        return;
      }

      const existing = merged[existingIndex];
      const incomingTime = new Date(incoming.lastModified || incoming.timestamp || 0).getTime();
      const existingTime = new Date(existing.lastModified || existing.timestamp || 0).getTime();
      const next = incomingTime >= existingTime
        ? {
            ...existing,
            ...incoming,
            id: existing.id || incoming.id,
            supabase_id: incoming.supabase_id || existing.supabase_id,
          }
        : existing;

      merged[existingIndex] = next;
      getOrderIdentityKeys(next).forEach(key => indexByKey.set(key, existingIndex));
    };

    local.forEach(upsertOrder);
    cloud.forEach(upsertOrder);

    return merged;
  };
  const scheduleRetry = () => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    const delay = nextRetryDelay(retryAttemptRef.current);
    retryAttemptRef.current += 1;
    retryTimeoutRef.current = setTimeout(() => {
      if (navigator.onLine) { syncPendingOrders(); syncPendingExpenses(); syncPendingInventory(); }
    }, delay);
  };
  const syncPendingOrders = async () => {
    // Read the queue from its ref mirror. The old approach — a no-op setState
    // updater followed by `await setTimeout(0)` — assumed React had run the
    // updater by the next macrotask. React 18 makes no such promise, so the
    // read could come back empty and the flush would silently do nothing,
    // stranding orders that were queued while offline.
    const currentPending = pendingSyncRef.current;
    if (!currentPending.length) return;
    const { held, toSend } = splitHeldUploads('orders', currentPending);
    if (!toSend.length) { setSyncStatusSafe('held'); return; }
    setSyncStatusSafe('syncing');
    const remaining = [...held];
    for (const order of toSend) { const ok = await syncOrderToCloud(order); if (!ok) remaining.push(order); }
    setPendingSync(remaining); saveToLocal({ pending: remaining });
    const failed = remaining.filter(order => !heldSyncKeysRef.current.has(heldSyncKey('orders', order))).length;
    if (!remaining.length) { setSyncStatusSafe('success'); showToast('All pending orders synced!', 'success'); }
    else if (failed) showToast(`${currentPending.length - remaining.length} orders synced, ${failed} failed`, 'warning');
  };
  const initialCloudSync = async () => {
    setSyncStatusSafe('syncing');
    // Sync orders
    const fetchStartedAt = new Date().toISOString();
    const cloud = await fetchRecentOrdersFromCloud();
    if (Array.isArray(cloud)) {
      setOrders(prev => {
        const merged = mergeOrders(prev, cloud);
        persistOrdersToStorage(merged);
        return merged;
      });
      // The first background tick then asks only for changes since this fetch,
      // instead of downloading the newest 500 orders again.
      periodicSyncSinceRef.current = resolveSyncWatermark({ tickStartIso: fetchStartedAt });
      analyticsHasResolvedOnceRef.current = true;
      setAnalyticsLoading(false);
      setLastSyncTime(new Date()); setSyncStatusSafe('success');
      await uploadStrandedOrders(cloud);
      // Owner-level reporting and the AI assistant read the whole history from
      // memory, so those logins still get it — in the background, after the
      // recent orders the screens need are already in.
      if (canViewFinancialReports(currentUserRef.current)) {
        fetchAnalyticsFromCloud({}).then((history) => {
          if (!Array.isArray(history) || !history.length) return;
          setOrders(prev => {
            const merged = mergeOrders(prev, history);
            persistOrdersToStorage(merged);
            return merged;
          });
        });
      }
    } else {
      // null means offline or an auth failure — record it rather than leaving the
      // status stuck on 'syncing'.
      setSyncStatusSafe(navigator.onLine ? 'error' : 'offline');
    }
    if (userCanAny(currentUserRef.current, RIDER_READ_PERMISSIONS)) {
      await refreshRiderData();
    }

    // INVENTORY STARTUP SEQUENCE
    // The SQL script (supabase_inventory_setup.sql) seeds correct data into Supabase.
    // On each new stock day (23:00 rollover), checkAndResetInventory resets stock to daily defaults.
    // Within the same cycle, it detects the reset already happened and skips — preserving live stock.
    //
    // Skipped entirely for logins without inventory rights (a kitchen display,
    // say). They used to run it anyway, collect a 403, and — because a failed
    // fetch is indistinguishable from an empty one downstream — fall back to
    // DEFAULT_INVENTORY, so the screen showed invented stock levels. A device
    // that cannot read stock should show no opinion about it rather than a
    // confident wrong one.
    if (!userCanAny(currentUserRef.current, INVENTORY_PERMISSIONS)) {
      console.log('[Inventory] Skipped — this login has no inventory permission.');
      await syncPendingInventory();
      return;
    }

    // Step 1: Run daily reset if this is the first open since the 11pm rollover
    const didReset = await checkAndResetInventory(currentUserRef.current?.fullName || 'System');
    console.log('[Inventory] Daily reset ran:', didReset);

    // Step 2: Fetch current inventory from Supabase (source of truth)
    const cloudInventory = await fetchInventoryFromCloud();
    console.log('[Inventory] Fetched from cloud:', cloudInventory?.length, 'items');

    if (cloudInventory?.length) {
      setInventory(cloudInventory);
      localStorage.setItem('inventory', JSON.stringify(cloudInventory));
    } else {
      // Supabase returned nothing — fall back to DEFAULT_INVENTORY so UI isn't empty.
      // This happens if the SQL setup script hasn't been run yet.
      console.warn('[Inventory] Cloud returned empty — using DEFAULT_INVENTORY as fallback. Run supabase_inventory_setup.sql to fix permanently.');
      const fallback = DEFAULT_INVENTORY.map(def => ({
        id: `inv_${def.id}`,
        ingredientId: def.id,
        itemName: def.name,
        currentStock: def.dailyDefault,
        lowStockThreshold: def.lowStockThreshold,
        dailyDefault: def.dailyDefault,
        unit: def.unit,
        costPerUnit: def.costPerUnit,
        supabase_id: null,
      }));
      setInventory(fallback);
      localStorage.setItem('inventory', JSON.stringify(fallback));
    }

    // Step 3: Flush any inventory changes queued while offline
    await syncPendingInventory();
  };
  // Expenses sync functions
const syncExpenseToCloud = async (expData) => {
  if (!navigator.onLine) {
    addToPendingSyncExpenses(expData);
    return false;
  }

  try {
    setSyncStatusSafe('syncing');

    const payload = {
      id: expData.id,
      timestamp: expData.timestamp,
      last_modified: expData.lastModified || new Date().toISOString(),
      category: expData.category,
      amount: Number(expData.amount),
      description: expData.description || '',
      by: expData.by || currentUserRef.current?.fullName || 'Unknown',
      status: expData.status || 'approved',
      by_role: expData.byRole || currentUserRef.current?.role || 'admin'
    };

      const result = await mutatePosResource('expenses', 'upsert', payload, posAccessTokenRef.current);
      const returnedId = result?.id;
    if (returnedId && !expData.supabase_id) {
      setExpenses(prev =>
        prev.map(e =>
          e.id === expData.id ? { ...e, supabase_id: returnedId } : e
        )
      );
    }

    releaseHeld('expenses', expData);
    setSyncStatusSafe('success');
    setLastSyncTime(new Date());
    return true;
  } catch (e) {
    // Queue before logging out, as orders do, so the next login uploads it.
    if (isAuthError(e)) { addToPendingSyncExpenses(expData); handleSessionExpired(); return false; }
    if (isRefusedForThisLogin(e)) {
      console.warn('Expense upload refused for this login; holding it on this tablet:', e);
      addToPendingSyncExpenses(expData);
      holdForAnotherLogin('expenses', expData);
      setSyncStatusSafe('held');
      return false;
    }
    console.error('Supabase expense sync error:', e);
    setSyncStatusSafe('error');
    addToPendingSyncExpenses(expData);
    scheduleRetry();
    showToast(safeErrorMessage(e, 'Couldn’t sync the expense. It’s saved and will retry.', { context: 'expense-sync' }), 'error');
    return false;
  }
};
  const deleteExpenseFromCloud = async (expId, supabase_id) => {
  if (!navigator.onLine || !supabase_id) return false;

  try {
    await mutatePosResource('expenses', 'delete', { id: expId, by: currentUserRef.current?.fullName || '' }, posAccessTokenRef.current);
    return true;
  } catch (e) {
    console.error('Supabase delete expense error:', e);
    showToast('Failed to delete expense from cloud', 'error');
    return false;
  }
};
  const addToPendingSyncExpenses = (expData) => {
    setPendingSyncExpenses(prev => {
      const newPending = [...prev.filter(p => p.id !== expData.id), { ...expData, syncTimestamp: Date.now() }];
      localStorage.setItem('pending_sync_expenses', JSON.stringify(newPending));
      return newPending;
    });
  };
const fetchExpensesFromCloud = async (userOverride, dateRangeOverride, prefetched) => {
  if (!navigator.onLine) return null;
  const effectiveUser = userOverride || currentUserRef.current;
  if (!effectiveUser) return null;
  // Nothing to show this login, and asking anyway got a 403 at every login and
  // "Sync now" ("Cloud expenses sync issue - working offline").
  if (!canReadExpenses(effectiveUser)) return [];
  // Use provided override (for periodic sync which has a stale closure) or fall back to current state
  const effectiveDateRange = dateRangeOverride !== undefined ? dateRangeOverride : expenseDateRange;

  try {
    // Same contract as fetchFromCloud: rows already in hand from the bootstrap
    // bundle skip the round trip entirely.
    const records = prefetched !== undefined
      ? getCollectionRecords(prefetched, ['expenses', 'rows', 'records', 'items'])
      : await fetchResourceRecordsFromCloud('expenses', ['expenses', 'rows', 'records', 'items'], {
        startDate: effectiveDateRange.startDate,
        endDate: effectiveDateRange.endDate,
      });

    return records.map(record => ({
      id: record.id,
      timestamp: record.timestamp,
      lastModified: record.last_modified || record.timestamp,
      category: record.category || '',
      amount: Number(record.amount || 0),
      description: record.description || '',
      by: record.by || 'Unknown',
      byRole: record.by_role || 'unknown',
      status: record.status || 'pending',
      supabase_id: record.id
    }));
  } catch (e) {
    console.error('Expenses fetch error (Supabase):', e);
    showToast('Cloud expenses sync issue - working offline', 'warning');
    return [];
  }
};
  const mergeExpenses = (local, cloud) => {
    const merged = [];
    const indexByKey = new Map();

    const upsertExpense = (incoming) => {
      const keys = getExpenseIdentityKeys(incoming);
      const existingIndex = keys.map((key) => indexByKey.get(key)).find((index) => index !== undefined);

      if (existingIndex === undefined) {
        const nextIndex = merged.push(incoming) - 1;
        keys.forEach((key) => indexByKey.set(key, nextIndex));
        return;
      }

      const existing = merged[existingIndex];
      const incomingTime = new Date(incoming.lastModified || incoming.timestamp || 0).getTime();
      const existingTime = new Date(existing.lastModified || existing.timestamp || 0).getTime();
      const next = incomingTime >= existingTime
        ? {
            ...existing,
            ...incoming,
            id: existing.id || incoming.id,
            supabase_id: incoming.supabase_id || existing.supabase_id,
          }
        : existing;

      merged[existingIndex] = next;
      getExpenseIdentityKeys(next).forEach((key) => indexByKey.set(key, existingIndex));
    };

    local.forEach(upsertExpense);
    cloud.forEach(upsertExpense);

    return dedupeExpenses(merged);
  };

  // ============================================
  // COMPLAINTS + MARKETING ACTIVITY LOGS
  // Both follow the expenses pattern: optimistic local write + localStorage
  // cache, cloud upsert through the generic POS resource API, and a full
  // refetch when the tab opens. Low volume, so no incremental sync.
  // ============================================

  const fetchComplaintsFromCloud = async () => {
    if (!navigator.onLine) return null;
    try {
      const records = await fetchResourceRecordsFromCloud('complaints', ['complaints', 'rows', 'records', 'items']);
      return records.map(record => ({
        id: record.id,
        orderId: record.order_id || null,
        issueType: record.issue_type || 'other',
        description: record.description || '',
        resolutionAction: record.resolution_action || null,
        status: record.status || 'open',
        resolutionDate: record.resolution_date || null,
        staffResponsible: record.staff_responsible || null,
        loggedBy: record.logged_by || null,
        timestamp: record.timestamp,
        lastModified: record.last_modified || record.timestamp,
      }));
    } catch (e) {
      if (isAuthError(e)) { handleSessionExpired(); return null; }
      console.error('Complaints fetch error:', e);
      showToast('Couldn’t load complaints from cloud — showing saved copy', 'warning');
      return null;
    }
  };

  const fetchStaffFromCloud = async () => {
    try {
      const records = await getPosResource('staff', posAccessTokenRef.current);
      const names = (Array.isArray(records) ? records : [])
        .map((entry) => String(entry.fullName || entry.username || '').trim())
        .filter(Boolean);
      if (names.length) setStaffMembers(Array.from(new Set(names)));
    } catch (e) {
      // Non-critical: the complaints tab falls back to recent order history.
      console.error('Staff list fetch error:', e);
    }
  };

  const refreshComplaints = async () => {
    setComplaintsLoading(true);
    fetchStaffFromCloud();
    try {
      const cloud = await fetchComplaintsFromCloud();
      if (cloud) {
        setComplaints(cloud);
        saveToLocal({ complaints: cloud });
      }
    } finally {
      setComplaintsLoading(false);
    }
  };

  const saveComplaint = async (complaint) => {
    const nowIso = new Date().toISOString();
    const record = { ...complaint, lastModified: nowIso };
    setComplaints(prev => {
      const next = [record, ...prev.filter(c => c.id !== record.id)]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      saveToLocal({ complaints: next });
      return next;
    });
    try {
      await mutatePosResource('complaints', 'upsert', {
        id: record.id,
        order_id: record.orderId || null,
        issue_type: record.issueType,
        description: record.description,
        resolution_action: record.resolutionAction || null,
        status: record.status || 'open',
        resolution_date: record.resolutionDate || null,
        staff_responsible: record.staffResponsible || null,
        logged_by: record.loggedBy || currentUserRef.current?.fullName || 'Unknown',
        timestamp: record.timestamp,
        last_modified: nowIso,
      }, posAccessTokenRef.current);
      return true;
    } catch (e) {
      if (isAuthError(e)) { handleSessionExpired(); return false; }
      console.error('Complaint sync error:', e);
      showToast(safeErrorMessage(e, 'Couldn’t sync the complaint. It’s saved on this device — use refresh to retry.', { context: 'complaint-sync' }), 'error');
      return false;
    }
  };

  const fetchMarketingFromCloud = async () => {
    if (!navigator.onLine) return null;
    try {
      const records = await fetchResourceRecordsFromCloud('marketing-activities', ['marketing-activities', 'rows', 'records', 'items']);
      return records.map(record => ({
        id: record.id,
        activityType: record.activity_type || 'other',
        channel: record.channel || '',
        title: record.title || '',
        description: record.description || '',
        cost: record.cost != null ? Number(record.cost) : null,
        occurredAt: record.occurred_at,
        createdBy: record.created_by || null,
        timestamp: record.timestamp,
        lastModified: record.last_modified || record.timestamp,
      }));
    } catch (e) {
      if (isAuthError(e)) { handleSessionExpired(); return null; }
      console.error('Marketing activities fetch error:', e);
      showToast('Couldn’t load marketing activities from cloud — showing saved copy', 'warning');
      return null;
    }
  };

  const refreshMarketingActivities = async () => {
    setMarketingLoading(true);
    try {
      const cloud = await fetchMarketingFromCloud();
      if (cloud) {
        setMarketingActivities(cloud);
        saveToLocal({ marketing: cloud });
      }
    } finally {
      setMarketingLoading(false);
    }
  };

  const saveMarketingActivity = async (activity) => {
    const nowIso = new Date().toISOString();
    const record = { ...activity, lastModified: nowIso };
    setMarketingActivities(prev => {
      const next = [record, ...prev.filter(a => a.id !== record.id)]
        .sort((a, b) => new Date(b.occurredAt) - new Date(a.occurredAt));
      saveToLocal({ marketing: next });
      return next;
    });
    try {
      await mutatePosResource('marketing-activities', 'upsert', {
        id: record.id,
        activity_type: record.activityType,
        channel: record.channel || null,
        title: record.title,
        description: record.description || null,
        cost: record.cost != null ? Number(record.cost) : null,
        occurred_at: record.occurredAt,
        created_by: record.createdBy || currentUserRef.current?.fullName || 'Unknown',
        timestamp: record.timestamp || nowIso,
        last_modified: nowIso,
      }, posAccessTokenRef.current);
      return true;
    } catch (e) {
      if (isAuthError(e)) { handleSessionExpired(); return false; }
      console.error('Marketing activity sync error:', e);
      showToast(safeErrorMessage(e, 'Couldn’t sync the marketing activity. It’s saved on this device — use refresh to retry.', { context: 'marketing-sync' }), 'error');
      return false;
    }
  };

  const deleteMarketingActivity = async (activityId) => {
    setMarketingActivities(prev => {
      const next = prev.filter(a => a.id !== activityId);
      saveToLocal({ marketing: next });
      return next;
    });
    try {
      await mutatePosResource('marketing-activities', 'delete', { id: activityId }, posAccessTokenRef.current);
      return true;
    } catch (e) {
      console.error('Marketing activity delete error:', e);
      showToast('Couldn’t delete the activity from cloud', 'error');
      return false;
    }
  };

  // Orders for a marketing impact window (±7 days around activities) can sit
  // outside the analytics range, so the tab fetches them directly.
  const fetchOrdersRange = async (startDate, endDate) => {
    const cloud = await fetchFromCloud({ startDate, endDate });
    return cloud || [];
  };

  // Refresh the on-demand logs whenever their tab is opened.
  useEffect(() => {
    if (!currentUser) return;
    if (currentTab === 'complaints') refreshComplaints();
    // Analytics needs the activity list too, for the campaign markers on the
    // revenue-by-day panel.
    if (currentTab === 'marketing' || (currentTab === 'analytics' && hasPermission('analytics'))) refreshMarketingActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, currentUser]);

  // ============================================
  // INVENTORY SYNC TO SUPABASE
  // ============================================
  
  const addToPendingSyncInventory = (invData) => {
    setPendingSyncInventory(prev => {
      const updated = [...prev.filter(p => p.id !== invData.id), { ...invData, syncTimestamp: Date.now() }];
      localStorage.setItem('pending_sync_inventory', JSON.stringify(updated));
      return updated;
    });
  };

  const syncInventoryToCloud = async (invData) => {
    if (!navigator.onLine) {
      addToPendingSyncInventory(invData);
      return false;
    }

    try {
      setSyncStatusSafe('syncing');

      // Always recover ingredientId from baseIngredients by name if missing
      let ingredientId = invData.ingredientId;
      if (!ingredientId && invData.itemName) {
        const match = baseIngredients.find(
          b => b.name.toLowerCase() === invData.itemName.toLowerCase()
        );
        if (match) ingredientId = match.id;
      }

      const payload = {
        ingredient_id: ingredientId || null,
        item_name: invData.itemName || 'Inventory item',
        current_stock: Number(invData.currentStock || 0),
        low_stock_threshold: Number(invData.lowStockThreshold || 0),
        daily_default: Number(invData.dailyDefault ?? 0),
        unit: invData.unit || 'piece',
        cost_per_unit: Number(invData.costPerUnit || 0),
        created_at: invData.createdAt || new Date().toISOString(),
        created_by: invData.createdBy || 'Unknown',
        last_modified: invData.lastModified || new Date().toISOString(),
        modified_by: invData.modifiedBy || currentUserRef.current?.fullName || 'Unknown'
      };

      const syncPayload = {
        ...payload,
        supabase_id: invData.supabase_id || null,
      };

      let result;
      try {
        result = await mutatePosResource('inventory', 'upsert', syncPayload, posAccessTokenRef.current);
      } catch (fullPayloadError) {
        if (!isRetryableSyncPayloadError(fullPayloadError)) throw fullPayloadError;

        const { created_at, created_by, modified_by, cost_per_unit, ...compatiblePayload } = syncPayload;
        console.warn('Inventory sync full payload failed; retrying with compatibility payload:', fullPayloadError);
        try {
          result = await mutatePosResource('inventory', 'upsert', compatiblePayload, posAccessTokenRef.current);
        } catch (compatiblePayloadError) {
          if (!isRetryableSyncPayloadError(compatiblePayloadError)) throw compatiblePayloadError;

          console.warn('Inventory sync compatibility payload failed; retrying with minimal payload:', compatiblePayloadError);
          result = await mutatePosResource('inventory', 'upsert', {
            ingredient_id: compatiblePayload.ingredient_id,
            item_name: compatiblePayload.item_name,
            current_stock: compatiblePayload.current_stock,
            low_stock_threshold: compatiblePayload.low_stock_threshold,
            unit: compatiblePayload.unit,
            last_modified: compatiblePayload.last_modified,
            supabase_id: compatiblePayload.supabase_id,
          }, posAccessTokenRef.current);
        }
      }

      const returnedId = result?.id;
      if (returnedId) {
        // Always store the Supabase UUID so future calls always take the update path
        setInventory(prev =>
          prev.map(i =>
            i.id === invData.id
              ? { ...i, supabase_id: returnedId, ingredientId: ingredientId || i.ingredientId }
              : i
          )
        );
        // Also persist the supabase_id to localStorage so it survives reload
        setInventory(prev => {
          localStorage.setItem('inventory', JSON.stringify(prev));
          return prev;
        });
      }

      releaseHeld('inventory', invData);
      setSyncStatusSafe('success');
      setLastSyncTime(new Date());
      return true;
    } catch (e) {
      if (isAuthError(e)) { addToPendingSyncInventory(invData); handleSessionExpired(); return false; }
      if (isRefusedForThisLogin(e)) {
        console.warn('Stock upload refused for this login; holding it on this tablet:', e);
        addToPendingSyncInventory(invData);
        holdForAnotherLogin('inventory', invData);
        setSyncStatusSafe('held');
        return false;
      }
      console.error('Supabase inventory sync error:', e);
      setSyncStatusSafe('error');
      addToPendingSyncInventory(invData);
      scheduleRetry();
      return false;
    }
  };

  const syncPendingInventory = async () => {
    const currentPending = pendingSyncInventoryRef.current;
    if (!currentPending.length) return;
    // Stock is changed by cashiers ('pos'); a supervisor or kitchen login on the
    // same tablet used to retry their queued changes and collect a 403 each time.
    const { held, toSend } = splitHeldUploads('inventory', currentPending);
    if (!toSend.length) { setSyncStatusSafe('held'); return; }
    setSyncStatusSafe('syncing');
    const remaining = [...held];
    for (const item of toSend) {
      const ok = await syncInventoryToCloud(item);
      if (!ok) remaining.push(item);
    }
    setPendingSyncInventory(remaining);
    localStorage.setItem('pending_sync_inventory', JSON.stringify(remaining));
    if (!remaining.length) showToast('Inventory synced!', 'success');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // DAILY INVENTORY RESET
  // ─────────────────────────────────────────────────────────────────────────

  // The stock day rolls over at 23:00 local time, not midnight: the kitchen
  // restocks after the day's trading closes, so the batch put out at 23:00
  // tonight is what trades tomorrow. Pushing the clock forward to that cycle's end labels
  // each cycle with the calendar day it serves, which is both what staff would
  // call it and a plain date string — what inventory-resets is keyed on.
  //
  // Labelling by cycle END (not start) also keeps the old midnight-based rows
  // meaningful: a reset recorded this morning under today's date still reads as
  // "this cycle is done", so switching over does not fire a surprise restock
  // mid-trade. The same holds for the move from 19:00 to 23:00: a cycle that
  // already restocked at 19:00 stays recorded, so the change never restocks
  // twice in one day.
  const cycleDateStr = () => {
    const cycleEnd = new Date();
    cycleEnd.setHours(cycleEnd.getHours() + (24 - INVENTORY_RESET_HOUR));
    return cycleEnd.toLocaleDateString('en-CA'); // YYYY-MM-DD
  };

  // writeDefaultsToCloud: fetch existing rows, then UPDATE or INSERT each base ingredient.
  // Does NOT rely on upsert/onConflict (which needs a unique constraint in Supabase).
  // This is the single source-of-truth writer for stock reset.
  const writeDefaultsToCloud = async (resetBy = 'System') => {
    if (!navigator.onLine) return false;
    try {
      const now = new Date().toISOString();
      // Fetch all existing inventory rows once
      const existing = await getPosResource('inventory', posAccessTokenRef.current);
      const existingRows = getCollectionRecords(existing, ['inventory', 'rows', 'records', 'items']);

      for (const def of DEFAULT_INVENTORY) {
        const payload = {
          ingredient_id: def.id,
          item_name: def.name,
          current_stock: def.dailyDefault,
          daily_default: def.dailyDefault,
          low_stock_threshold: def.lowStockThreshold,
          unit: def.unit,
          cost_per_unit: def.costPerUnit,
          last_modified: now,
          modified_by: resetBy,
        };
        // Match by ingredient_id first, then fall back to item_name match
        const match = existingRows.find(r =>
          r.ingredient_id === def.id ||
          (r.item_name || '').toLowerCase().trim() === def.name.toLowerCase().trim()
        );
        if (match) {
          try {
            await mutatePosResource('inventory', 'upsert', { ...payload, supabase_id: match.id }, posAccessTokenRef.current);
            console.log('Updated ' + def.name + ' ??? ' + def.dailyDefault);
          } catch (updateErr) {
            console.error('Update failed for ' + def.name + ':', updateErr);
          }
        } else {
          try {
            await mutatePosResource('inventory', 'upsert', { ...payload, created_at: now, created_by: resetBy }, posAccessTokenRef.current);
            console.log('Inserted ' + def.name + ' ??? ' + def.dailyDefault);
          } catch (insertErr) {
            console.error('Insert failed for ' + def.name + ':', insertErr);
          }
        }
      }
      return true;
    } catch (e) {
      console.error('writeDefaultsToCloud error:', e);
      return false;
    }
  };

  // Writes defaults to cloud once per stock cycle, then records the reset for
  // that cycle. Runs at login and again from the periodic tick, so a device left
  // open through 23:00 restocks without anyone reloading it.
  const checkAndResetInventory = async (resetByUser = 'System') => {
    if (!navigator.onLine) return false;
    try {
      const today = cycleDateStr();
      // Check if we already reset this cycle
      const resetRows = await getPosResource('inventory-resets', posAccessTokenRef.current, { date: today });
      const resetRecords = getCollectionRecords(resetRows, ['inventoryResets', 'inventory_resets', 'rows', 'records', 'items']);

      if (resetRecords.length > 0) {
        // Already reset this cycle — current values in Supabase are the live stock
        console.log('[Inventory] Already reset this cycle (' + today + ') — skipping.');
        resetCycleRef.current = today;
        return false;
      }

      // New cycle — write default stock to Supabase
      console.log('[Inventory] New stock cycle detected — resetting stock to daily defaults...');
      const ok = await writeDefaultsToCloud(resetByUser);
      if (!ok) {
        console.error('[Inventory] writeDefaultsToCloud failed.');
        return false;
      }
      resetCycleRef.current = today;

      // Record the reset so other devices skip it this cycle
      try {
        await mutatePosResource('inventory-resets', 'upsert', {
          reset_date: today,
          reset_by: resetByUser,
          reset_at: new Date().toISOString(),
        }, posAccessTokenRef.current);
      } catch (insertErr) {
        console.warn('[Inventory] Could not record reset:', insertErr.message);
      }

      showToast('📦 Stock reset to opening quantities', 'success', 4000);
      return true;
    } catch (e) {
      console.error('[Inventory] Daily reset error:', e);
      return false;
    }
  };

  // Admin can manually reset stock to opening quantities mid-day
  const manualInventoryReset = async () => {
    if (!window.confirm('Reset ALL stock to opening quantities? This will overwrite current stock for all devices.')) return;
    if (!navigator.onLine) { showToast('Cannot reset while offline', 'error'); return; }
    const today = cycleDateStr();
    try {
      const ok = await writeDefaultsToCloud(currentUser?.fullName || 'Admin');
      if (!ok) { showToast('Reset failed — check connection', 'error'); return; }
      resetCycleRef.current = today;
      // Overwrite this cycle's reset record
      await mutatePosResource('inventory-resets', 'upsert', {
        reset_date: today,
        reset_by: currentUser?.fullName || 'Admin',
        reset_at: new Date().toISOString(),
      }, posAccessTokenRef.current);
      // Refresh local state from cloud
      const fresh = await fetchInventoryFromCloud();
      setInventory(fresh);
      localStorage.setItem('inventory', JSON.stringify(fresh));
      showToast('✅ Stock reset to opening quantities', 'success');
    } catch (e) {
      console.error('Manual reset error:', e);
      showToast('Reset failed', 'error');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CLOUD-FIRST FETCH: Always trust Supabase as the single source of truth.
  // Deduplicates on ingredient_id (base items) or item_name (custom items).
  // ─────────────────────────────────────────────────────────────────────────
  const fetchInventoryFromCloud = async (prefetched) => {
    if (prefetched === undefined && !userCanAny(currentUserRef.current, INVENTORY_PERMISSIONS)) return [];
    try {
      const data = prefetched !== undefined
        ? prefetched
        : await getPosResource('inventory', posAccessTokenRef.current);
      const records = getCollectionRecords(data, ['inventory', 'rows', 'records', 'items']);
      if (!records.length) return [];

      // Deduplicate: keep the newest row per ingredient_id, then per item_name
      const byIngredientId = new Map();
      const byItemName = new Map();

      records.forEach(record => {
        const ingKey = record.ingredient_id;
        const nameKey = (record.item_name || '').toLowerCase().trim();
        if (ingKey) {
          if (!byIngredientId.has(ingKey)) byIngredientId.set(ingKey, record);
        } else if (nameKey) {
          if (!byItemName.has(nameKey)) byItemName.set(nameKey, record);
        }
      });

      const seen = new Set();
      const deduped = [];
      for (const r of byIngredientId.values()) { deduped.push(r); seen.add(r.id); }
      for (const r of byItemName.values()) { if (!seen.has(r.id)) deduped.push(r); }

      return deduped.map(record => {
        const def = DEFAULT_INVENTORY.find(d => d.id === record.ingredient_id);
        return {
          id: record.id,
          ingredientId: record.ingredient_id || null,
          itemName: record.item_name,
          currentStock: Number(record.current_stock ?? 0),
          lowStockThreshold: Number(record.low_stock_threshold ?? (def?.lowStockThreshold || 0)),
          dailyDefault: def ? def.dailyDefault : (record.daily_default != null ? Number(record.daily_default) : 0),
          unit: record.unit || 'piece',
          costPerUnit: Number(record.cost_per_unit || 0),
          createdAt: record.created_at,
          createdBy: record.created_by || 'Unknown',
          lastModified: record.last_modified,
          modifiedBy: record.modified_by,
          supabase_id: record.id,
        };
      });
    } catch (e) {
      console.error('Inventory fetch error:', e);
      return [];
    }
  };


  const mergeInventory = (local, cloud) => {
    // Simple merge: cloud always wins (Supabase is source of truth)
    const byName = new Map();
    local.forEach(i => byName.set((i.itemName || '').toLowerCase().trim(), i));
    cloud.forEach(ci => byName.set((ci.itemName || '').toLowerCase().trim(), ci));
    // Also ensure any DEFAULT_INVENTORY items not yet in cloud are kept from local
    DEFAULT_INVENTORY.forEach(def => {
      const key = def.name.toLowerCase().trim();
      if (!byName.has(key)) {
        byName.set(key, {
          id: `inv_${def.id}`, ingredientId: def.id, itemName: def.name,
          currentStock: def.dailyDefault, lowStockThreshold: def.lowStockThreshold,
          dailyDefault: def.dailyDefault, unit: def.unit, costPerUnit: def.costPerUnit,
        });
      }
    });

    return Array.from(byName.values());
  };
  
  const syncPendingExpenses = async () => {
    const currentPending = pendingSyncExpensesRef.current;
    if (!currentPending.length) return;
    const { held, toSend } = splitHeldUploads('expenses', currentPending);
    if (!toSend.length) { setSyncStatusSafe('held'); return; }
    setSyncStatusSafe('syncing');
    const remaining = [...held];
    for (const exp of toSend) { const ok = await syncExpenseToCloud(exp); if (!ok) remaining.push(exp); }
    setPendingSyncExpenses(remaining); saveToLocal({ pendingExpenses: remaining });
    const failed = remaining.filter(exp => !heldSyncKeysRef.current.has(heldSyncKey('expenses', exp))).length;
    if (!remaining.length) { setSyncStatusSafe('success'); showToast('All pending expenses synced!', 'success'); }
    else if (failed) showToast(`${currentPending.length - remaining.length} expenses synced, ${failed} failed`, 'warning');
  };
  const initialExpensesSync = async (user) => {
    setSyncStatusSafe('syncing');
    const cloud = await fetchExpensesFromCloud(user);
    if (cloud?.length) {
      setExpenses(prev => {
        const merged = mergeExpenses(prev, cloud);
        saveToLocal({ expenses: merged });
        return merged;
      });
      setLastSyncTime(new Date());
    }
    // Always land on a terminal status. An empty result is normal (no expenses in
    // range, or a role the backend won't serve expenses to) and used to leave the
    // status parked on 'syncing' — which kept the header spinner running and the
    // "Sync now" button disabled for the rest of the session.
    setSyncStatusSafe(cloud === null ? 'error' : 'success');
  };
  const startPeriodicSync = () => {
    if (syncIntervalRef.current) clearTimeout(syncIntervalRef.current);
    syncEmptyTicksRef.current = 0;
    // Every start claims a new generation. A tick already in flight when the user
    // logs out belongs to the old generation, so its finally-block reschedule is
    // dropped instead of leaving an orphan poller running with a dead token.
    syncGenerationRef.current += 1;
    const generation = syncGenerationRef.current;

    const runTick = async () => {
      // Default to the base interval for any skip/early-return path; only a
      // completed tick computes a (possibly backed-off) next delay.
      let nextDelay = SYNC_BASE_MS;
      let stop = false;
      // A dedicated in-flight flag, NOT syncStatus. syncStatus is a UI label that
      // several write paths leave parked on 'syncing' when they fail; gating the
      // poller on it meant one bad expense fetch at login could kill background
      // sync for the rest of the session (the device then only updated on a
      // manual refresh).
      if (syncTickInFlightRef.current) return;
      syncTickInFlightRef.current = true;
      try {
        // Skip while the tab is hidden — POS screens stay open 24/7, polling
        // overnight burns Vercel compute/transfer for nothing. wakeSync() runs a
        // tick the moment the screen comes back, so this costs no freshness.
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        if (!navigator.onLine) return;

        await syncPendingOrders();
        await syncPendingExpenses();
        await syncPendingInventory();

        // Stock rolls over at 23:00, by which time every POS screen has been
        // logged in for hours — the login-time check alone would never fire on
        // the device that matters. Re-check each tick and restock the moment the
        // boundary is crossed; checkAndResetInventory is idempotent across
        // devices, and the bootstrap fetch below picks the new levels straight up.
        if (resetCycleRef.current !== cycleDateStr()
          && userCanAny(currentUserRef.current, INVENTORY_PERMISSIONS)) {
          await checkAndResetInventory(currentUserRef.current?.fullName || 'System');
        }

        // One batched request (bootstrap) instead of 7 separate endpoint calls.
        // updatedSince makes idle ticks return only changed orders/inventory.
        const tickStart = new Date().toISOString();
        let bundle = null;
        try {
          bundle = await getPosResource('bootstrap', posAccessTokenRef.current, {
            startDate: expenseDateRangeRef.current?.startDate,
            endDate: expenseDateRangeRef.current?.endDate,
            updatedSince: periodicSyncSinceRef.current || undefined,
          });
        } catch (e) {
          if (isAuthError(e)) { stop = true; handleSessionExpired(); return; }
          console.error('Periodic sync error:', e);
          return;
        }
        if (!bundle) return;

        // `|| []` would be wrong here: a bundle that omits a collection entirely
        // (older backend deploy, or a role the route won't serve) must fall back
        // to a real fetch, not be read as "nothing changed". Only an actual array
        // counts as prefetched data.
        const prefetchedFrom = (key) => (Array.isArray(bundle[key]) ? bundle[key] : undefined);

        const cloudOrders = await fetchFromCloud(undefined, prefetchedFrom('orders'));
        if (cloudOrders) {
          // Use functional update so we always merge against the CURRENT orders state,
          // not the stale closure value captured when the interval was created.
          setOrders(prev => {
            const mergedOrders = mergeOrders(prev, cloudOrders);
            const hasChanges = mergedOrders.length !== prev.length ||
              mergedOrders.some(o => prev.find(x => x.id === o.id)?.lastModified !== o.lastModified);
            if (hasChanges) {
              persistOrdersToStorage(mergedOrders);
              return mergedOrders;
            }
            return prev;
          });
        }
        await refreshRiderData(bundle);
        const cloudExpenses = await fetchExpensesFromCloud(undefined, undefined, prefetchedFrom('expenses'));
        if (cloudExpenses) {
          setExpenses(prev => {
            const mergedExpenses = mergeExpenses(prev, cloudExpenses);
            const hasChanges = mergedExpenses.length !== prev.length ||
              mergedExpenses.some(me => {
                const local = prev.find(le => le.id === me.id);
                return !local || local.lastModified !== me.lastModified || local.status !== me.status;
              });
            if (hasChanges) {
              localStorage.setItem('expenses', JSON.stringify(mergedExpenses));
              return mergedExpenses;
            }
            return prev;
          });
        }
        const cloudInventory = await fetchInventoryFromCloud(prefetchedFrom('inventory'));
        if (cloudInventory?.length) {
          setInventory(prev => {
            const merged = mergeInventory(prev, cloudInventory);
            const hasChanges = merged.length !== prev.length ||
              merged.some(m => prev.find(p => p.id === m.id)?.currentStock !== m.currentStock);
            if (hasChanges) {
              localStorage.setItem('inventory', JSON.stringify(merged));
              return merged;
            }
            return prev;
          });
        }

        // An empty bundle means updatedSince returned no changed rows — widen the
        // gap before the next poll. Any changed row snaps back to the base rate.
        const changedRows = countBundleChanges(bundle);
        syncEmptyTicksRef.current = changedRows > 0 ? 0 : syncEmptyTicksRef.current + 1;
        nextDelay = nextSyncDelay(syncEmptyTicksRef.current);

        // Only advance the watermark now that the bundle's rows are merged in.
        // The bootstrap call is the single source of tick data, so reaching here
        // means every changed row was applied — and the slop covers clock skew
        // between this device and whichever one wrote the row.
        periodicSyncSinceRef.current = resolveSyncWatermark({ bundle, tickStartIso: tickStart })
          || periodicSyncSinceRef.current;
        setLastSyncTime(new Date()); setSyncStatusSafe('success');
      } finally {
        syncTickInFlightRef.current = false;
        if (!stop && generation === syncGenerationRef.current) {
          syncIntervalRef.current = setTimeout(runTick, nextDelay);
        }
      }
    };

    runPeriodicTickRef.current = runTick;
    syncIntervalRef.current = setTimeout(runTick, SYNC_BASE_MS);
  };

  // Tear the poller down for good (logout / expired session).
  const stopPeriodicSync = () => {
    syncGenerationRef.current += 1;
    runPeriodicTickRef.current = null;
    syncTickInFlightRef.current = false;
    if (syncIntervalRef.current) clearTimeout(syncIntervalRef.current);
    syncIntervalRef.current = null;
  };

  // Run a sync tick right now, at the base rate, instead of waiting out the
  // adaptive backoff. Used when the screen wakes, the network returns, or the
  // user opens a tab whose data must be current (Queue/Kitchen).
  const wakeSync = () => {
    if (!runPeriodicTickRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (!navigator.onLine || syncTickInFlightRef.current) return;
    // A wake means something probably changed while we were not looking, so drop
    // the idle backoff before running.
    syncEmptyTicksRef.current = 0;
    if (syncIntervalRef.current) clearTimeout(syncIntervalRef.current);
    runPeriodicTickRef.current();
  };

  // Latest-ref so the listener effect below registers once per session instead of
  // re-attaching on every render.
  const wakeSyncRef = useRef(() => {});
  useEffect(() => { wakeSyncRef.current = wakeSync; });

  // Resync the moment the screen comes back. Ticks are skipped while the tab is
  // hidden, and browsers throttle background timers hard — Chrome clamps hidden
  // tabs to roughly one timer per minute, and an Android PWA in the background
  // can be frozen outright. Without this, a kitchen tablet that sleeps shows
  // stale orders until someone manually refreshes.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const wake = () => wakeSyncRef.current();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') wake();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    window.addEventListener('pageshow', wake);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('pageshow', wake);
    };
  }, [isAuthenticated]);

  // Opening Queue or Kitchen is a statement that this screen's orders must be
  // current right now — don't make the user wait out the idle backoff.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (currentTab === 'queue' || currentTab === 'kitchen') wakeSyncRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, isAuthenticated]);

  // Live order sync. The backend bumps a content-free beacon row on every write
  // to orders; seeing it change, we immediately pull the real data down the
  // authenticated /api/pos path. This is what makes a payment approved on the
  // till appear on the kitchen screen in about a second instead of on the next
  // poll. Polling stays running underneath as the floor — if the websocket is
  // blocked or drops, the app degrades to exactly its previous behaviour.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return subscribeToOrderChanges(
      () => wakeSyncRef.current(),
      {
        onStatus: (status) => {
          realtimeStatusRef.current = status;
          if (status === 'SUBSCRIBED') console.log('[Realtime] live order sync connected');
          else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[Realtime] live order sync unavailable (${status}) — polling only`);
          }
        },
      },
    );
  }, [isAuthenticated]);

  // ---------------------------------------------------------------------------
  // New website order alarm
  // ---------------------------------------------------------------------------
  const {
    alarmingOrders,
    isRinging: orderAlarmRinging,
    rungOut: orderAlarmRungOut,
    acknowledge: acknowledgeOrderAlarm,
  } = useOrderAlarm({ orders, enabled: isAuthenticated });

  const handleViewAlarmedOrders = useCallback(() => {
    acknowledgeOrderAlarm();
    // Never drop someone onto a tab they have no permission for — the same
    // guard the ?tab= deep link uses.
    if (getAvailableTabs().some((tab) => tab.id === 'queue')) setCurrentTab('queue');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acknowledgeOrderAlarm]);

  // A push can land while this page is frozen or merely backgrounded. The
  // service worker cannot see the order list, so it only tells us to sync; the
  // alarm’s own deduplication then decides whether the order is genuinely new.
  // That keeps one source of truth for "have we already shouted about this?".
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    if (!('serviceWorker' in navigator)) return undefined;

    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type !== 'PUSH_RECEIVED') return;
      wakeSyncRef.current();
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
  }, [isAuthenticated]);

  const addToOrder = useCallback((category, itemName) => {
    setCurrentBundles(prev => prev.map(b => {
      if (b.id !== activeBundleId) return b;
      const key = `${category}_${itemName}`;
      const currentQuantity = b.items[key]?.quantity || 0;
      return { ...b, items: { ...b.items, [key]: { category, name: itemName, price: menuData[category].find(i => i.name === itemName)?.price || 0, quantity: currentQuantity + 1 } } };
    }));
  }, [activeBundleId]);
  const updateOrderQuantity = useCallback((bundleId, orderKey, newQuantity) => {
    setCurrentBundles(prev => prev.map(b => {
      if (b.id !== bundleId) return b;
      const updatedItems = { ...b.items };
      if (newQuantity <= 0) {
        delete updatedItems[orderKey];
      } else {
        updatedItems[orderKey] = { ...updatedItems[orderKey], quantity: newQuantity };
      }
      return { ...b, items: updatedItems };
    }));
  }, []);
  const addNewBundle = useCallback(() => {
    const newId = Date.now();
    setCurrentBundles(prev => [...prev, createEmptyBundle(newId, `Package ${prev.length + 1}`)]);
    setActiveBundleId(newId);
  }, []);
  const updateBundleName = useCallback((bundleId, newName) => {
    setCurrentBundles(prev => prev.map(b => b.id === bundleId ? { ...b, name: newName } : b));
  }, []);
  const updateBundlePackaging = useCallback((bundleId, packaging) => {
    setCurrentBundles(prev => prev.map(b => (
      b.id === bundleId ? { ...b, packaging } : b
    )));
  }, []);
  const removeBundle = useCallback((bundleId) => {
    setCurrentBundles(prev => {
      const updated = prev.filter(b => b.id !== bundleId);
      if (updated.length === 0) {
        const newId = Date.now();
        updated.push(createEmptyBundle(newId, 'Package 1'));
        setActiveBundleId(newId);
      } else if (activeBundleId === bundleId) {
        setActiveBundleId(updated[0]?.id);
      }
      return updated;
    });
  }, [activeBundleId]);
  const clearCurrentOrder = useCallback(() => {
    const newId = Date.now();
    setCurrentBundles([createEmptyBundle(newId, 'Package 1')]);
    setActiveBundleId(newId);
    setCustomerName(''); setCustomerPhone(''); setDeliveryAddress(''); setDeliveryFeeInput(''); setNotes(''); setPosPaymentMethod('');
    setSplitPayments([]);
    setDiscountPercent(''); setDiscountReason(''); setDiscountReasonDetail('');
  }, []);
  // Returns stock status for a single menu item given current inventory snapshot
  // Returns { available: bool, shortage: [{ingredientName, need, have}] }
  const getItemStockStatus = useCallback((itemName, quantity = 1, inventorySnapshot = inventory) => {
    const recipe = menuRecipes[itemName];
    if (!recipe) return { available: true, shortage: [] }; // No recipe = not tracked
    const shortage = [];
    recipe.forEach(({ ingredientId, quantity: recipeQty }) => {
      const needed = recipeQty * quantity;
      const invItem = inventorySnapshot.find(i => i.ingredientId === ingredientId);
      const have = invItem?.currentStock ?? 0;
      if (have < needed) {
        shortage.push({ ingredientName: invItem?.itemName || ingredientId, need: needed, have });
      }
    });
    return { available: shortage.length === 0, shortage };
  }, [inventory]);

  // Validates the full cart against current inventory.
  // Returns null if OK, or an array of problem descriptions if not.
  const validateStockForOrder = useCallback((bundles, inventorySnapshot = inventory) => {
    // Accumulate total ingredient demand across all bundles
    const totalDemand = {}; // { ingredientId: totalNeeded }
    bundles.forEach(bundle => {
      Object.values(bundle.items).forEach(orderItem => {
        if (!orderItem.quantity) return;
        const recipe = menuRecipes[orderItem.name];
        if (!recipe) return;
        recipe.forEach(({ ingredientId, quantity: recipeQty }) => {
          totalDemand[ingredientId] = (totalDemand[ingredientId] || 0) + recipeQty * orderItem.quantity;
        });
      });
    });
    const problems = [];
    Object.entries(totalDemand).forEach(([ingredientId, needed]) => {
      const invItem = inventorySnapshot.find(i => i.ingredientId === ingredientId);
      const have = invItem?.currentStock ?? 0;
      if (have < needed) {
        const name = invItem?.itemName || ingredientId;
        problems.push(`${name}: need ${needed}, only ${have} in stock`);
      }
    });
    return problems.length > 0 ? problems : null;
  }, [inventory]);

  const processOrder = async () => {
    const items = currentBundles.flatMap(b => Object.values(b.items).filter(i => i.quantity > 0));
    const deliveryFeeAmount = Number(deliveryFeeInput || 0);
    if (!items.length) return showToast('Please add items to your order', 'warning');
    if (!posPaymentMethod) return showToast('Select a payment method before placing the order', 'warning');
    if (!orderSource) return showToast('Select where this order came from (order source)', 'warning');
    if (orderSource === 'online' && !orderSourceDetail) return showToast('Select the online channel (WhatsApp, phone call, TikTok, ...)', 'warning');
    if (orderSource === 'other' && !orderSourceDetail.trim()) return showToast('Please specify the order source', 'warning');
    if (orderType === 'online') {
      if (!customerName.trim()) return showToast('Customer name is required for online orders', 'warning');
      if (!customerPhone.trim()) return showToast('Contact number is required for online orders', 'warning');
      if (!deliveryAddress.trim()) return showToast('Delivery address is required for online orders', 'warning');
    }
    if (orderType === 'online' && deliveryFeeAmount < 0) return showToast('Delivery fee cannot be negative.', 'warning');

    // Validate discount
    const requestedDiscountPercent = Number(discountPercent || 0);
    if (requestedDiscountPercent > 0) {
      if (requestedDiscountPercent > maxDiscountPercent) {
        return showToast(
          canApplyDiscountDirectly
            ? `Discount cannot exceed ${MAX_DISCOUNT_PERCENT}%`
            : `Staff discounts are capped at ${STAFF_MAX_DISCOUNT_PERCENT}%. Ask an admin to apply a larger discount.`,
          'warning'
        );
      }
      if (!discountReason) return showToast('Select a reason for the discount', 'warning');
      if (discountReason === 'other' && !discountReasonDetail.trim()) {
        return showToast('Please specify the discount reason', 'warning');
      }
    }

    // Validate split payment
    if (posPaymentMethod === 'split') {
      if (splitPayments.length < 2) {
        return showToast('Split payment requires at least 2 payment methods', 'warning');
      }
      const totalSplitAmount = splitPayments.reduce((sum, p) => sum + Number(p.amount), 0);
      const orderTotal = calculateTotal + packagingCost + (orderType === 'online' ? deliveryFeeAmount : 0) - appliedDiscountAmount;
      if (Math.abs(totalSplitAmount - orderTotal) > 0.01) {
        return showToast(`Split payment total (${formatCedi(totalSplitAmount)}) must equal order total (${formatCedi(orderTotal)})`, 'warning');
      }
    }
    
    // Validate stock availability before confirming
    const stockProblems = validateStockForOrder(currentBundles);
    if (stockProblems) {
      return showToast(
        `Cannot place order — insufficient stock: ${stockProblems.join('; ')}`,
        'error',
        6000
      );
    }

    setShowOrderConfirm(true);
  };
  const confirmPlaceOrder = async () => {
    setShowOrderConfirm(false);
    // appliedDiscountAmount is 0 for a staff request: the order is created at
    // full price and only drops once an admin approves.
    const total = calculateTotal + packagingCost + (orderType === 'online' ? Number(deliveryFeeInput || 0) : 0) - appliedDiscountAmount;
    await completeOrder(total);
  };
  const completeOrder = async (total) => {
    const items = currentBundles.flatMap(b => Object.values(b.items).filter(i => i.quantity > 0));
    const orderPackaging = currentBundles.some(bundle => bundleHasItems(bundle) && bundle.packaging === 'paper') ? 'paper' : 'rubber';
    setIsProcessing(true);
    try {
      await new Promise(r => setTimeout(r, 1200));
      const now = new Date().toISOString();
      const order = {
        id: Date.now().toString(),
        timestamp: now,
        lastModified: now,
        items,
        bundles: currentBundles.map(b => ({ ...b, items: Object.values(b.items).filter(i => i.quantity > 0) })),
        total,
        status: 'pending_payment',
        processedBy: currentUser.fullName,
        processedById: currentUser.username,
        orderType,
        orderSource,
        orderSourceDetail: orderSourceDetail.trim() || null,
        paymentMethod: posPaymentMethod,
        splitPayments: posPaymentMethod === 'split' ? splitPayments : null,
        version: 1,
        adjustments: [],
        approval: null,
        dueAmount: null,
        refundDue: null,
        packaging: orderPackaging
      };
      // Customer details are required for online orders and optional for
      // in-person ones — attach them whenever the cashier filled them in.
      if (customerName.trim()) order.customerName = customerName.trim();
      if (customerPhone.trim()) order.customerPhone = customerPhone.trim();
      // Discount: an admin's decision is final and lands straight in the
      // adjustments ledger. Anyone else raises a pending approval instead — the
      // order stays at full price and the Payment Queue keeps it locked until
      // an admin decides.
      const discountPercentValue = Number(discountPercent || 0);
      if (discountPercentValue > 0) {
        const trimmedDetail = discountReasonDetail.trim();
        if (canApplyDiscountDirectly) {
          order.adjustments = [
            ...order.adjustments,
            {
              type: 'discount',
              percent: discountPercentValue,
              base: 'items',
              amount: calcDiscountAmount(calculateTotal, discountPercentValue),
              reason: discountReason,
              reasonDetail: trimmedDetail || null,
              appliedBy: currentUser.fullName,
              appliedById: currentUser.username,
              approvedBy: currentUser.fullName,
              approvedById: currentUser.username,
              timestamp: now,
            },
          ];
        } else {
          order.approval = {
            status: 'pending',
            type: 'discount',
            payload: {
              percent: discountPercentValue,
              reason: discountReason,
              reasonDetail: trimmedDetail || null,
              estimatedAmount: calcDiscountAmount(calculateTotal, discountPercentValue),
            },
            requestedBy: currentUser.fullName,
            requestedById: currentUser.username,
            requestedAt: now,
          };
        }
      }
      if (order.orderType === 'online') {
        const deliveryFeeAmount = Number(deliveryFeeInput || 0);
        order.deliveryVerificationCode = createDeliveryVerificationCode();
        if (deliveryAddress) order.deliveryAddress = deliveryAddress;
        if (notes) order.notes = stripDeliveryVerificationCodeFromNotes(notes);
        order.adjustments = [
          ...order.adjustments,
          {
            type: 'delivery_fee',
            amount: Number(deliveryFeeAmount.toFixed(2)),
            source: 'pos_manual',
            timestamp: now,
          },
        ];
      }
      
      // Deduct inventory for this order (pass order ID for tracking)
      await deductInventoryForOrder(items, order.id);
      
      // Use functional update to avoid stale closure on `orders`
      setOrders(prev => {
        const newOrders = [order, ...prev];
        saveToLocal({ orders: newOrders });
        return newOrders;
      });
      const ok = await syncOrderToCloud(order);
      const newId = Date.now();
      setCurrentBundles([createEmptyBundle(newId, 'Package 1')]);
      setActiveBundleId(newId);
      setCustomerName(''); setCustomerPhone(''); setDeliveryAddress(''); setDeliveryFeeInput(''); setNotes(''); setPosPaymentMethod(''); setSplitPayments([]); setOrderSource(''); setOrderSourceDetail(''); setCelebrateOrder(true);
      setDiscountPercent(''); setDiscountReason(''); setDiscountReasonDetail('');
      const syncLabel = ok ? ' | Synced' : ' | Saved locally';
      showToast(
        hasPendingDiscountRequest(order)
          ? `Order placed at full price — ${discountPercentValue}% discount sent for admin approval. Payment is locked until it's decided.${syncLabel}`
          : `Order placed in queue! Total: ${formatCedi(total)}${syncLabel}`,
        'success',
        hasPendingDiscountRequest(order) ? 5000 : 2500
      );
      setTimeout(() => setCelebrateOrder(false), 1800);
    } catch (e) {
      showToast('Error processing order', 'error'); console.error(e);
    } finally { setIsProcessing(false); }
  };
  // Status + timestamps
  const updateOrderStatus = (orderId, newStatus, additionalData = {}) => {
    // The cloud write and the localStorage write used to happen INSIDE the
    // setOrders updater. React may invoke an updater more than once for a single
    // update, which turned one status change into duplicate upserts. Compute the
    // next order in the updater (pure), then do the side effects once, after.
    let changedOrder = null;
    setOrders(prev => {
      const nowIso = new Date().toISOString();
      const updated = prev.map(o => {
        if (o.id !== orderId) return o;
        const extra = {};
        if (newStatus === 'paid' && !o.paidAt) extra.paidAt = nowIso;
        if (newStatus === 'ready' && !o.readyAt) extra.readyAt = nowIso;
        if ((newStatus === 'picked_up' || newStatus === 'delivered') && !o.completedAt) extra.completedAt = nowIso;
        const next = { ...o, status: newStatus, lastModified: nowIso, ...extra, ...additionalData };
        changedOrder = next;
        return next;
      });
      nextOrdersToPersistRef.current = updated;
      return updated;
    });

    if (nextOrdersToPersistRef.current) {
      saveToLocal({ orders: nextOrdersToPersistRef.current });
      nextOrdersToPersistRef.current = null;
    }
    if (changedOrder) {
      // A local status change means this device's view of the world just moved —
      // poll at the base rate again rather than staying in idle backoff.
      syncEmptyTicksRef.current = 0;
      syncOrderToCloud(changedOrder);
    }
  };
  // Payment confirm
  const handlePaymentConfirm = (orderId, orderType_) => {
    if (payingOrdersRef.current.has(orderId)) return; // already confirming this order
    const order = orders.find(o => o.id === orderId); if (!order) return;
    // Re-check the lock here as well as on the button: the till must never
    // settle an order whose discount an admin hasn't ruled on yet.
    if (hasPendingDiscountRequest(order)) {
      return showToast('This order has a discount awaiting admin approval. Payment is locked until it is approved or denied.', 'warning', 4000);
    }
    const method = order.paymentMethod; if (!method) return showToast('Missing payment method on order. Please set method at POS.', 'warning');

    // Validate first (these return early WITHOUT locking the order), then commit once.
    let updates;
    if (orderType_ === 'in-person' && method === 'cash') {
      const amtStr = paymentInputs[order.id] || ''; const payment = parseFloat(amtStr || '0') || 0;
      if (payment < order.total) return showToast('Payment amount is insufficient', 'warning');
      updates = { paymentAmount: payment, balanceGiven: payment - order.total };
    } else {
      updates = { paymentAmount: order.total, balanceGiven: 0 };
    }

    payingOrdersRef.current.add(orderId);
    updateOrderStatus(orderId, 'paid', updates);
    setPaymentInputs(prev => { const clone = { ...prev }; delete clone[orderId]; return clone; });
    showToast('Payment confirmed! Order moved to kitchen.', 'success');
    // Release after the order has left the queue so a stuck lock can't persist.
    setTimeout(() => payingOrdersRef.current.delete(orderId), 2000);
};
  const forceSync = async () => {
    if (!navigator.onLine) return showToast('Cannot sync while offline', 'warning');
    setSyncStatusSafe('syncing');
    await syncPendingOrders();
    const cloud = await fetchRecentOrdersFromCloud();
    if (cloud) {
      setOrders(prev => {
        const merged = mergeOrders(prev, cloud);
        saveToLocal({ orders: merged });
        return merged;
      });
    }
    if (cloud) await uploadStrandedOrders(cloud);
    await refreshRiderData();
    await syncPendingExpenses();
    const cloudExpenses = await fetchExpensesFromCloud();
    if (cloudExpenses) {
      setExpenses(prev => {
        const merged = mergeExpenses(prev, cloudExpenses);
        saveToLocal({ expenses: merged });
        return merged;
      });
    }

    // Sync inventory
    await syncPendingInventory();
    const cloudInventory = await fetchInventoryFromCloud();
    if (cloudInventory && cloudInventory.length > 0) {
      setInventory(prev => {
        const merged = mergeInventory(prev, cloudInventory);
        saveToLocal({ inventory: merged });
        return merged;
      });
    }

    await new Promise(r => setTimeout(r, 800));
    setSyncStatusSafe('success'); setLastSyncTime(new Date()); showToast(`Manual sync completed!`, 'success');
  };
  const resetSales = async () => {
    if (!currentUser || currentUser.role !== 'admin') return showToast('Only administrators can reset sales data', 'error');
    const ok = window.confirm('WARNING: This will permanently delete all order history.\n\nThis action cannot be undone!\n\nClick OK to proceed.');
    if (!ok) return;
    try {
      setOrders([]); setPendingSync([]);
      localStorage.removeItem('restaurant_orders'); localStorage.removeItem('pending_sync'); localStorage.removeItem('last_sync_time');
      const newId = Date.now();
      setCurrentBundles([createEmptyBundle(newId, 'Package 1')]);
      setActiveBundleId(newId);
      setSyncStatusSafe('idle'); setLastSyncTime(null);
      showToast('Sales data reset successfully!', 'success');
    } catch { showToast('Error resetting data', 'error'); }
  };
  const showEndOfDaySummary = () => {
    const todayStr = new Date().toDateString();
    const todayOrders = orders.filter(o => isAnalyticsRevenueOrder(o) && new Date(getAnalyticsTimestamp(o)).toDateString() === todayStr);
    const todayRevenue = todayOrders.reduce((sum, order) => sum + getOrderRevenueAmount(order), 0);
    const todayExpenses = expenseLedger
      .filter((expense) => expense.status === 'approved' && new Date(expense.timestamp).toDateString() === todayStr)
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    const todayProfit = todayRevenue - todayExpenses;
    let summary = `End of Day Summary - ${todayStr}:\nOrders: ${todayOrders.length}\nRevenue: ${formatCedi(todayRevenue)}`;
    // Expenses and profit are owner-level figures.
    if (canViewFinancialReports(currentUser)) {
      summary += `\nOther Expenses: ${formatCedi(todayExpenses)}\nProfit: ${formatCedi(todayProfit)}`;
    }
    showToast(summary, 'info', 15000);
  };
  const analyticsFetchDateRange = useMemo(() => {
    const nowDate = new Date();
    const today = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());
    let start = null;
    let end = null;

    if (dateFilter === 'all') return {};

    if (dateFilter === 'custom') {
      if (customDate) return { startDate: customDate, endDate: customDate };
      if (customDateRange.start && customDateRange.end) {
        return { startDate: customDateRange.start, endDate: customDateRange.end };
      }
      return null;
    }

    switch (dateFilter) {
      case 'today':
        start = today;
        end = today;
        break;
      case 'yesterday':
        start = new Date(today.getTime() - DAY_IN_MS);
        end = start;
        break;
      case 'last7days':
        start = new Date(today.getTime() - (6 * DAY_IN_MS));
        end = today;
        break;
      case 'week':
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        end = today;
        break;
      case 'last30days':
        start = new Date(today.getTime() - (29 * DAY_IN_MS));
        end = today;
        break;
      case 'month':
        start = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);
        end = today;
        break;
      case 'quarter': {
        const quarter = Math.floor(nowDate.getMonth() / 3);
        start = new Date(nowDate.getFullYear(), quarter * 3, 1);
        end = today;
        break;
      }
      case 'year':
        start = new Date(nowDate.getFullYear(), 0, 1);
        end = today;
        break;
      default:
        return {};
    }

    return { startDate: formatBackendDate(start), endDate: formatBackendDate(end) };
  }, [dateFilter, customDate, customDateRange]);
  // Date filter helper
  const getDateFilterCondition = useMemo(() => createDateRangeMatcher({
    dateFilter,
    customDate,
    customDateRange,
  }), [dateFilter, customDate, customDateRange]);
  useEffect(() => {
    // History shares the date filter. It used to rely on login downloading the
    // whole order history; login now fetches only recent days.
    if (!isOnline || !currentUser || !['analytics', 'history'].includes(currentTab) || analyticsFetchDateRange === null) return;

    // Skip if we already fetched the exact same range very recently. Filter clicks,
    // tab re-entries, and prop churn during render all fire this effect — without
    // this guard each one would trigger another paginated round trip.
    const rangeKey = JSON.stringify(analyticsFetchDateRange);
    const now = Date.now();
    if (
      lastAnalyticsFetchRef.current.rangeKey === rangeKey
      && now - lastAnalyticsFetchRef.current.when < ANALYTICS_REFRESH_DEDUPE_MS
    ) {
      return;
    }
    lastAnalyticsFetchRef.current = { rangeKey, when: now };

    let cancelled = false;
    let showSkeletonTimer = null;

    // First fetch ever for this session → skeleton already on screen (initial
    // state is true), nothing to do. Subsequent filter clicks → debounce
    // 250ms before flipping it back on, so quick incremental round-trips
    // don't flash the placeholder.
    if (analyticsHasResolvedOnceRef.current) {
      showSkeletonTimer = setTimeout(() => {
        if (!cancelled) setAnalyticsLoading(true);
      }, 250);
    }

    fetchAnalyticsFromCloud(analyticsFetchDateRange).then((cloud) => {
      if (showSkeletonTimer) clearTimeout(showSkeletonTimer);
      if (cancelled) return;
      analyticsHasResolvedOnceRef.current = true;
      setAnalyticsLoading(false);
      if (!Array.isArray(cloud) || cloud.length === 0) return;
      setOrders(prev => {
        const merged = mergeOrders(prev, cloud);
        saveToLocal({ orders: merged });
        return merged;
      });
    });

    return () => {
      cancelled = true;
      if (showSkeletonTimer) clearTimeout(showSkeletonTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsFetchDateRange, currentTab, currentUser, isOnline]);
  // Safety nets:
  //   - If offline and we have no chance of a fresh fetch, drop the skeleton
  //     immediately so the user at least sees whatever the local cache holds.
  //   - If online but the fetch never resolves (slow / dead network), drop it
  //     after 10s so we don't strand the user staring at placeholders.
  useEffect(() => {
    if (!analyticsLoading) return undefined;
    if (!isOnline) {
      setAnalyticsLoading(false);
      return undefined;
    }
    const timer = setTimeout(() => setAnalyticsLoading(false), 10000);
    return () => clearTimeout(timer);
  }, [analyticsLoading, isOnline]);
  // View helpers
  const getOrdersFor = (status) => {
    const filtered = orders.filter(o => o.status === status && getDateFilterCondition(o.timestamp));
    const sortKey = status === 'ready' ? 'readyAt' : status === 'paid' ? 'paidAt' : status === 'pending_payment' ? 'timestamp' : 'timestamp';
    const asc = ['pending_payment', 'paid', 'ready'].includes(status);
    return [...filtered].sort((a, b) => {
      const ta = new Date(a[sortKey] || a.timestamp).getTime();
      const tb = new Date(b[sortKey] || b.timestamp).getTime();
      return asc ? ta - tb : tb - ta;
    });
  };
  // Returns orders that are active (pending_payment / paid / ready) but fall OUTSIDE the
  // current date filter — so staff can be warned about stale/old open orders.
  const getOutOfRangeActiveOrders = () => {
    const activeStatuses = ['pending_payment', 'paid', 'ready'];
    return orders.filter(o => activeStatuses.includes(o.status) && !getDateFilterCondition(o.timestamp));
  };
  const analyticsSnapshot = useMemo(() => buildAnalyticsSnapshot({
    orders,
    expenses,
    matchesRange: getDateFilterCondition,
  }), [orders, expenses, getDateFilterCondition]);
  const {
    reportOrders: filteredAnalyticsOrders,
    revenueOrders: settledAnalyticsOrders,
    analyticsData,
    salesByUser,
    revenueByMethod,
    revenueBySource,
    topItems,
    statusDistribution,
    totalRefunds,
    totalDue,
    dailyTrend,
    hourlyTrend,
    prepTimeStats,
    fulfillmentTimeStats,
  } = analyticsSnapshot;
  const todaysOrders = analyticsData.todaysOrders;
  const totalRevenue = analyticsData.totalRevenue;
  const avgOrderValue = analyticsData.avgOrderValue;
  const expenseLedger = useMemo(() => dedupeExpenses(expenses), [expenses]);
  // Approvals data (admin tab)
  // The Approvals tab renders amendments and discount requests very differently
  // (one diffs items, the other shows a percentage and a reason), so split them
  // here rather than making one table handle both shapes.
  const pendingApprovals = useMemo(
    () => orders.filter(o => o.approval && o.approval.status === 'pending' && o.approval.type !== 'discount'),
    [orders]
  );
  const pendingDiscountApprovals = useMemo(() => orders.filter(hasPendingDiscountRequest), [orders]);
  const pendingExpenseApprovals = useMemo(
    () => expenseLedger.filter((expense) => expense.status === 'pending'),
    [expenseLedger]
  );
  // History sorted newest
  const completedForRangeHistory = useMemo(() => orders.filter(o => (o.status === 'picked_up' || o.status === 'delivered') && getDateFilterCondition(o.timestamp)), [orders, getDateFilterCondition]);
  const historySorted = useMemo(() => [...completedForRangeHistory].sort((a, b) => {
    const ta = new Date(a.completedAt || a.timestamp).getTime();
    const tb = new Date(b.completedAt || b.timestamp).getTime();
    return tb - ta;
  }), [completedForRangeHistory]);
  const ridersById = useMemo(() => new Map(riders.map((rider) => [rider.id, rider])), [riders]);
  const riderPresenceById = useMemo(() => new Map(riderPresence.map((entry) => [entry.rider_id, entry])), [riderPresence]);
  const riderAssignmentsByOrder = useMemo(() => {
    const latestAssignments = new Map();

    riderAssignments.forEach((assignment) => {
      const current = latestAssignments.get(assignment.order_id);
      const nextAssignedAt = new Date(assignment.assigned_at || 0).getTime();
      const currentAssignedAt = current ? new Date(current.assigned_at || 0).getTime() : -1;

      if (!current || nextAssignedAt >= currentAssignedAt) {
        latestAssignments.set(assignment.order_id, assignment);
      }
    });

    return latestAssignments;
  }, [riderAssignments]);
  const getOrderDeliveryFee = useCallback((order) => Number(getOrderDeliveryFeeInfo(order)?.amount || 0), []);
  const getOrderAssignedRider = useCallback((order) => {
    if (!order) return null;

    const assignment = riderAssignmentsByOrder.get(order.id);
    if (assignment) {
      const rider = ridersById.get(assignment.rider_id);
      const presence = riderPresenceById.get(assignment.rider_id);

      return {
        assignmentId: assignment.id,
        riderId: assignment.rider_id,
        riderName: rider?.name || assignment.rider_name || 'Assigned rider',
        riderPhone: rider?.phone || '',
        status: assignment.status || 'assigned',
        assignedAt: assignment.assigned_at || null,
        pickedUpAt: assignment.picked_up_at || null,
        deliveredAt: assignment.delivered_at || null,
        note: getOrderRiderAssignmentAdjustment(order)?.note || '',
        presenceStatus: presence?.availability_status || 'offline',
      };
    }

    const fallback = getOrderRiderAssignmentAdjustment(order);
    if (!fallback) return null;

    return {
      assignmentId: null,
      riderId: fallback.riderId || null,
      riderName: fallback.riderName || 'Assigned rider',
      riderPhone: fallback.riderPhone || '',
      status: fallback.status || null,
      assignedAt: fallback.timestamp || null,
      pickedUpAt: null,
      deliveredAt: null,
      note: fallback.note || '',
      presenceStatus: fallback.presenceStatus || 'offline',
    };
  }, [riderAssignmentsByOrder, riderPresenceById, ridersById]);
  const deliveredForRiderRange = useMemo(
    () => orders.filter(o => o.status === 'delivered' && getDateFilterCondition(o.completedAt || o.timestamp)),
    [orders, getDateFilterCondition]
  );
  const riderDeliveryOrders = useMemo(
    () => deliveredForRiderRange.filter(o => o.orderType === 'online'),
    [deliveredForRiderRange]
  );
  const riderSummary = useMemo(() => {
    const map = new Map();

    riderDeliveryOrders.forEach(order => {
      const riderInfo = getOrderAssignedRider(order);
      const riderName = riderInfo?.riderName?.trim() || 'Unassigned';
      const riderPhone = riderInfo?.riderPhone || '';
      const key = riderName.toLowerCase();
      const payout = getOrderDeliveryFee(order);
      const completedAt = order.completedAt || order.timestamp;

      if (!map.has(key)) {
        map.set(key, {
          riderName,
          riderPhone,
          deliveries: 0,
          totalPayout: 0,
          totalOrderValue: 0,
          lastDeliveryAt: completedAt,
          orders: [],
        });
      }

      const entry = map.get(key);
      entry.deliveries += 1;
      entry.totalPayout += payout;
      entry.totalOrderValue += Number(order.total || 0);
      entry.lastDeliveryAt = new Date(completedAt).getTime() > new Date(entry.lastDeliveryAt).getTime() ? completedAt : entry.lastDeliveryAt;
      if (!entry.riderPhone && riderPhone) entry.riderPhone = riderPhone;
      entry.orders.push({
        ...order,
        riderInfo: riderInfo || null,
      });
    });

    return Array.from(map.values())
      .map(entry => ({
        ...entry,
        averagePayout: entry.deliveries > 0 ? entry.totalPayout / entry.deliveries : 0,
      }))
      .sort((a, b) => {
        if (b.totalPayout !== a.totalPayout) return b.totalPayout - a.totalPayout;
        return b.deliveries - a.deliveries;
      });
  }, [getOrderAssignedRider, getOrderDeliveryFee, riderDeliveryOrders]);
  const riderSummaryTotals = useMemo(() => riderSummary.reduce((totals, rider) => ({
    riders: totals.riders + 1,
    deliveries: totals.deliveries + rider.deliveries,
    totalPayout: totals.totalPayout + rider.totalPayout,
    unassignedDeliveries: totals.unassignedDeliveries + (rider.riderName === 'Unassigned' ? rider.deliveries : 0),
  }), {
    riders: 0,
    deliveries: 0,
    totalPayout: 0,
    unassignedDeliveries: 0,
  }), [riderSummary]);
  // Filtered expenses
  const filteredExpenses = useMemo(() => {
    const lowerSearch = expenseSearch.toLowerCase();

    return expenseLedger
      .filter((expense) =>
        getDateFilterCondition(expense.timestamp) &&
        (
          expense.category.toLowerCase().includes(lowerSearch) ||
          expense.description.toLowerCase().includes(lowerSearch) ||
          expense.by.toLowerCase().includes(lowerSearch)
        )
      )
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [expenseLedger, expenseSearch, getDateFilterCondition]);
  const approvedFilteredExpenses = useMemo(
    () => filteredExpenses.filter((expense) => expense.status === 'approved'),
    [filteredExpenses]
  );
  const pendingFilteredExpenses = useMemo(
    () => filteredExpenses.filter((expense) => expense.status === 'pending'),
    [filteredExpenses]
  );
  const rejectedFilteredExpenses = useMemo(
    () => filteredExpenses.filter((expense) => expense.status === 'rejected'),
    [filteredExpenses]
  );
  const totalExpenses = useMemo(
    () => approvedFilteredExpenses.reduce((sum, expense) => sum + expense.amount, 0),
    [approvedFilteredExpenses]
  );
  const pendingExpenseTotal = useMemo(
    () => pendingFilteredExpenses.reduce((sum, expense) => sum + expense.amount, 0),
    [pendingFilteredExpenses]
  );
  const rejectedExpenseTotal = useMemo(
    () => rejectedFilteredExpenses.reduce((sum, expense) => sum + expense.amount, 0),
    [rejectedFilteredExpenses]
  );
  const expensesByCategory = useMemo(() => {
    const map = {};
    approvedFilteredExpenses.forEach((expense) => {
      map[expense.category] = (map[expense.category] || 0) + expense.amount;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [approvedFilteredExpenses]);
  // Enable sound (must be called from a user gesture once)
  const enableSound = async () => {
    try {
      // Covers the synthesized new-order chime as well as the ready-voice clip:
      // they are separate audio paths and both need unlocking by a gesture.
      alertSound.arm();
      const probe = new Audio(SOUND_SRC);
      probe.volume = 0.8;
      probe.preload = 'auto';
      await probe.play();
      probe.pause();
      probe.currentTime = 0;
      setSoundArmed(true);
      showToast('Sound enabled 🔔', 'success');
    } catch (e) {
      console.warn('Enable sound failed:', e);
      showToast('Please tap again to allow sound', 'warning');
    }
  };
  // Play the "order is ready" voice file
  const playBell = (count = 1) => {
    const times = Math.min(count, 3);
    for (let i = 0; i < times; i++) {
      setTimeout(() => {
        try {
          if (!soundArmed) return;
          const a = new Audio(SOUND_SRC);
          a.volume = 0.8;
          a.preload = 'auto';
          a.play().catch(err => console.warn('Audio play error:', err));
        } catch (e) {
          console.warn('Audio creation error:', e);
        }
      }, i * 900);
    }
  };
  // Initialize baseline ready IDs once to avoid playing on first load
  useEffect(() => {
    if (!readyInitRef.current) {
      const initialReady = orders.filter(o => o.status === 'ready').map(o => o.id);
      prevReadyIdsRef.current = new Set(initialReady);
      readyInitRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Play sound when new orders become "ready"
  useEffect(() => {
    const readyOrders = orders.filter(o => o.status === 'ready');
    const nextSet = new Set(readyOrders.map(o => o.id));
    if (!readyInitRef.current) {
      prevReadyIdsRef.current = nextSet;
      readyInitRef.current = true;
      return;
    }
    let newOnes = 0;
    readyOrders.forEach(o => {
      if (!prevReadyIdsRef.current.has(o.id)) newOnes++;
    });
    if (newOnes > 0) {
      if (soundArmed) playBell(newOnes);
      else showToast('New order ready. Tap "Enable sound" to hear alerts.', 'warning');
    }
    prevReadyIdsRef.current = nextSet;
  }, [orders, soundArmed]);
  // Real-time order updates now come through the protected POS backend sync loop.
  // Amend flow
  const startAmend = (order) => {
    // Amending is the supervisor's job; front desk and kitchen hand it over.
    if (!canAmendOrders(currentUser)) {
      return showToast('Only a supervisor can amend orders. Ask the supervisor to make this change.', 'warning', 5000);
    }
    // An amend clears `approval` wholesale (see applyAmendDirect), which would
    // silently swallow a discount request that nobody has ruled on yet. Make the
    // admin decide the discount first.
    if (hasPendingDiscountRequest(order)) {
      return showToast('Resolve the pending discount request before amending this order.', 'warning', 4000);
    }
    if (order.status === 'ready' || order.status === 'picked_up' || order.status === 'delivered') {
      if (currentUser.role !== 'admin') return showToast('This order cannot be amended at this stage.', 'warning');
    }
    setAmendTarget(order);
    // Deep copy: the draft is edited in place by amendAdd/amendUpdateQty, and
    // handing it the order's own bundle objects meant those edits rewrote the
    // live order and its "original" audit snapshot as you typed.
    setAmendDraftBundles(cloneBundlesForAmend(order, defaultBundlePackaging));
    setAmendReason('');
    setAmendPaymentMethod(order.paymentMethod || '');
    setAmendSplitPayments(order.splitPayments ? order.splitPayments.map(p => ({...p})) : []);
    setAmendCustomerName(order.customerName || '');
    setAmendCustomerPhone(order.customerPhone || '');
    setAmendCustomerAddress(order.deliveryAddress || '');
    setShowAmendModal(true);
  };
  const closeAmend = () => { 
    setShowAmendModal(false); 
    setAmendTarget(null); 
    setAmendDraftBundles([]); 
    setAmendReason(''); 
    setAmendPaymentMethod('');
    setAmendSplitPayments([]);
    setAmendCustomerName('');
    setAmendCustomerPhone('');
    setAmendCustomerAddress('');
    setAmendModalTab('items');
  };
  const amendAdd = (bundleId, name, price) => {
    if (price === undefined) return;
    setAmendDraftBundles(prev => prev.map(b => {
      if (b.id !== bundleId) return b;
      const currentItems = Array.isArray(b.items) ? b.items : Object.values(b.items || {});
      const updatedItems = [...currentItems];
      const idx = updatedItems.findIndex(i => i.name === name);
      if (idx >= 0) {
        updatedItems[idx].quantity += 1;
      } else {
        updatedItems.push({ name, price, quantity: 1 });
      }
      return { ...b, items: updatedItems };
    }));
  };
  const amendUpdateQty = (bundleId, name, qty) => {
    setAmendDraftBundles(prev => prev.map(b => {
      if (b.id !== bundleId) return b;
      const currentItems = Array.isArray(b.items) ? b.items : Object.values(b.items || {});
      const updatedItems = [...currentItems];
      const idx = updatedItems.findIndex(i => i.name === name);
      if (idx >= 0) {
        updatedItems[idx].quantity = Math.max(0, qty);
        if (updatedItems[idx].quantity === 0) updatedItems.splice(idx, 1);
      }
      return { ...b, items: updatedItems };
    }));
  };
  const amendRemove = (bundleId, name) => setAmendDraftBundles(prev => prev.map(b => {
    if (b.id !== bundleId) return b;
    const currentItems = Array.isArray(b.items) ? b.items : Object.values(b.items || {});
    return { ...b, items: currentItems.filter(i => i.name !== name) };
  }));
  const amendAddBundle = () => {
    const newId = Date.now();
    setAmendDraftBundles(prev => [...prev, {
      id: newId,
      name: `Package ${prev.length + 1}`,
      items: [],
      packaging: defaultBundlePackaging,
    }]);
  };
  const applyAmendDirect = (order, newBundles, newPaymentMethod, reason, actorName, actorId, customerInfo = {}, newSplitPayments = null) => {
    const newItems = newBundles.flatMap(b => {
      const items = b?.items;
      if (!items) return [];
      return Array.isArray(items) ? items : Object.values(items);
    });
    const originalTotal = getOrderRevenueAmount(order);
    const newTotal = calculateAmendedOrderTotal(order, newBundles);
    const now = new Date().toISOString();
    const adjustment = {
      type: 'amend',
      amendedAt: now,
      amendedBy: actorName,
      amendedById: actorId,
      reason,
      originalItems: order.items,
      originalTotal,
      originalBundles: order.bundles,
      originalPaymentMethod: order.paymentMethod || null,
      newPaymentMethod: newPaymentMethod ?? order.paymentMethod ?? null,
      newItems,
      newBundles,
      newTotal,
      diffAmount: Number((newTotal - originalTotal).toFixed(2)),
      // Customer info changes
      ...(customerInfo.name !== undefined && customerInfo.name !== order.customerName && {
        originalCustomerName: order.customerName,
        newCustomerName: customerInfo.name
      }),
      ...(customerInfo.phone !== undefined && customerInfo.phone !== order.customerPhone && {
        originalCustomerPhone: order.customerPhone,
        newCustomerPhone: customerInfo.phone
      }),
      ...(customerInfo.address !== undefined && customerInfo.address !== order.deliveryAddress && {
        originalCustomerAddress: order.deliveryAddress,
        newCustomerAddress: customerInfo.address
      })
    };
    const updated = {
      ...order,
      items: newItems,
      bundles: newBundles,
      total: newTotal,
      paymentMethod: newPaymentMethod ?? order.paymentMethod,
      splitPayments: newPaymentMethod === 'split' ? (newSplitPayments || order.splitPayments || []) : (newPaymentMethod ? null : order.splitPayments),
      ...(customerInfo.name !== undefined && { customerName: customerInfo.name }),
      ...(customerInfo.phone !== undefined && { customerPhone: customerInfo.phone }),
      ...(customerInfo.address !== undefined && { deliveryAddress: customerInfo.address }),
      version: (order.version || 1) + 1,
      adjustments: [...(order.adjustments || []), adjustment],
      lastModified: now,
      approval: null
    };
    return updated;
  };
  const confirmAmend = () => {
    if (!amendTarget) return;
    const newTotal = calculateAmendedOrderTotal(amendTarget, amendDraftBundles);
    const diff = Number((newTotal - getOrderRevenueAmount(amendTarget)).toFixed(2));
    if (!canAmendOrders(currentUser)) return showToast('Only a supervisor can amend orders.', 'warning');
    const reason = amendReason.trim();
    if (!reason) {
      setAmendModalTab('details');
      return showToast('Enter a reason for this amendment.', 'warning');
    }

    // Validate split if selected
    if (amendPaymentMethod === 'split') {
      if (amendSplitPayments.length < 2) return showToast('Add at least 2 payment methods for a split', 'warning');
      const splitTotal = amendSplitPayments.reduce((s, p) => s + Number(p.amount), 0);
      if (Math.abs(splitTotal - newTotal) > 0.01) return showToast('Split amounts must equal the new order total', 'warning');
    }

    const customerInfo = {
      name: amendCustomerName,
      phone: amendCustomerPhone,
      address: amendCustomerAddress
    };
    
    setOrders(prev => {
      const updated = prev.map(o => {
        if (o.id !== amendTarget.id) return o;
        let next = applyAmendDirect(o, amendDraftBundles, amendPaymentMethod || o.paymentMethod, reason, currentUser.fullName, currentUser.username, customerInfo, amendPaymentMethod === 'split' ? amendSplitPayments : null);
        if (o.status === 'paid' || o.status === 'ready') {
          if (diff > 0) {
            next = { ...next, status: 'pending_payment', paidAt: null, dueAmount: diff, refundDue: null };
          } else if (diff < 0) {
            next = { ...next, status: 'paid', refundDue: Math.abs(diff), dueAmount: null };
          }
        }
        syncOrderToCloud(next);
        return next;
      });
      saveToLocal({ orders: updated });
      return updated;
    });
    closeAmend();
    showToast('Order amended successfully.', 'success');
  };
  const approveAmend = (orderId) => {
    setOrders(prev => {
      const now = new Date().toISOString();
      const updated = prev.map(o => {
        if (o.id !== orderId) return o;
        const app = o.approval;
        if (!app || app.status !== 'pending' || app.type !== 'amend') return o;
        const newBundles = app.payload.newBundles || (o.bundles ? o.bundles : [{
          id: Date.now(),
          name: 'Package 1',
          items: o.items,
          packaging: o.packaging || defaultBundlePackaging,
        }]);
        const newItems = newBundles.flatMap(b => {
          const items = b?.items;
          if (!items) return [];
          return Array.isArray(items) ? items : Object.values(items);
        });
        const originalTotal = getOrderRevenueAmount(o);
        const newTotal = Number(app.payload.newTotal || calculateAmendedOrderTotal(o, newBundles));
        const diff = Number((newTotal - originalTotal).toFixed(2));
        const newPaymentMethod = app.payload.newPaymentMethod ?? o.paymentMethod;
        const newSplitPayments = app.payload.newSplitPayments || null;
        const customerInfo = app.payload.customerInfo || {};
        const adjustment = {
          type: 'amend',
          amendedAt: now,
          amendedBy: currentUser.fullName,
          amendedById: currentUser.username,
          reason: app.payload.reason || 'Amended (approved)',
          originalItems: o.items,
          originalTotal,
          originalBundles: o.bundles,
          originalPaymentMethod: o.paymentMethod || null,
          newPaymentMethod: newPaymentMethod ?? o.paymentMethod ?? null,
          newItems,
          newBundles,
          newTotal,
          diffAmount: diff,
          approvedFromRequest: true,
          requestedBy: app.requestedBy,
          requestedById: app.requestedById,
          requestedAt: app.requestedAt,
          approvedAt: now,
          approvedBy: currentUser.fullName,
          approvedById: currentUser.username,
          // Customer info changes
          ...(customerInfo.name !== undefined && customerInfo.name !== o.customerName && {
            originalCustomerName: o.customerName,
            newCustomerName: customerInfo.name
          }),
          ...(customerInfo.phone !== undefined && customerInfo.phone !== o.customerPhone && {
            originalCustomerPhone: o.customerPhone,
            newCustomerPhone: customerInfo.phone
          }),
          ...(customerInfo.address !== undefined && customerInfo.address !== o.deliveryAddress && {
            originalCustomerAddress: o.deliveryAddress,
            newCustomerAddress: customerInfo.address
          })
        };

        let next = {
          ...o,
          items: newItems,
          bundles: newBundles,
          total: newTotal,
          paymentMethod: newPaymentMethod,
          splitPayments: newPaymentMethod === 'split' ? (newSplitPayments || o.splitPayments || []) : (newPaymentMethod ? null : o.splitPayments),
          ...(customerInfo.name !== undefined && { customerName: customerInfo.name }),
          ...(customerInfo.phone !== undefined && { customerPhone: customerInfo.phone }),
          ...(customerInfo.address !== undefined && { deliveryAddress: customerInfo.address }),
          version: (o.version || 1) + 1,
          adjustments: [...(o.adjustments || []), adjustment],
          lastModified: now,
          approval: { ...app, status: 'approved', approvedAt: now, approvedBy: currentUser.fullName, approvedById: currentUser.username }
        };
        if (o.status === 'paid' || o.status === 'ready') {
          if (diff > 0) {
            next = { ...next, status: 'pending_payment', paidAt: null, dueAmount: diff, refundDue: null };
          } else if (diff < 0) {
            next = { ...next, status: 'paid', refundDue: Math.abs(diff), dueAmount: null };
          }
        }
        syncOrderToCloud(next);
        return next;
      });
      saveToLocal({ orders: updated });
      return updated;
    });
    showToast('Amendment approved and applied.', 'success');
  };
  const denyAmend = (orderId) => {
    setOrders(prev => {
      const now = new Date().toISOString();
      const updated = prev.map(o => {
        if (o.id !== orderId) return o;
        if (!o.approval || o.approval.status !== 'pending') return o;
        const next = { ...o, approval: { ...o.approval, status: 'denied', deniedAt: now, deniedBy: currentUser.fullName, deniedById: currentUser.username }, lastModified: now };
        syncOrderToCloud(next); return next;
      });
      saveToLocal({ orders: updated }); return updated;
    });
    showToast('Amendment denied.', 'warning');
  };
  // Discount request decisions (admin, from the Approvals tab). Approving writes
  // the discount into the adjustments ledger and recomputes the total from the
  // order itself, so it stays consistent with analytics. Denying leaves the
  // full-price total untouched. Either way the Payment Queue unlocks.
  const approveDiscount = (orderId) => {
    if (currentUser?.role !== 'admin') return showToast('Only administrators can approve discounts', 'error');
    let approvedPercent = 0;
    setOrders(prev => {
      const now = new Date().toISOString();
      const updated = prev.map(o => {
        if (o.id !== orderId) return o;
        const app = getPendingDiscountRequest(o);
        if (!app) return o;

        const percent = Number(app.payload?.percent || 0);
        if (percent <= 0) return o;
        approvedPercent = percent;

        const adjustment = {
          type: 'discount',
          percent,
          base: 'items',
          amount: calcDiscountAmount(getOrderItemRevenue(o), percent),
          reason: app.payload?.reason || null,
          reasonDetail: app.payload?.reasonDetail || null,
          appliedBy: app.requestedBy,
          appliedById: app.requestedById,
          approvedBy: currentUser.fullName,
          approvedById: currentUser.username,
          requestedAt: app.requestedAt,
          timestamp: now,
        };
        const withDiscount = { ...o, adjustments: [...(o.adjustments || []), adjustment] };
        const next = {
          ...withDiscount,
          total: round2(getReconstructedOrderTotal(withDiscount)),
          version: (o.version || 1) + 1,
          lastModified: now,
          approval: { ...app, status: 'approved', approvedAt: now, approvedBy: currentUser.fullName, approvedById: currentUser.username },
        };
        syncOrderToCloud(next);
        return next;
      });
      saveToLocal({ orders: updated }); return updated;
    });
    showToast(approvedPercent > 0 ? `${approvedPercent}% discount approved and applied.` : 'Discount approved.', 'success');
  };
  const denyDiscount = (orderId) => {
    if (currentUser?.role !== 'admin') return showToast('Only administrators can deny discounts', 'error');
    setOrders(prev => {
      const now = new Date().toISOString();
      const updated = prev.map(o => {
        if (o.id !== orderId) return o;
        const app = getPendingDiscountRequest(o);
        if (!app) return o;
        const next = {
          ...o,
          approval: { ...app, status: 'denied', deniedAt: now, deniedBy: currentUser.fullName, deniedById: currentUser.username },
          lastModified: now,
        };
        syncOrderToCloud(next); return next;
      });
      saveToLocal({ orders: updated }); return updated;
    });
    showToast('Discount denied — the order stays at full price.', 'warning');
  };
  // Expense submit handler
  const handleExpenseSubmit = (e) => {
    e.preventDefault();
    if (!expenseDraft.category || !expenseDraft.amount || parseFloat(expenseDraft.amount) <= 0) return showToast('Valid category and positive amount required', 'warning');
    const now = new Date().toISOString();
  const newExp = {
  id: Date.now().toString(),
  timestamp: now,
  lastModified: now,
  category: expenseDraft.category,
  amount: parseFloat(expenseDraft.amount),
  description: expenseDraft.description,
  by: currentUser.fullName,
  byRole: currentUser.role,
  status: currentUser.role === 'admin' ? 'approved' : 'pending',
};
    setExpenses(prev => [...prev, newExp]);
    syncExpenseToCloud(newExp);
    setExpenseDraft({ category: '', amount: '', description: '' });
    showToast('Expense recorded', 'success');
  };
  const startEditExpense = (exp) => {
    if (currentUser.role !== 'admin') return showToast('Only admins can edit expenses', 'warning');
    setEditExpenseTarget(exp);
    setExpenseDraft({ category: exp.category, amount: exp.amount.toString(), description: exp.description });
    setShowExpenseEditModal(true);
  };
  const closeEditExpense = () => {
    setShowExpenseEditModal(false);
    setEditExpenseTarget(null);
    setExpenseDraft({ category: '', amount: '', description: '' });
  };
  const confirmEditExpense = () => {
    if (!editExpenseTarget) return;
    if (!expenseDraft.category || !expenseDraft.amount || parseFloat(expenseDraft.amount) <= 0) return showToast('Valid category and positive amount required', 'warning');
    const now = new Date().toISOString();
    const updatedExp = {
      ...editExpenseTarget,
      category: expenseDraft.category,
      amount: parseFloat(expenseDraft.amount),
      description: expenseDraft.description,
      lastModified: now
    };
    setExpenses(prev => prev.map(e => e.id === updatedExp.id ? updatedExp : e));
    syncExpenseToCloud(updatedExp);
    closeEditExpense();
    showToast('Expense updated', 'success');
  };
  // ── core: mutate + sync one expense ──
  const executeExpenseAction = async (expenseId, newStatus) => {
    const exp = expenseLedger.find((expense) => expense.id === expenseId);
    if (!exp) return;
    const updatedExp = { ...exp, status: newStatus, lastModified: new Date().toISOString() };
    setExpenses(prev => prev.map(e => e.id === expenseId ? updatedExp : e));
    await syncExpenseToCloud(updatedExp);
  };

  // ── single approve / reject  →  opens in-app confirm dialog ──
  const approveExpense = (expenseId) => {
    const exp = expenseLedger.find((expense) => expense.id === expenseId);
    if (!exp) return;
    setConfirmDialog({
      type: 'approve',
      ids: [expenseId],
      message: `Approve expense "${exp.category}" (${formatCedi(exp.amount)}) submitted by ${exp.by}?`
    });
  };

  const rejectExpense = (expenseId) => {
    const exp = expenseLedger.find((expense) => expense.id === expenseId);
    if (!exp) return;
    setConfirmDialog({
      type: 'reject',
      ids: [expenseId],
      message: `Reject expense "${exp.category}" (${formatCedi(exp.amount)}) submitted by ${exp.by}?`
    });
  };

  // ── bulk: selected checkboxes ──
  const approveSelected = () => {
    if (selectedExpenseIds.size === 0) return showToast('Select at least one expense first', 'warning');
    const total = [...selectedExpenseIds].reduce((s, id) => {
      const e = expenseLedger.find((expense) => expense.id === id);
      return s + (e ? Number(e.amount) : 0);
    }, 0);
    setConfirmDialog({
      type: 'approve',
      ids: [...selectedExpenseIds],
      message: `Approve ${selectedExpenseIds.size} selected expense(s) totalling ${formatCedi(total)}?`
    });
  };

  const rejectSelected = () => {
    if (selectedExpenseIds.size === 0) return showToast('Select at least one expense first', 'warning');
    const total = [...selectedExpenseIds].reduce((s, id) => {
      const e = expenseLedger.find((expense) => expense.id === id);
      return s + (e ? Number(e.amount) : 0);
    }, 0);
    setConfirmDialog({
      type: 'reject',
      ids: [...selectedExpenseIds],
      message: `Reject ${selectedExpenseIds.size} selected expense(s) totalling ${formatCedi(total)}?`
    });
  };

  // ── approve ALL pending at once ──
  const approveAllExpenses = () => {
    if (pendingExpenseApprovals.length === 0) return;
    const total = pendingExpenseApprovals.reduce((s, e) => s + Number(e.amount), 0);
    setConfirmDialog({
      type: 'approve',
      ids: pendingExpenseApprovals.map(e => e.id),
      message: `Approve all ${pendingExpenseApprovals.length} pending expense(s) totalling ${formatCedi(total)}?`
    });
  };

  // ── confirm-dialog executor (called by the modal's confirm button) ──
  const confirmExpenseAction = async () => {
    if (!confirmDialog) return;
    const { type, ids } = confirmDialog;
    const newStatus = type === 'approve' ? 'approved' : 'rejected';
    for (const id of ids) {
      await executeExpenseAction(id, newStatus);
    }
    setSelectedExpenseIds(new Set());
    setConfirmDialog(null);
    showToast(
      ids.length === 1
        ? `Expense ${newStatus}`
        : `${ids.length} expenses ${newStatus}`,
      newStatus === 'approved' ? 'success' : 'error'
    );
  };

  // ── checkbox helpers ──
  const toggleExpenseSelect = (id) => {
    setSelectedExpenseIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedExpenseIds.size === pendingExpenseApprovals.length) {
      setSelectedExpenseIds(new Set());
    } else {
      setSelectedExpenseIds(new Set(pendingExpenseApprovals.map(e => e.id)));
    }
  };
  const startDeleteExpense = (id) => {
    if (currentUser.role !== 'admin') return showToast('Only admins can delete expenses', 'warning');
    setDeleteExpenseId(id);
    setShowExpenseDeleteConfirm(true);
  };
  const confirmDeleteExpense = async () => {
    const exp = expenseLedger.find((expense) => expense.id === deleteExpenseId);
    if (!exp) return;
    if (exp.supabase_id) {
      const deleted = await deleteExpenseFromCloud(exp.id, exp.supabase_id);
      if (!deleted) return showToast('Failed to delete from cloud', 'error');
    }
    setExpenses(prev => {
      const updated = prev.filter(e => e.id !== deleteExpenseId);
      saveToLocal({ expenses: updated });
      return updated;
    });
    setShowExpenseDeleteConfirm(false);
    setDeleteExpenseId(null);
    showToast('Expense deleted', 'success');
  };
  // NEW: Fetch employee's own expenses
  const fetchMyExpenses = () => {
    return expenseLedger.filter((expense) => expense.by === currentUser.fullName);
  };
  
  // NEW: Submit expense for approval (already handled by handleExpenseSubmit!)
  // Your existing handleExpenseSubmit already does this correctly with the status check
  
  // NEW: Handle expense approval/rejection (ADMIN ONLY)
  const handleExpenseApproval = async (expenseId, decision) => {
    if (currentUser.role !== 'admin') {
      return showToast('Only admins can approve expenses', 'warning');
    }
    
    const expense = expenseLedger.find((entry) => entry.id === expenseId);
    if (!expense) return;
    
    const now = new Date().toISOString();
    const updatedExp = {
      ...expense,
      status: decision === 'approve' ? 'approved' : 'rejected',
      lastModified: now,
      reviewedAt: now,
      reviewedBy: currentUser.fullName
    };
    
    setExpenses(prev => prev.map(e => e.id === expenseId ? updatedExp : e));
    syncExpenseToCloud(updatedExp);
    showToast(`Expense ${decision === 'approve' ? 'approved' : 'rejected'}`, 'success');
  };
  
  // NEW: Clear date range filter
  const clearDateFilter = () => {
    setExpenseDateRange({ startDate: '', endDate: '' });
  };
  
  // ============================================
  // INVENTORY MANAGEMENT FUNCTIONS
  // ============================================
  
  // Check inventory levels and update low stock alerts
  const checkInventoryLevels = useCallback(() => {
    const lowStock = inventory.filter(item => item.currentStock <= item.lowStockThreshold);
    setLowStockItems(lowStock);
    if (lowStock.length > 0 && !showStockAlert) {
      setShowStockAlert(true);
    }
  }, [inventory, showStockAlert]);
  
  // Initialize inventory from base ingredients ONLY when there is genuinely no data anywhere.
  // Guard: check localStorage first (cloud sync writes there), only seed as last resort.
  // IMPORTANT: We never push seeded zeros to cloud — cloud already has real stock values.
  // The seed is purely a local placeholder so the UI shows something while cloud sync runs.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (inventory.length > 0) return; // already have data in state
    // Cloud sync writes to localStorage — check there before seeding zeros
    const saved = localStorage.getItem('inventory');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setInventory(parsed);
          return;
        }
      } catch { /* fall through */ }
    }
    // Truly no data anywhere (fresh Vercel deploy, no localStorage) —
    // seed with zeros as a UI placeholder. DO NOT sync to cloud here:
    // initialCloudSync will fetch real stock values from Supabase and
    // mergeInventory will replace these placeholders with the real data.
    const now = new Date().toISOString();
    const initialInventory = baseIngredients.map(ing => ({
      id: `inv_${ing.id}_${Date.now()}`,
      ingredientId: ing.id,
      itemName: ing.name,
      currentStock: 0,
      lowStockThreshold: ing.reorderLevel,
      unit: ing.unit,
      costPerUnit: ing.costPerUnit,
      createdAt: now,
      createdBy: 'System',
      lastModified: now,
      needsInitialCount: true
      // NOTE: no supabase_id here on purpose — cloud sync will assign it
    }));
    setInventory(initialInventory);
    // Only persist to localStorage — NOT to cloud
    localStorage.setItem('inventory', JSON.stringify(initialInventory));
    showToast('📦 Loading inventory from cloud...', 'info', 3000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inventory.length]);
  
  // Run inventory check whenever inventory changes
  useEffect(() => {
    checkInventoryLevels();
  }, [inventory, checkInventoryLevels]);
  
  // Add or update inventory item
  const handleInventorySave = async () => {
    if (!inventoryForm.itemName || !inventoryForm.currentStock || !inventoryForm.lowStockThreshold) {
      return showToast('Please fill all required fields', 'warning');
    }
    
    const now = new Date().toISOString();
    
    if (editInventoryItem) {
      // Update existing item — preserve ingredientId always
      const targetId = editInventoryItem.id;
      let updatedItem = null;
      setInventory(prev => {
        const updated = prev.map(item => {
          if (item.id === targetId) {
            updatedItem = {
              ...item,
              itemName: inventoryForm.itemName,
              currentStock: Number(inventoryForm.currentStock),
              lowStockThreshold: Number(inventoryForm.lowStockThreshold),
              dailyDefault: inventoryForm.dailyDefault !== '' ? Number(inventoryForm.dailyDefault) : (item.dailyDefault ?? 0),
              unit: inventoryForm.unit,
              costPerUnit: Number(inventoryForm.costPerUnit) || 0,
              lastModified: now,
              modifiedBy: currentUser.fullName,
            };
            return updatedItem;
          }
          return item;
        });
        localStorage.setItem('inventory', JSON.stringify(updated));
        return updated;
      });
      setTimeout(async () => {
        if (updatedItem) await syncInventoryToCloud(updatedItem);
      }, 0);
      showToast('Inventory item updated', 'success');
    } else {
      // Add new custom item — try to match ingredientId by name
      let ingredientId = null;
      const match = baseIngredients.find(
        b => b.name.toLowerCase() === inventoryForm.itemName.toLowerCase()
      );
      if (match) ingredientId = match.id;

      const newItem = {
        id: `inv_custom_${Date.now()}`,
        ingredientId,
        itemName: inventoryForm.itemName,
        currentStock: Number(inventoryForm.currentStock),
        lowStockThreshold: Number(inventoryForm.lowStockThreshold),
        dailyDefault: inventoryForm.dailyDefault !== '' ? Number(inventoryForm.dailyDefault) : 0,
        unit: inventoryForm.unit,
        costPerUnit: Number(inventoryForm.costPerUnit) || 0,
        createdAt: now,
        createdBy: currentUser.fullName,
        lastModified: now,
      };
      setInventory(prev => {
        const updated = [...prev, newItem];
        localStorage.setItem('inventory', JSON.stringify(updated));
        return updated;
      });
      await syncInventoryToCloud(newItem);
      showToast('Inventory item added', 'success');
    }
    
    closeInventoryModal();
  };
  
  // Deduct inventory when order is placed - uses recipe mapping
  const deductInventoryForOrder = async (orderItems, orderId) => {
    const deductions = {}; // { ingredientId: totalQuantity }
    const movementDetails = []; // Track what was deducted for logging

    // For each item in the order, look up its recipe and accumulate ingredient needs
    orderItems.forEach(orderItem => {
      const recipe = menuRecipes[orderItem.name];
      if (recipe) {
        recipe.forEach(({ ingredientId, quantity: recipeQty }) => {
          deductions[ingredientId] = (deductions[ingredientId] || 0) + recipeQty * orderItem.quantity;
        });
        movementDetails.push({ menuItem: orderItem.name, quantity: orderItem.quantity });
      }
    });

    if (Object.keys(deductions).length === 0) return;

    const now = new Date().toISOString();

    // Collect updated items outside the updater so we can sync them after
    let itemsToSync = [];

    // Use functional update to always deduct from the CURRENT inventory state
    setInventory(prev => {
      const ingredientsDeducted = [];
      itemsToSync = []; // reset for this run
      const updated = prev.map(item => {
        if (item.ingredientId && deductions[item.ingredientId]) {
          const deductAmount = deductions[item.ingredientId];
          ingredientsDeducted.push({ name: item.itemName, quantity: deductAmount, unit: item.unit });
          const updatedItem = {
            ...item,
            currentStock: Math.max(0, item.currentStock - deductAmount),
            lastModified: now,
            lastDeduction: { amount: deductAmount, at: now, reason: `Order #${orderId?.slice(-6) || 'Unknown'}` }
          };
          itemsToSync.push(updatedItem); // queue for sync with fresh values
          return updatedItem;
        }
        return item;
      });

      // Persist locally immediately
      localStorage.setItem('inventory', JSON.stringify(updated));

      // Log the movement
      const movement = {
        id: `mov_${Date.now()}`,
        timestamp: now,
        type: 'deduction',
        orderId,
        menuItems: movementDetails,
        ingredientsDeducted,
        by: currentUser.fullName
      };
      setStockMovements(prev2 => [movement, ...prev2].slice(0, 100));

      return updated;
    });

    // Sync the freshly-updated items to cloud AFTER the state update (not inside the updater)
    // Use a small timeout to let React batch the state update first.
    // One at a time: firing every ingredient at once alongside the order upload
    // meant any single failure turned the header red. Once one fails, queue the
    // rest for the retry rather than make each wait out its own timeout.
    setTimeout(async () => {
      let failed = false;
      for (const item of itemsToSync) {
        if (failed) { addToPendingSyncInventory(item); continue; }
        failed = !(await syncInventoryToCloud(item));
      }
    }, 0);
  };
  
  // Adjust stock (for manual adjustments)
  const adjustInventoryStock = async (itemId, adjustment, reason) => {
    const now = new Date().toISOString();
    let adjustedItem = null;

    setInventory(prev => {
      const updated = prev.map(item => {
        if (item.id === itemId) {
          adjustedItem = {
            ...item,
            currentStock: Math.max(0, item.currentStock + adjustment),
            lastModified: now,
            modifiedBy: currentUser.fullName,
            lastAdjustment: {
              amount: adjustment,
              reason,
              by: currentUser.fullName,
              at: now
            }
          };
          return adjustedItem;
        }
        return item;
      });
      localStorage.setItem('inventory', JSON.stringify(updated));
      return updated;
    });

    // Sync to cloud after state update (use timeout so adjustedItem is populated)
    setTimeout(async () => {
      if (adjustedItem) {
        await syncInventoryToCloud(adjustedItem);
        showToast(`Stock adjusted for ${adjustedItem.itemName}`, 'success');
      }
    }, 0);
  };
  
  // Delete inventory item
  const deleteInventoryItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) return;
    
    let itemToDelete = null;
    setInventory(prev => {
      itemToDelete = prev.find(i => i.id === itemId) || null;
      const updated = prev.filter(item => item.id !== itemId);
      localStorage.setItem('inventory', JSON.stringify(updated));
      return updated;
    });

    // Delete from cloud after state update
    setTimeout(async () => {
      if (itemToDelete) {
        try {
          const deleteId = itemToDelete.supabase_id || itemToDelete.id;
          await mutatePosResource('inventory', 'delete', { id: deleteId }, posAccessTokenRef.current);
        } catch (e) {
          console.error('Failed to delete from cloud:', e);
        }
      }
    }, 0);
    
    showToast('Inventory item deleted', 'success');
  };
  
  // Open inventory modal for adding
  const openInventoryModal = () => {
    setInventoryForm({ itemName: '', currentStock: '', lowStockThreshold: '', dailyDefault: '', unit: 'piece' });
    setEditInventoryItem(null);
    setShowInventoryModal(true);
  };
  
  // Open inventory modal for editing
  const openEditInventory = (item) => {
    setInventoryForm({
      itemName: item.itemName,
      currentStock: item.currentStock.toString(),
      lowStockThreshold: item.lowStockThreshold.toString(),
      dailyDefault: (item.dailyDefault ?? '').toString(),
      unit: item.unit,
      costPerUnit: (item.costPerUnit || 0).toString()
    });
    setEditInventoryItem(item);
    setShowInventoryModal(true);
  };
  
  // Close inventory modal
  const closeInventoryModal = () => {
    setShowInventoryModal(false);
    setEditInventoryItem(null);
    setInventoryForm({ itemName: '', currentStock: '', lowStockThreshold: '', dailyDefault: '', unit: 'piece', costPerUnit: '' });
  };
  
  const getSyncStatusDisplay = () => {
    const statusConfig = {
      idle: { color: 'text-gray-500', text: 'Ready', bg: 'bg-gray-100' },
      syncing: { color: 'text-indigo-700', text: 'Syncing...', bg: 'bg-indigo-100' },
      success: { color: 'text-green-600', text: 'Synced', bg: 'bg-green-100' },
      error: { color: 'text-red-600', text: 'Error', bg: 'bg-red-100' },
      offline: { color: 'text-orange-600', text: 'Offline', bg: 'bg-orange-100' },
      held: { color: 'text-amber-700', text: 'Saved on tablet', bg: 'bg-amber-100' }
    };
    const cfg = statusConfig[syncStatus];
    return (
      <div className={classNames('flex items-center space-x-2 px-3 py-1 rounded-full transition-all duration-300', cfg.bg, cfg.color)}>
        {syncStatus === 'syncing' && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>}
        <span className="text-xs font-medium">{cfg.text}</span>
        {lastSyncTime && syncStatus === 'success' && <span className="text-xs opacity-75">({lastSyncTime.toLocaleTimeString()})</span>}
      </div>
    );
  };
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const showOrderDetailsModal = (order) => { setSelectedOrder(order); setShowOrderDetails(true); };
  const closeRiderAssignmentModal = () => {
    setShowRiderAssignmentModal(false);
    setRiderAssignmentTarget(null);
    setRiderAssignmentForm({ riderId: '', note: '' });
  };
  const openRiderAssignmentModal = (order) => {
    const assignedRider = getOrderAssignedRider(order);
    setRiderAssignmentTarget(order);
    setRiderAssignmentForm({
      riderId: assignedRider?.riderId || '',
      note: assignedRider?.note || '',
    });
    setShowRiderAssignmentModal(true);
  };
  const confirmRiderAssignment = async () => {
    if (!riderAssignmentTarget) return;
    if (!isOnline) return showToast('Go online before assigning a rider.', 'warning');

    const riderId = riderAssignmentForm.riderId;
    const selectedRider = riders.find((rider) => rider.id === riderId);
    const selectedPresenceStatus = riderPresenceById.get(riderId)?.availability_status || 'offline';
    const canAssignSelectedRider = ['online', 'delivering'].includes(selectedPresenceStatus);
    const note = (riderAssignmentForm.note || '').trim();
    const shouldMarkReady = Boolean(riderAssignmentForm.markReady);

    if (!selectedRider) return showToast('Select a rider to continue.', 'warning');
    if (!canAssignSelectedRider) {
      return showToast('Only riders who are online or already delivering can be assigned from the POS.', 'warning');
    }

    const nowIso = new Date().toISOString();
    const cloudAssignments = await fetchRiderAssignmentsFromCloud();
    if (Array.isArray(cloudAssignments)) setRiderAssignments(cloudAssignments);
    const latestCloudAssignment = Array.isArray(cloudAssignments)
      ? cloudAssignments
          .filter((assignment) => assignment.order_id === riderAssignmentTarget.id)
          .sort((a, b) => new Date(b.assigned_at || 0).getTime() - new Date(a.assigned_at || 0).getTime())[0]
      : null;
    const existingAssignment = latestCloudAssignment || riderAssignmentsByOrder.get(riderAssignmentTarget.id);
    const preserveAssignmentProgress = existingAssignment
      && existingAssignment.rider_id === selectedRider.id
      && ['assigned', 'accepted', 'arrived_at_restaurant', 'picked_up'].includes(existingAssignment.status);
    const assignmentPayload = existingAssignment
      ? {
          rider_id: selectedRider.id,
          status: preserveAssignmentProgress ? (existingAssignment.status || 'assigned') : 'assigned',
          assigned_at: preserveAssignmentProgress ? (existingAssignment.assigned_at || nowIso) : nowIso,
        }
      : {
          order_id: riderAssignmentTarget.id,
          rider_id: selectedRider.id,
          status: 'assigned',
          assigned_at: nowIso,
        };

    try {
      await mutatePosResource('rider-assignments', 'upsert', existingAssignment
        ? { ...assignmentPayload, id: existingAssignment.id }
        : assignmentPayload, posAccessTokenRef.current);
    } catch (assignmentError) {
      showToast(safeErrorMessage(assignmentError, 'Couldn’t assign the rider. Please try again.', { context: 'assign-rider' }), 'error');
      return;
    }

    const adjustment = {
      type: 'rider_assignment',
      timestamp: nowIso,
      by: currentUser?.fullName || 'Unknown',
      byId: currentUser?.username || 'unknown',
      riderId: selectedRider.id,
      riderName: selectedRider.name,
      riderPhone: selectedRider.phone || null,
      note: note || null,
      status: 'assigned',
    };

    let savedOrder = null;
    setOrders(prev => {
      const updated = prev.map(o => {
        if (o.id !== riderAssignmentTarget.id) return o;

        const next = {
          ...o,
          status: shouldMarkReady ? 'ready' : o.status,
          readyAt: shouldMarkReady ? (o.readyAt || nowIso) : o.readyAt,
          lastModified: nowIso,
          adjustments: [...(o.adjustments || []), adjustment],
        };

        savedOrder = next;
        syncOrderToCloud(next);
        return next;
      });

      saveToLocal({ orders: updated });
      return updated;
    });

    await refreshRiderData();
    if (savedOrder && selectedOrder?.id === savedOrder.id) setSelectedOrder(savedOrder);

    const deliveryVerificationCode = getOrderDeliveryVerificationCode(savedOrder || riderAssignmentTarget);
    closeRiderAssignmentModal();
    showToast(
      `${selectedRider.name} assigned${deliveryVerificationCode ? `. Delivery code: ${deliveryVerificationCode}` : ''}.`,
      'success',
    );
  };
  const searchTerm = useMemo(() => menuSearch.trim().toLowerCase(), [menuSearch]);
  const filteredMain = useMemo(() => {
    if (!searchTerm) return menuData.mainPackages;
    return menuData.mainPackages.filter(i => i.name.toLowerCase().includes(searchTerm));
  }, [searchTerm, menuData.mainPackages]);
  const filteredExtras = useMemo(() => {
    if (!searchTerm) return menuData.extras;
    return menuData.extras.filter(i => i.name.toLowerCase().includes(searchTerm));
  }, [searchTerm, menuData.extras]);
  const filteredPromotions = useMemo(() => {
    if (!searchTerm) return menuData.promotions;
    return menuData.promotions.filter(i => i.name.toLowerCase().includes(searchTerm));
  }, [searchTerm, menuData.promotions]);
  const calculateTotal = useMemo(() =>
    currentBundles.reduce((t, b) => t + Object.values(b.items).reduce((s, i) => s + i.price * i.quantity, 0), 0),
    [currentBundles]
  );
  const deliveryFee = orderType === 'online' ? Number(deliveryFeeInput || 0) : 0;
  const packagingCost = currentBundles.reduce((sum, bundle) => (
    bundleHasItems(bundle) ? sum + (packagingPricing[bundle.packaging || defaultBundlePackaging] || 0) : sum
  ), 0);
  const packaging = packagingCost > 0 ? 'paper' : 'rubber';
  // Admins discount the cart directly; everyone else is raising a request, so
  // the cart keeps showing full price and the discount only bites once an admin
  // approves it from the Approvals tab.
  const canApplyDiscountDirectly = currentUser?.role === 'admin';
  const maxDiscountPercent = canApplyDiscountDirectly ? MAX_DISCOUNT_PERCENT : STAFF_MAX_DISCOUNT_PERCENT;
  const discountAmount = calcDiscountAmount(calculateTotal, discountPercent);
  const appliedDiscountAmount = canApplyDiscountDirectly ? discountAmount : 0;
  const grandTotal = calculateTotal + packagingCost + deliveryFee - appliedDiscountAmount;
  const calcBundleTotal = useCallback((items, bundlePackaging = defaultBundlePackaging) => (
    Object.values(items).reduce((s, i) => s + i.price * i.quantity, 0) +
    (packagingPricing[bundlePackaging || defaultBundlePackaging] || 0)
  ), []);
  const renderMenuSection = useCallback((title, items, category) => {
    return (
      <div className="mb-2">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h3 className="flex items-center gap-3 text-base font-bold text-gray-700 dark:text-gray-100">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/80 text-[color:var(--text-accent)] shadow-sm ring-1 ring-black/5 dark:bg-slate-900/70 dark:text-rose-100">
              <MenuCategoryIcon category={category} className="h-4 w-4" />
            </span>
            <span>
              {title}
              <span className="ml-2 text-xs font-medium text-gray-400">({items.length})</span>
            </span>
          </h3>
        </div>
        {items.length === 0 ? (
          <div className="text-center text-gray-400 py-8 text-sm">
            No items match your search
          </div>
        ) : (
          <div className="pos-menu-grid">
            {items.map(item => {
              const orderKey = `${category}_${item.name}`;
              const activeBundle = currentBundles.find(b => b.id === activeBundleId);
              const currentQuantity = activeBundle?.items[orderKey]?.quantity || 0;
              const stockStatus1 = getItemStockStatus(item.name, 1);
              const isOutOfStock = !stockStatus1.available;
              const nextStatus = getItemStockStatus(item.name, Math.max(1, currentQuantity + 1));
              const isLowStock = !isOutOfStock && !nextStatus.available;

              return (
                <div key={item.name}
                  onClick={() => { if (!isOutOfStock) addToOrder(category, item.name); }}
                  className={classNames(
                    'menu-card group cursor-pointer',
                    isOutOfStock ? 'card-oos cursor-not-allowed' : currentQuantity > 0 && 'menu-card-active'
                  )}>
                  <div className="menu-card-media card-img">
                    {item.image ? (
                      <img src={item.image} alt={item.name}
                        className={classNames('h-full w-full object-cover', isOutOfStock && 'grayscale')}
                        loading="lazy"
                        onError={(e) => {
                          if (!e.currentTarget.src.includes('/Images/komis.png')) {
                            e.currentTarget.src = '/Images/komis.png';
                            e.currentTarget.style.objectFit = 'contain';
                            e.currentTarget.style.padding = '1.2rem';
                            e.currentTarget.style.background = 'rgba(255, 248, 244, 0.82)';
                            return;
                          }
                          e.currentTarget.style.display = 'none';
                        }} />
                    ) : (
                      <img
                        src="/Images/komis.png"
                        alt="Komis logo"
                        className="h-full w-full object-contain p-5"
                        loading="lazy"
                      />
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/56 via-black/10 to-transparent p-2">
                      {!isOutOfStock && (
                        <div className="flex justify-start">
                          <span className="menu-card-price price-badge rounded-2xl px-2 py-1 font-bold shadow-sm">
                            {formatCedi(item.price)}
                          </span>
                        </div>
                      )}
                    </div>
                    {currentQuantity > 0 && !isOutOfStock && (
                      <div className="absolute right-2 top-2 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[10px] font-bold text-white shadow-lg">
                        {currentQuantity}
                      </div>
                    )}
                    {isOutOfStock && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[color:var(--bg-overlay)]">
                        <span className="oos-badge">Out of stock</span>
                      </div>
                    )}
                    {isLowStock && (
                      <div className="absolute left-2 top-2">
                        <span className="rounded-full bg-amber-500 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white shadow">Low stock</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5 p-2.5">
                    <div className="min-w-0">
                      <h4 className="menu-card-title" title={item.name}>{item.name}</h4>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [addToOrder, activeBundleId, currentBundles, getItemStockStatus, updateOrderQuantity]);

  // ── Context value: all state & functions shared with page/modal components ──
  const ctxValue = {
    // Auth
    currentUser, isAuthenticated, handleLogin, handleLogout, loginForm, setLoginForm, loginError, hasPermission,
    // Lets a screen send the user to another tab (e.g. Finance's Money home → Approvals).
    openTab: setCurrentTab,
    // Orders
    orders, setOrders, currentBundles, setCurrentBundles, activeBundleId, setActiveBundleId,
    addToOrder, updateOrderQuantity, addNewBundle, updateBundleName, updateBundlePackaging, removeBundle, clearCurrentOrder,
    processOrder, confirmPlaceOrder, isProcessing, getOrdersFor, getOutOfRangeActiveOrders,
    updateOrderStatus, handlePaymentConfirm, showOrderDetailsModal,
    selectedOrder, setSelectedOrder, showOrderDetails, setShowOrderDetails,
    showRiderAssignmentModal, setShowRiderAssignmentModal, riderAssignmentTarget, riderAssignmentForm, setRiderAssignmentForm,
    openRiderAssignmentModal, closeRiderAssignmentModal, confirmRiderAssignment,
    riders, riderPresence, riderAssignments, getOrderAssignedRider, getOrderDeliveryFee, getOrderDeliveryVerificationCode,
    // POS inputs
    orderType, setOrderType, orderSource, setOrderSource, orderSourceDetail, setOrderSourceDetail,
    customerName, setCustomerName, customerPhone, setCustomerPhone,
    deliveryAddress, setDeliveryAddress, deliveryFeeInput, setDeliveryFeeInput, notes, setNotes,
    posPaymentMethod, setPosPaymentMethod, splitPayments, setSplitPayments,
    showSplitPaymentModal, setShowSplitPaymentModal,
    packaging, packagingCost, grandTotal, calculateTotal, calcBundleTotal,
    showOrderConfirm, setShowOrderConfirm,
    // Discount (cart)
    discountPercent, setDiscountPercent, discountReason, setDiscountReason,
    discountReasonDetail, setDiscountReasonDetail,
    discountAmount, appliedDiscountAmount, canApplyDiscountDirectly, maxDiscountPercent,
    // Menu
    menuData, menuSearch, setMenuSearch, filteredMain, filteredExtras, filteredPromotions,
    renderMenuSection, getItemStockStatus, validateStockForOrder, searchTerm,
    // Sync
    syncStatus, isOnline, lastSyncTime, pendingSync, forceSync, syncOrderToCloud,
    getSyncStatusDisplay, enableSound, soundArmed,
    // Date filters
    dateFilter, setDateFilter, customDate, setCustomDate, customDateRange, setCustomDateRange,
    // Responsive
    isMobile, showMobileCart, setShowMobileCart,
    showHeaderMenu, setShowHeaderMenu, headerMenuRef,
    // Analytics
    analyticsData, filteredAnalyticsOrders, settledAnalyticsOrders, salesByUser, revenueByMethod, revenueBySource, topItems,
    statusDistribution, totalRefunds, totalDue, dailyTrend, hourlyTrend,
    prepTimeStats, fulfillmentTimeStats, todaysOrders, totalRevenue, avgOrderValue,
    analyticsLoading,
    // Approvals
    pendingApprovals, pendingExpenseApprovals, approveAmend, denyAmend,
    pendingDiscountApprovals, approveDiscount, denyDiscount,
    getOrderDiscountAmount, getOrderDiscountAdjustment, hasPendingDiscountRequest, getPendingDiscountRequest,
    // Amend
    showAmendModal, setShowAmendModal, amendTarget, setAmendTarget,
    amendDraftBundles, setAmendDraftBundles, amendReason, setAmendReason,
    amendPaymentMethod, setAmendPaymentMethod, amendSplitPayments, setAmendSplitPayments,
    amendCustomerName, setAmendCustomerName, amendCustomerPhone, setAmendCustomerPhone,
    amendCustomerAddress, setAmendCustomerAddress, amendModalTab, setAmendModalTab,
    startAmend, closeAmend, confirmAmend, amendAdd, amendUpdateQty, amendRemove, amendAddBundle,
    // Expenses
    expenses: expenseLedger, setExpenses, pendingSyncExpenses, setPendingSyncExpenses,
    expenseDraft, setExpenseDraft, expenseSearch, setExpenseSearch,
    showExpenseEditModal, setShowExpenseEditModal, editExpenseTarget, setEditExpenseTarget,
    showExpenseDeleteConfirm, setShowExpenseDeleteConfirm, deleteExpenseId, setDeleteExpenseId,
    expenseDateRange, setExpenseDateRange, clearDateFilter,
    myExpenses, showMyExpenseModal, setShowMyExpenseModal, myExpenseForm, setMyExpenseForm,
    selectedExpenseIds, setSelectedExpenseIds, confirmDialog, setConfirmDialog,
    handleExpenseSubmit, startEditExpense, closeEditExpense,
    confirmEditExpense, startDeleteExpense, confirmDeleteExpense,
    approveExpense, rejectExpense, approveSelected, rejectSelected,
    approveAllExpenses, confirmExpenseAction, toggleExpenseSelect, toggleSelectAll,
    syncExpenseToCloud, addToPendingSyncExpenses,
    filteredExpenses, approvedFilteredExpenses, pendingFilteredExpenses, rejectedFilteredExpenses,
    totalExpenses, pendingExpenseTotal, rejectedExpenseTotal, expensesByCategory,
    handleExpenseApproval, fetchMyExpenses,
    // History
    historySorted, completedForRangeHistory,
    // Complaints + marketing logs
    complaints, complaintsLoading, refreshComplaints, saveComplaint, staffMembers,
    marketingActivities, marketingLoading, refreshMarketingActivities,
    saveMarketingActivity, deleteMarketingActivity, fetchOrdersRange,
    // Riders
    riderDeliveryOrders, riderSummary, riderSummaryTotals,
    // Inventory
    inventory, setInventory, lowStockItems, showInventoryModal, setShowInventoryModal,
    inventoryForm, setInventoryForm, editInventoryItem, setEditInventoryItem,
    inventorySearch, setInventorySearch, showStockAlert, setShowStockAlert,
    stockMovements, setStockMovements, showMovementsModal, setShowMovementsModal,
    pendingSyncInventory, setPendingSyncInventory,
    handleInventorySave, openInventoryModal, openEditInventory, closeInventoryModal,
    adjustInventoryStock, deleteInventoryItem, manualInventoryReset,
    // Export
    showExportModal, setShowExportModal, exportRangeType, setExportRangeType,
    exportSingleDate, setExportSingleDate, exportStartDate, setExportStartDate,
    exportEndDate, setExportEndDate, exportTypes, setExportTypes,
    exportOrderStatuses, setExportOrderStatuses, runExport,
    // EOD
    showEodReport, setShowEodReport, eodReportDate, setEodReportDate, computeEodReport, showEndOfDaySummary,
    // Kitchen display
    kitchenDisplayMode, setKitchenDisplayMode,
    // Receipts
    printReceiptWindow,
    // Notifications
    notification, showToast, celebrateOrder,
    // Misc
    paymentInputs, setPaymentInputs, resetSales,
    getAvailableTabs, getDateFilterCondition,
    // Theme
    darkMode, toggleDarkMode,
    // AI assistant — reads the live POS session token for the /api/ai backend call
    getAccessToken: () => posAccessTokenRef.current,
  };

  const availableTabs = getAvailableTabs();
  // Render by the tab we can actually show, never the raw currentTab.
  //
  // currentTab initialises to 'pos' and only handleLogin ever reset it — the
  // session-restore path did not. So a kitchen-only employee who reloaded the
  // page or reopened the PWA got the entire POS order-entry screen (menu,
  // customer details, payment, discounts) while the header, which already fell
  // back to availableTabs[0], said "Kitchen". Deriving the title and the body
  // from one value keeps them honest and makes it impossible to render a tab
  // this user has no permission for.
  const activeTab = availableTabs.some((tab) => tab.id === currentTab)
    ? currentTab
    : availableTabs[0]?.id;
  const currentTabMeta = availableTabs.find((tab) => tab.id === activeTab);
  const mobilePrimaryTabs = ['pos', 'queue', 'kitchen'];
  const mobileMoreTabs = availableTabs.filter((tab) => !mobilePrimaryTabs.includes(tab.id));
  if (!isAuthenticated) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        <video autoPlay loop muted playsInline className="absolute top-0 left-0 w-full h-full object-cover">
          <source src="/videos/restaurant-background.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="relative z-10 min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, rgba(36, 16, 11, 0.52) 0%, rgba(112, 33, 24, 0.36) 100%)' }}>
          <div className="relative max-w-md w-full">
            <div className="rounded-[30px] border p-8 shadow-2xl card" style={{ background: 'linear-gradient(180deg, rgba(255, 250, 247, 0.94) 0%, rgba(255, 243, 240, 0.96) 100%)', borderColor: 'rgba(255, 255, 255, 0.24)' }}>
              <div className="text-center mb-8">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[28px] bg-white/70 shadow-lg ring-1 ring-black/5">
                  <img src="/Images/komis.png" alt="Komis logo" className="h-14 w-14 object-contain" />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ color: '#251714' }}>Komis Restaurant</h2>
                <p style={{ color: '#7f655a' }}>Sign in to your account</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#7f655a' }}>Username</label>
                  <input type="text" required
                    className="w-full px-4 py-3 rounded-xl transition-all duration-200 input-focus"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(180, 35, 24, 0.14)', color: '#251714' }}
                    placeholder="Enter username" value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}/>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#7f655a' }}>Password</label>
                  <input type="password" required
                    className="w-full px-4 py-3 rounded-xl transition-all duration-200 input-focus"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(180, 35, 24, 0.14)', color: '#251714' }}
                    placeholder="Enter password" value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}/>
                </div>
                {loginError && <div className="rounded-xl p-3 text-sm" style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.16)', color: '#b42318' }}>{loginError}</div>}
                <button type="submit" disabled={isLoggingIn} aria-busy={isLoggingIn}
                  className="w-full flex items-center justify-center gap-2 font-semibold py-3 px-6 rounded-xl transition-all duration-200 active:scale-95 shadow-lg button-hover disabled:opacity-80 disabled:cursor-wait"
                  style={{ backgroundColor: '#B42318', color: '#fff7f6' }}>
                  {isLoggingIn && <span className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" aria-hidden="true"></span>}
                  {isLoggingIn ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <AppContext.Provider value={ctxValue}>
    <ClockProvider fastTick={kitchenDisplayMode}>
    <div className={classNames('app-shell min-h-screen bg-gray-50', darkMode && 'dark')} style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <InstallPrompt />
      <NewOrderAlert
        orders={alarmingOrders}
        isRinging={orderAlarmRinging}
        rungOut={orderAlarmRungOut}
        onAcknowledge={acknowledgeOrderAlarm}
        onView={handleViewAlarmedOrders}
      />
      {showPushPrompt && (
        <div className="fixed top-0 left-0 right-0 z-[9998] flex items-center justify-between px-4 py-3 shadow-lg" style={{ backgroundColor: '#065f46', color: '#fff', paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}>
          <span className="text-sm font-medium">Enable notifications for order alerts?</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handlePushAccept} className="text-sm font-semibold px-3 py-1 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              Enable
            </button>
            <button onClick={handlePushDismiss} className="text-sm px-2 py-1 rounded-lg opacity-80 hover:opacity-100">
              Not now
            </button>
          </div>
        </div>
      )}
      <AppModals />
      <Suspense fallback={null}>
        <AiAssistant />
      </Suspense>
      <div className={classNames('flex', isMobile ? 'flex-col' : 'flex-row')}>
        {/* Left sidebar nav - desktop */}
        {!isMobile && (
          <aside className="sidebar-nav h-screen sticky top-0 flex flex-col py-5 custom-scrollbar overflow-y-auto">
            <div className="px-4 pb-5">
              <div className="sidebar-brand-mark">
                <img src="/Images/komis.png" alt="Komis logo" className="sidebar-brand-image" />
              </div>
            </div>
            <div className="px-2">
            {availableTabs.map(tab => (
              <button key={tab.id} onClick={() => setCurrentTab(tab.id)}
                className={classNames('sidebar-nav-btn', activeTab === tab.id && 'active')}>
                <span className="sidebar-icon relative">
                  <ShellIcon name={tab.id} />
                  {tab.id === 'queue' && getOrdersFor('pending_payment').length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                      {getOrdersFor('pending_payment').length}
                    </span>
                  )}
                  {tab.id === 'kitchen' && getOrdersFor('paid').length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                      {getOrdersFor('paid').length}
                    </span>
                  )}
                  {tab.id === 'approvals' && currentUser.role === 'admin' && (pendingApprovals.length + pendingExpenseApprovals.length) > 0 && (
                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                      {pendingApprovals.length + pendingExpenseApprovals.length}
                    </span>
                  )}
                </span>
                <span className="nav-label text-sm font-medium">{tab.label}</span>
              </button>
            ))}
            </div>
          </aside>
        )}
        {/* Main content area */}
        <div className="app-main flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="app-header" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
            <div className="px-4 sm:px-6 lg:px-8">
              <div className={classNames('flex justify-between items-center gap-4', isMobile ? 'min-h-[60px] py-2.5' : 'min-h-[72px] py-3')}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="app-header-title min-w-0">
                    <h1 className="truncate">{currentTabMeta?.label || 'Komis POS'}</h1>
                  </div>
                  {isMobile ? (
                    <div className="mobile-sync-chip">
                      {getSyncStatusDisplay()}
                    </div>
                  ) : (
                  <div className="hidden sm:flex items-center gap-2 flex-wrap">
                    <span className="header-meta">{currentUser.fullName}</span>
                    <span className="header-chip">{currentUser.role}</span>
                    {getSyncStatusDisplay()}
                    {!isOnline && <span className="header-chip">Offline</span>}
                    {pendingSync.length > 0 && <span className="header-chip">{pendingSync.length} pending</span>}
                    {pendingSyncExpenses.length > 0 && <span className="header-chip">{pendingSyncExpenses.length} expenses</span>}
                    {!soundArmed && (
                      <button onClick={enableSound} className="action-btn action-btn-secondary action-btn-sm">
                        Enable sound
                      </button>
                    )}
                  </div>
                  )}
                </div>
                {!isMobile && (
                <div className="flex items-center gap-3">
                  {/* Notifications toggle — bell shows current state */}
                  {isPushSupported() && (
                    <button
                      onClick={handleToggleNotifications}
                      className="header-menu-button relative"
                      aria-label={notificationsEnabled ? 'Notifications on' : 'Notifications off'}
                      title={notificationsEnabled ? 'Notifications are ON — tap to turn off' : 'Notifications are OFF — tap to turn on'}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: notificationsEnabled ? '#059669' : '#9ca3af' }}>
                        <path d="M10.268 21a2 2 0 0 0 3.464 0" />
                        <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
                        {!notificationsEnabled && <path d="M3 3l18 18" />}
                      </svg>
                      {notificationsEnabled && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ backgroundColor: '#059669', boxShadow: '0 0 0 2px var(--bg-surface)' }} />
                      )}
                    </button>
                  )}
                  {/* Dark mode toggle */}
                  <button onClick={toggleDarkMode} className="theme-toggle" aria-label="Toggle dark mode" title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} />
                  {/* Menu */}
                  <div className="relative" ref={headerMenuRef}>
                    <button onClick={() => setShowHeaderMenu(s => !s)}
                      className="header-menu-button" aria-label="Open menu">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" /></svg>
                    </button>
                    {showHeaderMenu && (
                      <div className="header-menu absolute right-0 mt-2 z-50" style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--border-primary)' }}>
                        <button onClick={() => { setShowHeaderMenu(false); forceSync(); }} disabled={syncStatus === 'syncing'} className="header-menu-item disabled:opacity-50">Sync now</button>
                        <button onClick={() => { setShowHeaderMenu(false); showEndOfDaySummary(); }} className="header-menu-item">End-of-day summary</button>
                        {isPushSupported() && (
                          <button onClick={() => { setShowHeaderMenu(false); handleToggleNotifications(); }} className="header-menu-item">
                            {notificationsEnabled ? 'Turn off notifications' : 'Turn on notifications'}
                          </button>
                        )}
                        {!isRunningInstalled() && (
                          <button onClick={() => { setShowHeaderMenu(false); handleInstallApp(); }} className="header-menu-item">Install app</button>
                        )}
                        {currentUser.role === 'admin' && (
                          <button onClick={() => { setShowHeaderMenu(false); setShowExportModal(true); }} className="header-menu-item accent">Export data</button>
                        )}
                        {currentUser.role === 'admin' && (
                          <button onClick={() => { setShowHeaderMenu(false); resetSales(); }} className="header-menu-item danger">Reset sales</button>
                        )}
                        <div className="my-1.5 border-t border-gray-100" />
                        <button onClick={() => { setShowHeaderMenu(false); handleLogout(); }} className="header-menu-item">Log out</button>
                      </div>
                    )}
                  </div>
                </div>
                )}
                {isMobile && isPushSupported() && (
                  <button
                    onClick={handleToggleNotifications}
                    className="header-menu-button relative flex-shrink-0"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    aria-label={notificationsEnabled ? 'Notifications on' : 'Notifications off'}
                    title={notificationsEnabled ? 'Notifications are ON — tap to turn off' : 'Notifications are OFF — tap to turn on'}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: notificationsEnabled ? '#059669' : '#9ca3af' }}>
                      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
                      <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
                      {!notificationsEnabled && <path d="M3 3l18 18" />}
                    </svg>
                    {notificationsEnabled && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ backgroundColor: '#059669', boxShadow: '0 0 0 2px var(--bg-surface)' }} />
                    )}
                  </button>
                )}
              </div>
            </div>
          </header>
          {/* Main content */}
          <main className={classNames('px-4 sm:px-6 lg:px-8 py-5 sm:py-8 flex-1', activeTab === 'pos' ? 'max-w-full' : 'max-w-7xl mx-auto w-full')}>
            <Suspense fallback={<div className="flex items-center justify-center py-20 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading…</div>}>
              {activeTab === 'pos' && <POSTab />}
              {activeTab === 'approvals' && currentUser.role === 'admin' && <ApprovalsTab />}
              {activeTab === 'queue' && <QueueTab />}
              {activeTab === 'kitchen' && <KitchenTab />}
              {activeTab === 'history' && <HistoryTab />}
              {activeTab === 'riders' && <RidersTab />}
              {activeTab === 'analytics' && <AnalyticsTab />}
              {activeTab === 'expenses' && <ExpensesTab />}
              {activeTab === 'myexpenses' && <MyExpensesTab />}
              {activeTab === 'inventory' && <InventoryTab />}
              {activeTab === 'complaints' && <ComplaintsTab />}
              {activeTab === 'eod' && <EodSalesTab />}
              {activeTab === 'marketing' && <MarketingTab />}
              {activeTab === 'finance' && <FinanceTab />}
            </Suspense>
          </main>
        </div>
        {/* Mobile bottom nav */}
        {isMobile && (
          <>
            <BottomNav
              currentTab={activeTab}
              onSelectTab={(tabId) => {
                setCurrentTab(tabId);
                setShowMobileMoreMenu(false);
              }}
              onOpenMore={() => setShowMobileMoreMenu(true)}
              queueCount={getOrdersFor('pending_payment').length}
              kitchenCount={getOrdersFor('paid').length}
            />

            {showMobileMoreMenu && (
              <div className="mobile-more-backdrop" onClick={() => setShowMobileMoreMenu(false)}>
                <div className="mobile-more-sheet" onClick={(e) => e.stopPropagation()}>
                  <div className="drawer-handle" />
                  <div className="space-y-3">
                    {mobileMoreTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => {
                          setCurrentTab(tab.id);
                          setShowMobileMoreMenu(false);
                        }}
                        className={classNames('mobile-more-item', currentTab === tab.id && 'active')}
                      >
                        <span className="mobile-more-icon">
                          <ShellIcon name={tab.id} />
                        </span>
                        <span className="truncate">{tab.label}</span>
                      </button>
                    ))}

                    <div className="mobile-more-divider" />

                    <button type="button" onClick={toggleDarkMode} className="mobile-more-item">
                      <span className="mobile-more-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 3v2.5" /><path d="M12 18.5V21" /><path d="m4.93 4.93 1.77 1.77" /><path d="m17.3 17.3 1.77 1.77" /><path d="M3 12h2.5" /><path d="M18.5 12H21" /><path d="m4.93 19.07 1.77-1.77" /><path d="m17.3 6.7 1.77-1.77" /><circle cx="12" cy="12" r="4.5" />
                        </svg>
                      </span>
                      <span>{darkMode ? 'Light mode' : 'Dark mode'}</span>
                    </button>

                    {isPushSupported() && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowMobileMoreMenu(false);
                          handleToggleNotifications();
                        }}
                        className="mobile-more-item"
                      >
                        <span className="mobile-more-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
                          </svg>
                        </span>
                        <span>{notificationsEnabled ? 'Turn off notifications' : 'Turn on notifications'}</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        setShowMobileMoreMenu(false);
                        forceSync();
                      }}
                      disabled={syncStatus === 'syncing'}
                      className="mobile-more-item disabled:opacity-50"
                    >
                      <span className="mobile-more-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" />
                        </svg>
                      </span>
                      <span>Sync now</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowMobileMoreMenu(false);
                        handleLogout();
                      }}
                      className="mobile-more-item danger"
                    >
                      <span className="mobile-more-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" />
                        </svg>
                      </span>
                      <span>Log out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </ClockProvider>
    </AppContext.Provider>
  );
};
export default RestaurantPOS;

